import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import RichContentEditor from '../components/RichContentEditor';
import BoardGalleryEditor from '../components/BoardGalleryEditor';
import AffiliateLinksEditor from '../components/AffiliateLinksEditor';
import TutorialUrlsEditor from '../components/TutorialUrlsEditor';
import CapabilityTagsField from '../components/CapabilityTagsField';
import BoardPublicSvgPreview from '../components/BoardPublicSvgPreview';
import PinBulkElectricalDefaultsPanel from '../components/PinBulkElectricalDefaultsPanel';
import ProjectBuilderAdminPanel from '../components/ProjectBuilderAdminPanel';
import ProjectBuilderLessonsManager from '../components/ProjectBuilderLessonsManager';
import PinoutCalloutEditor from '../components/PinoutCalloutEditor';
import WiringConnectionEditor from '../components/WiringConnectionEditor';
import ICPinouts from '../components/ICPinouts';
import GuidesAdminPanel from '../components/GuidesAdminPanel';
import AdsAdminPanel from '../components/AdsAdminPanel';
import { adminFetch } from '../services/adminApi';
import { appUrl, assetPath } from '../utils/appPaths';
import useUnsavedChangesGuard from '../hooks/useUnsavedChangesGuard';
import {
  deriveBoardSeoDescription,
  deriveBoardSeoKeywords,
  deriveWiringGuideSeoDescription,
  deriveWiringGuideSeoKeywords,
} from '../utils/seo';
import {
  normalizeAffiliateLinks,
  validateAffiliateLinks
} from '../utils/affiliateLinks';
import {
  normalizeTutorialUrls,
  validateTutorialUrls
} from '../utils/tutorialUrls';
import {
  ALT_PIN_CALLOUT_COLOR_OPTIONS,
  applySuggestedAlternateNameDefaults,
  formatSuggestedAlternateNameCoordinate,
  getEditablePinAlternateNameEntries,
  getSuggestedAlternateNamePosition,
  serializePinAlternateNames
} from '../utils/pinAlternateNames';
import {
  initializePinFunctionAutofill,
  updatePinWithFunctionAutofill
} from '../utils/pinFunctionAutofill';
import {
  buildPinoutPreviewSvg,
  getCalloutLayoutKey,
  getPinLayoutKey,
  normalizeRotation,
  rotateCallouts,
  validatePinCallouts
} from '../utils/pinCalloutLayout';
import {
  BOARD_VERIFICATION_STATUS_OPTIONS,
  PIN_ROLE_OPTIONS,
  PROTOCOL_OPTIONS,
  SUGGESTED_PIN_CAPABILITY_TAGS,
  createEmptyBoardCompatibilityFields,
  normalizeBoardVerificationStatus,
  normalizeBooleanFlag,
  normalizeSupportedProtocols,
  normalizeStringArray,
  toggleSupportedProtocolValue,
  withPinCompatibilityDefaults
} from '../utils/compatibilityOptions';
import {
  applyBulkElectricalValuesToPins,
  countEligibleBulkElectricalPins,
  createEmptyBulkElectricalValues,
  hasCompleteBulkElectricalValues,
} from '../utils/pinElectricalDefaults';
import { createBoardPublicPinoutImageUpload } from '../utils/boardPublicPinoutPng';
import {
  Menu,
  Upload, 
  Edit, 
  Trash2, 
  X, 
  Eye, 
  EyeOff,
  Settings,
  Cpu,
  Link,
  FileText,
  Globe,
  CircuitBoard,
  Image,
  BookOpen
} from 'lucide-react';

const BRAND_LOGO_WIDTH = 480;
const BRAND_LOGO_HEIGHT = 200;
const EMPTY_BULK_ELECTRICAL_VALUES = createEmptyBulkElectricalValues();
const HOME_POPULAR_BOARD_LIMIT = 4;

const extractParsedFritzingData = (uploadPayload) => {
  const result = uploadPayload?.data && (Array.isArray(uploadPayload.data.pins) || uploadPayload.data.displaySVG)
    ? uploadPayload.data
    : (uploadPayload || {});

  return {
    pins: Array.isArray(result.pins) ? result.pins : [],
    totalPins: result.totalPins ?? (Array.isArray(result.pins) ? result.pins.length : 0),
    displaySVG: result.displaySVG || '',
    viewType: result.viewType || 'fritzing',
    boardMetadata: result.boardMetadata || {},
    filename: uploadPayload?.filename || '',
    fileType: uploadPayload?.fileType || '',
    requestId: uploadPayload?.requestId || ''
  };
};

const ADMIN_SECTIONS = [
  {
    id: 'boards',
    label: 'Pinouts',
    icon: Cpu,
  },
  {
    id: 'project-builder',
    label: 'Builder',
    icon: CircuitBoard,
  },
  {
    id: 'lessons',
    label: 'Lessons',
    icon: BookOpen,
  },
  {
    id: 'ads',
    label: 'Ads',
    icon: Image,
  },
  {
    id: 'ic-pinouts',
    label: 'IC Pinouts',
    icon: CircuitBoard,
  },
  {
    id: 'wiring',
    label: 'Wiring',
    icon: Link,
  },
  {
    id: 'guides',
    label: 'Guides',
    icon: FileText,
  },
];

const prepareUploadedPins = (pins) => (
  Array.isArray(pins)
    ? pins.map((pin) => ({
        ...initializePinFunctionAutofill(
          {
            ...withPinCompatibilityDefaults(pin),
            functions: pin?.functions || '',
            alternate_pin_names: serializePinAlternateNames(pin?.alternate_pin_names ?? pin?.alternatePinNames ?? ''),
            alternate_functions: pin?.alternate_functions || '',
            description: pin?.description || ''
          },
          { preferSuggested: true }
        )
      }))
    : []
);

const createEmptyBoardForm = () => ({
  name: '',
  description: '',
  meta_description: '',
  seo_keywords: '',
  manufacturer: '',
  package_type: '',
  cpu: '',
  clock_speed: '',
  flash_memory: '',
  ram: '',
  rom: '',
  show_mcu_specs: true,
  image_url: '',
  link: '',
  datasheet_url: '',
  tutorial_urls: [],
  category: '',
  verification_status: 'unverified',
  svg_display_scale: 1,
  project_builder_scale: 1,
  project_builder_enabled: 1,
  public_svg_rotation_deg: 0,
  gallery_images: [],
  affiliate_links: [],
  ...createEmptyBoardCompatibilityFields()
});

const ADMIN_PIN_METADATA_JSON_EXAMPLE = `[
  {
    "pin_number": "1",
    "pin_name": "GPIO21",
    "pin_role": "i2c-sda",
    "primary_function": "GPIO21",
    "capability_tags": ["gpio", "i2c-sda"],
    "alternate_function": "I2C SDA, ADC",
    "description": "<p>Shared data line for the default I2C bus.</p>",
    "voltage_min_v": 0,
    "voltage_max_v": 3.3,
    "current_limit_ma": 20
  },
  {
    "pin_name": "LED_BUILTIN",
    "use_with_caution": true,
    "caution_reason": "Shared with an onboard status LED."
  }
]`;

const ADMIN_BOARD_METADATA_JSON_EXAMPLE = `{
  "manufacturer": "Arduino",
  "package_type": "DIP",
  "cpu": "ATmega328P",
  "supply_voltage_min_v": 1.8,
  "supply_voltage_max_v": 5.5,
  "logic_voltage_min_v": 3.3,
  "logic_voltage_max_v": 5,
  "current_draw_typ_ma": 45,
  "current_draw_max_ma": 200,
  "rail_3v3_max_ma": 50,
  "rail_5v_max_ma": 500,
  "clock_speed": "16 MHz",
  "flash": "32 KB",
  "ram": "2 KB",
  "rom": "",
  "supported_protocols": ["i2c", "spi", "uart"],
  "documentation_url": "https://docs.arduino.cc/",
  "datasheet_url": "https://example.com/datasheet.pdf",
  "verification_status": "unverified",
  "description": "<p>Classic ATmega328P development board.</p>"
}`;

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value, key);

const readImportField = (entry, keys) => {
  for (const key of keys) {
    if (hasOwn(entry, key)) {
      return entry[key];
    }
  }

  return undefined;
};

const hasImportField = (entry, keys) => keys.some((key) => hasOwn(entry, key));

const PIN_VOLTAGE_MIN_IMPORT_KEYS = ['voltage_min_v', 'voltageMinV', 'voltage_min', 'voltageMin', 'minimum_voltage', 'minimumVoltage', 'min_voltage', 'minVoltage'];
const PIN_VOLTAGE_MAX_IMPORT_KEYS = ['voltage_max_v', 'voltageMaxV', 'voltage_max', 'voltageMax', 'maximum_voltage', 'maximumVoltage', 'max_voltage', 'maxVoltage'];
const PIN_CURRENT_LIMIT_IMPORT_KEYS = ['current_limit_ma', 'currentLimitMa', 'current_limit', 'currentLimit', 'current_ma', 'currentMa'];

const normalizeImportNumber = (value, fieldLabel) => {
  if (value === undefined) {
    return undefined;
  }

  if (value === null || value === '') {
    return '';
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    throw new Error(`${fieldLabel} must be numeric.`);
  }

  return numericValue;
};

const normalizeImportString = (value) => {
  if (value === undefined) {
    return undefined;
  }

  if (value === null) {
    return '';
  }

  return String(value);
};

const normalizePinMetadataImportEntries = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (value && typeof value === 'object' && Array.isArray(value.pins)) {
    return value.pins;
  }

  return null;
};

const normalizeBoardMetadataImportEntry = (value) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    if (value.board && typeof value.board === 'object' && !Array.isArray(value.board)) {
      return value.board;
    }

    if (value.metadata && typeof value.metadata === 'object' && !Array.isArray(value.metadata)) {
      return value.metadata;
    }

    if (value.board_metadata && typeof value.board_metadata === 'object' && !Array.isArray(value.board_metadata)) {
      return value.board_metadata;
    }

    return value;
  }

  return null;
};

const normalizeComparableNumber = (value) => {
  if (value === '' || value === null || value === undefined) {
    return '';
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return String(value);
  }

  return Number(numericValue.toFixed(6));
};

const buildAdminBoardMetadataExportPayload = (boardRecord) => ({
  manufacturer: String(boardRecord?.manufacturer ?? ''),
  package_type: String(boardRecord?.package_type ?? ''),
  cpu: String(boardRecord?.cpu ?? ''),
  supply_voltage_min_v: normalizeComparableNumber(boardRecord?.supply_voltage_min_v),
  supply_voltage_max_v: normalizeComparableNumber(boardRecord?.supply_voltage_max_v),
  logic_voltage_min_v: normalizeComparableNumber(boardRecord?.logic_voltage_min_v),
  logic_voltage_max_v: normalizeComparableNumber(boardRecord?.logic_voltage_max_v),
  current_draw_typ_ma: normalizeComparableNumber(boardRecord?.current_draw_typ_ma),
  current_draw_max_ma: normalizeComparableNumber(boardRecord?.current_draw_max_ma),
  rail_3v3_max_ma: normalizeComparableNumber(boardRecord?.rail_3v3_max_ma),
  rail_5v_max_ma: normalizeComparableNumber(boardRecord?.rail_5v_max_ma),
  clock_speed: String(boardRecord?.clock_speed ?? ''),
  flash: String(boardRecord?.flash_memory ?? ''),
  ram: String(boardRecord?.ram ?? ''),
  rom: String(boardRecord?.rom ?? ''),
  supported_protocols: normalizeSupportedProtocols(boardRecord?.supported_protocols),
  documentation_url: String(boardRecord?.link ?? ''),
  datasheet_url: String(boardRecord?.datasheet_url ?? ''),
  verification_status: normalizeBoardVerificationStatus(boardRecord?.verification_status),
  description: String(boardRecord?.description ?? ''),
});

const buildAdminExportableAlternateNameEntries = (pin) => (
  getEditablePinAlternateNameEntries(pin)
    .filter((entry) => String(entry?.name ?? '').trim())
    .map((entry) => {
      const exportedEntry = {
        name: String(entry?.name ?? '').trim(),
      };
      const normalizedX = normalizeComparableNumber(entry?.x);
      const normalizedY = normalizeComparableNumber(entry?.y);
      const normalizedColor = String(entry?.color ?? '').trim();

      if (normalizedX !== '') {
        exportedEntry.x = normalizedX;
      }

      if (normalizedY !== '') {
        exportedEntry.y = normalizedY;
      }

      if (normalizedColor) {
        exportedEntry.color = normalizedColor;
      }

      return Object.keys(exportedEntry).length === 1 ? exportedEntry.name : exportedEntry;
    })
);

const normalizeBoardPublishedValue = (value) => {
  if (value === true || value === 1 || value === '1') {
    return 1;
  }

  return 0;
};

const normalizeBoardPopularOnHomeValue = (value) => {
  if (value === true || value === 1 || value === '1') {
    return 1;
  }

  return 0;
};

const normalizeProjectBuilderEnabledValue = (value) => {
  if (value === undefined || value === null || value === '') {
    return 1;
  }

  if (value === false || value === 0 || value === '0') {
    return 0;
  }

  if (typeof value === 'string') {
    return ['false', 'no', 'off'].includes(value.trim().toLowerCase()) ? 0 : 1;
  }

  return 1;
};

const normalizeAdminBoardRecord = (board) => {
  if (!board || typeof board !== 'object') {
    return board;
  }

  const normalizedPinCount = Number(board.pin_count);
  const normalizedProjectBuilderScale = Number(board.project_builder_scale);
  const normalizedPublicSvgRotation = Number(board.public_svg_rotation_deg);

  return {
    ...board,
    published: normalizeBoardPublishedValue(board.published),
    popular_on_home: normalizeBoardPopularOnHomeValue(board.popular_on_home),
    project_builder_enabled: normalizeProjectBuilderEnabledValue(board.project_builder_enabled),
    pin_count: Number.isFinite(normalizedPinCount) ? normalizedPinCount : board.pin_count,
    tutorial_urls: normalizeTutorialUrls(board.tutorial_urls),
    project_builder_scale: Number.isFinite(normalizedProjectBuilderScale)
      ? normalizedProjectBuilderScale
      : board.project_builder_scale,
    public_svg_rotation_deg: Number.isFinite(normalizedPublicSvgRotation)
      ? normalizedPublicSvgRotation
      : board.public_svg_rotation_deg,
  };
};

const normalizeAdminBoardCollection = (boards) => (
  Array.isArray(boards) ? boards.map(normalizeAdminBoardRecord) : []
);

const SVG_SIZE_DECIMALS = 2;
const SCALED_COORDINATE_DECIMALS = 4;
const SVG_SIZE_INPUT_MODE_UNITS = 'units';
const SVG_SIZE_INPUT_MODE_PERCENT = 'percent';
const UPLOAD_SVG_BASE_VIEWBOX_MIN_X_ATTR = 'data-micropinouts-base-vbx';
const UPLOAD_SVG_BASE_VIEWBOX_MIN_Y_ATTR = 'data-micropinouts-base-vby';
const UPLOAD_SVG_BASE_VIEWBOX_WIDTH_ATTR = 'data-micropinouts-base-vbw';
const UPLOAD_SVG_BASE_VIEWBOX_HEIGHT_ATTR = 'data-micropinouts-base-vbh';
const UPLOAD_SVG_SCALE_WRAPPER_ATTR = 'data-micropinouts-intrinsic-scale-wrapper';
const UPLOAD_SVG_SCALE_VALUE_ATTR = 'data-micropinouts-intrinsic-scale';
const EMPTY_UPLOAD_SVG_SIZE_INFO = {
  minX: 0,
  minY: 0,
  width: null,
  height: null,
  widthText: '',
  heightText: '',
  unitLabel: 'SVG units',
  sourceLabel: 'the uploaded SVG',
};

const EMPTY_UPLOAD_SVG_BASE_BOX = {
  minX: 0,
  minY: 0,
  width: null,
  height: null,
};

const formatSvgSizeNumber = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return '';
  }

  return String(Number(numeric.toFixed(SVG_SIZE_DECIMALS)));
};

const formatSvgCoordinateNumber = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '';
  }

  return String(Number(numeric.toFixed(SVG_SIZE_DECIMALS)));
};

const roundScaledCoordinate = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return value;
  }

  return Number(numeric.toFixed(SCALED_COORDINATE_DECIMALS));
};

const scaleCoordinateAroundCenter = (value, center, ratio) => {
  const numericValue = Number(value);
  const numericCenter = Number(center);
  const numericRatio = Number(ratio);

  if (!Number.isFinite(numericValue) || !Number.isFinite(numericCenter) || !Number.isFinite(numericRatio)) {
    return value;
  }

  return roundScaledCoordinate(numericCenter + ((numericValue - numericCenter) * numericRatio));
};

const parseSvgViewBox = (value) => {
  const parts = String(value || '')
    .trim()
    .split(/[\s,]+/)
    .map((segment) => Number.parseFloat(segment))
    .filter((segment) => Number.isFinite(segment));

  if (parts.length >= 4) {
    return {
      minX: parts[0],
      minY: parts[1],
      width: parts[2],
      height: parts[3]
    };
  }

  return null;
};

const extractSvgAttribute = (markup, attributeName) => {
  const escapedAttributeName = String(attributeName || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = new RegExp(`\\b${escapedAttributeName}=["']([^"']+)["']`, 'i');
  return String(markup || '').match(pattern)?.[1] || '';
};

const parseSvgLengthValue = (value) => {
  const match = String(value || '').trim().match(/^([+-]?\d*\.?\d+)([a-z%]*)$/i);
  if (!match) {
    return null;
  }

  const amount = Number.parseFloat(match[1]);
  if (!Number.isFinite(amount) || amount <= 0) {
    return null;
  }

  return {
    value: amount,
    unit: String(match[2] || '').toLowerCase()
  };
};

const parseUploadSvgIntrinsicSize = (markup) => {
  const fallback = EMPTY_UPLOAD_SVG_SIZE_INFO;

  const rawMarkup = String(markup || '').trim();
  if (!rawMarkup) {
    return fallback;
  }

  let rawOriginalWidth = '';
  let rawOriginalHeight = '';
  let rawViewBox = '';
  let rawWidthAttribute = '';
  let rawHeightAttribute = '';

  if (typeof DOMParser !== 'undefined') {
    try {
      const parser = new DOMParser();
      const parsedDocument = parser.parseFromString(rawMarkup, 'image/svg+xml');
      const parseError = parsedDocument.querySelector('parsererror');
      const svgElement = parsedDocument.documentElement;

      if (!parseError && svgElement && svgElement.nodeName.toLowerCase() === 'svg') {
        rawOriginalWidth = svgElement.getAttribute('data-orig-vbw') || '';
        rawOriginalHeight = svgElement.getAttribute('data-orig-vbh') || '';
        rawViewBox = svgElement.getAttribute('viewBox') || '';
        rawWidthAttribute = svgElement.getAttribute('width') || '';
        rawHeightAttribute = svgElement.getAttribute('height') || '';
      }
    } catch (error) {
      // Fall through to the string-based fallback below.
    }
  }

  if (!rawOriginalWidth) {
    rawOriginalWidth = extractSvgAttribute(rawMarkup, 'data-orig-vbw');
  }

  if (!rawOriginalHeight) {
    rawOriginalHeight = extractSvgAttribute(rawMarkup, 'data-orig-vbh');
  }

  if (!rawViewBox) {
    rawViewBox = extractSvgAttribute(rawMarkup, 'viewBox');
  }

  if (!rawWidthAttribute) {
    rawWidthAttribute = extractSvgAttribute(rawMarkup, 'width');
  }

  if (!rawHeightAttribute) {
    rawHeightAttribute = extractSvgAttribute(rawMarkup, 'height');
  }

  const originalWidth = Number.parseFloat(rawOriginalWidth);
  const originalHeight = Number.parseFloat(rawOriginalHeight);
  const originalMinX = Number.parseFloat(extractSvgAttribute(rawMarkup, 'data-orig-vbx'));
  const originalMinY = Number.parseFloat(extractSvgAttribute(rawMarkup, 'data-orig-vby'));

  if (Number.isFinite(originalWidth) && originalWidth > 0 && Number.isFinite(originalHeight) && originalHeight > 0) {
    return {
      minX: Number.isFinite(originalMinX) ? originalMinX : 0,
      minY: Number.isFinite(originalMinY) ? originalMinY : 0,
      width: originalWidth,
      height: originalHeight,
      widthText: formatSvgSizeNumber(originalWidth),
      heightText: formatSvgSizeNumber(originalHeight),
      unitLabel: 'SVG units',
      sourceLabel: 'the SVG footprint captured from the upload'
    };
  }

  const widthAttribute = parseSvgLengthValue(rawWidthAttribute);
  const heightAttribute = parseSvgLengthValue(rawHeightAttribute);
  if (
    widthAttribute &&
    heightAttribute &&
    widthAttribute.unit !== '%' &&
    heightAttribute.unit !== '%'
  ) {
    const sharedUnit = widthAttribute.unit === heightAttribute.unit
      ? (widthAttribute.unit || 'unitless values')
      : 'mixed SVG units';

    return {
      minX: 0,
      minY: 0,
      width: widthAttribute.value,
      height: heightAttribute.value,
      widthText: formatSvgSizeNumber(widthAttribute.value),
      heightText: formatSvgSizeNumber(heightAttribute.value),
      unitLabel: sharedUnit,
      sourceLabel: 'the SVG width and height attributes'
    };
  }

  const viewBox = parseSvgViewBox(rawViewBox);
  if (viewBox && viewBox.width > 0 && viewBox.height > 0) {
    return {
      minX: viewBox.minX,
      minY: viewBox.minY,
      width: viewBox.width,
      height: viewBox.height,
      widthText: formatSvgSizeNumber(viewBox.width),
      heightText: formatSvgSizeNumber(viewBox.height),
      unitLabel: 'SVG units',
      sourceLabel: 'the SVG viewBox'
    };
  }

  return fallback;
};

const parseUploadSvgGeometryBox = (markup) => {
  const fallback = EMPTY_UPLOAD_SVG_BASE_BOX;
  const rawMarkup = String(markup || '').trim();
  if (!rawMarkup) {
    return fallback;
  }

  const rawBaseMinX = extractSvgAttribute(rawMarkup, UPLOAD_SVG_BASE_VIEWBOX_MIN_X_ATTR);
  const rawBaseMinY = extractSvgAttribute(rawMarkup, UPLOAD_SVG_BASE_VIEWBOX_MIN_Y_ATTR);
  const rawBaseWidth = extractSvgAttribute(rawMarkup, UPLOAD_SVG_BASE_VIEWBOX_WIDTH_ATTR);
  const rawBaseHeight = extractSvgAttribute(rawMarkup, UPLOAD_SVG_BASE_VIEWBOX_HEIGHT_ATTR);

  const baseMinX = Number.parseFloat(rawBaseMinX);
  const baseMinY = Number.parseFloat(rawBaseMinY);
  const baseWidth = Number.parseFloat(rawBaseWidth);
  const baseHeight = Number.parseFloat(rawBaseHeight);

  if (Number.isFinite(baseWidth) && baseWidth > 0 && Number.isFinite(baseHeight) && baseHeight > 0) {
    return {
      minX: Number.isFinite(baseMinX) ? baseMinX : 0,
      minY: Number.isFinite(baseMinY) ? baseMinY : 0,
      width: baseWidth,
      height: baseHeight,
    };
  }

  const rawViewBox = extractSvgAttribute(rawMarkup, 'viewBox');
  const viewBox = parseSvgViewBox(rawViewBox);
  if (viewBox && viewBox.width > 0 && viewBox.height > 0) {
    return {
      minX: viewBox.minX,
      minY: viewBox.minY,
      width: viewBox.width,
      height: viewBox.height,
    };
  }

  const rawOriginalWidth = extractSvgAttribute(rawMarkup, 'data-orig-vbw');
  const rawOriginalHeight = extractSvgAttribute(rawMarkup, 'data-orig-vbh');
  const rawOriginalMinX = extractSvgAttribute(rawMarkup, 'data-orig-vbx');
  const rawOriginalMinY = extractSvgAttribute(rawMarkup, 'data-orig-vby');

  const originalWidth = Number.parseFloat(rawOriginalWidth);
  const originalHeight = Number.parseFloat(rawOriginalHeight);
  const originalMinX = Number.parseFloat(rawOriginalMinX);
  const originalMinY = Number.parseFloat(rawOriginalMinY);

  if (Number.isFinite(originalWidth) && originalWidth > 0 && Number.isFinite(originalHeight) && originalHeight > 0) {
    return {
      minX: Number.isFinite(originalMinX) ? originalMinX : 0,
      minY: Number.isFinite(originalMinY) ? originalMinY : 0,
      width: originalWidth,
      height: originalHeight,
    };
  }

  const rawWidthAttribute = extractSvgAttribute(rawMarkup, 'width');
  const rawHeightAttribute = extractSvgAttribute(rawMarkup, 'height');
  const widthAttribute = parseSvgLengthValue(rawWidthAttribute);
  const heightAttribute = parseSvgLengthValue(rawHeightAttribute);

  if (
    widthAttribute &&
    heightAttribute &&
    widthAttribute.unit !== '%' &&
    heightAttribute.unit !== '%'
  ) {
    return {
      minX: 0,
      minY: 0,
      width: widthAttribute.value,
      height: heightAttribute.value,
    };
  }

  return fallback;
};

const resolveUploadSvgBaseBox = (svgElement) => {
  if (!svgElement) {
    return null;
  }

  const parseAttr = (name) => Number.parseFloat(svgElement.getAttribute(name) || '');

  const baseWidth = parseAttr(UPLOAD_SVG_BASE_VIEWBOX_WIDTH_ATTR);
  const baseHeight = parseAttr(UPLOAD_SVG_BASE_VIEWBOX_HEIGHT_ATTR);
  if (Number.isFinite(baseWidth) && baseWidth > 0 && Number.isFinite(baseHeight) && baseHeight > 0) {
    return {
      minX: Number.isFinite(parseAttr(UPLOAD_SVG_BASE_VIEWBOX_MIN_X_ATTR)) ? parseAttr(UPLOAD_SVG_BASE_VIEWBOX_MIN_X_ATTR) : 0,
      minY: Number.isFinite(parseAttr(UPLOAD_SVG_BASE_VIEWBOX_MIN_Y_ATTR)) ? parseAttr(UPLOAD_SVG_BASE_VIEWBOX_MIN_Y_ATTR) : 0,
      width: baseWidth,
      height: baseHeight,
    };
  }

  const originalWidth = parseAttr('data-orig-vbw');
  const originalHeight = parseAttr('data-orig-vbh');
  if (Number.isFinite(originalWidth) && originalWidth > 0 && Number.isFinite(originalHeight) && originalHeight > 0) {
    return {
      minX: Number.isFinite(parseAttr('data-orig-vbx')) ? parseAttr('data-orig-vbx') : 0,
      minY: Number.isFinite(parseAttr('data-orig-vby')) ? parseAttr('data-orig-vby') : 0,
      width: originalWidth,
      height: originalHeight,
    };
  }

  const viewBox = parseSvgViewBox(svgElement.getAttribute('viewBox') || '');
  if (viewBox && viewBox.width > 0 && viewBox.height > 0) {
    return {
      minX: viewBox.minX,
      minY: viewBox.minY,
      width: viewBox.width,
      height: viewBox.height,
    };
  }

  const widthAttribute = parseSvgLengthValue(svgElement.getAttribute('width') || '');
  const heightAttribute = parseSvgLengthValue(svgElement.getAttribute('height') || '');
  if (widthAttribute && heightAttribute) {
    return {
      minX: 0,
      minY: 0,
      width: widthAttribute.value,
      height: heightAttribute.value,
    };
  }

  return null;
};

const scaleUploadedPinsGeometry = (pins, ratio, center) => {
  if (!Array.isArray(pins) || pins.length === 0) {
    return pins;
  }

  if (!Number.isFinite(Number(ratio)) || Math.abs(Number(ratio) - 1) < 0.000001) {
    return pins;
  }

  return pins.map((pin) => {
    const currentX = pin?.position_x ?? pin?.position?.x;
    const currentY = pin?.position_y ?? pin?.position?.y;
    const nextX = scaleCoordinateAroundCenter(currentX, center.x, ratio);
    const nextY = scaleCoordinateAroundCenter(currentY, center.y, ratio);

    if (!Number.isFinite(Number(nextX)) || !Number.isFinite(Number(nextY))) {
      return pin;
    }

    return {
      ...pin,
      position_x: nextX,
      position_y: nextY,
      position: pin?.position
        ? {
            ...pin.position,
            x: nextX,
            y: nextY,
          }
        : pin?.position,
    };
  });
};

const updateUploadSvgIntrinsicSize = (markup, widthValue, heightValue) => {
  const rawMarkup = String(markup || '').trim();
  const nextWidth = Number(widthValue);
  const nextHeight = Number(heightValue);

  if (!rawMarkup || !Number.isFinite(nextWidth) || nextWidth <= 0 || !Number.isFinite(nextHeight) || nextHeight <= 0) {
    return rawMarkup;
  }

  const serializedWidth = formatSvgSizeNumber(nextWidth);
  const serializedHeight = formatSvgSizeNumber(nextHeight);

  if (!serializedWidth || !serializedHeight) {
    return rawMarkup;
  }

  if (typeof DOMParser !== 'undefined' && typeof XMLSerializer !== 'undefined') {
    try {
      const parser = new DOMParser();
      const parsedDocument = parser.parseFromString(rawMarkup, 'image/svg+xml');
      const parseError = parsedDocument.querySelector('parsererror');
      const svgElement = parsedDocument.documentElement;

      if (!parseError && svgElement && svgElement.nodeName.toLowerCase() === 'svg') {
        const baseBox = resolveUploadSvgBaseBox(svgElement);
        const viewBox = parseSvgViewBox(svgElement.getAttribute('viewBox') || '');
        const currentWidthAttribute = parseSvgLengthValue(svgElement.getAttribute('width') || '');
        const currentHeightAttribute = parseSvgLengthValue(svgElement.getAttribute('height') || '');
        const scaleFactor = baseBox && Number.isFinite(baseBox.width) && baseBox.width > 0
          ? nextWidth / baseBox.width
          : null;

        if (baseBox && Number.isFinite(baseBox.width) && baseBox.width > 0 && Number.isFinite(baseBox.height) && baseBox.height > 0) {
          svgElement.setAttribute(UPLOAD_SVG_BASE_VIEWBOX_MIN_X_ATTR, formatSvgCoordinateNumber(baseBox.minX) || '0');
          svgElement.setAttribute(UPLOAD_SVG_BASE_VIEWBOX_MIN_Y_ATTR, formatSvgCoordinateNumber(baseBox.minY) || '0');
          svgElement.setAttribute(UPLOAD_SVG_BASE_VIEWBOX_WIDTH_ATTR, formatSvgSizeNumber(baseBox.width) || '');
          svgElement.setAttribute(UPLOAD_SVG_BASE_VIEWBOX_HEIGHT_ATTR, formatSvgSizeNumber(baseBox.height) || '');

          const baseCenterX = baseBox.minX + (baseBox.width / 2);
          const baseCenterY = baseBox.minY + (baseBox.height / 2);
          const nextMinX = baseCenterX - (nextWidth / 2);
          const nextMinY = baseCenterY - (nextHeight / 2);
          let scaleWrapper = svgElement.querySelector(`[${UPLOAD_SVG_SCALE_WRAPPER_ATTR}="1"]`);

          if (!scaleWrapper) {
            scaleWrapper = parsedDocument.createElementNS('http://www.w3.org/2000/svg', 'g');
            scaleWrapper.setAttribute(UPLOAD_SVG_SCALE_WRAPPER_ATTR, '1');

            const preservedTagNames = new Set(['defs', 'style', 'title', 'desc', 'metadata']);
            Array.from(svgElement.childNodes).forEach((childNode) => {
              if (
                childNode.nodeType === Node.ELEMENT_NODE
                && preservedTagNames.has(childNode.nodeName.toLowerCase())
              ) {
                return;
              }

              scaleWrapper.appendChild(childNode);
            });

            svgElement.appendChild(scaleWrapper);
          }

          if (Number.isFinite(scaleFactor) && scaleFactor > 0) {
            scaleWrapper.setAttribute(UPLOAD_SVG_SCALE_VALUE_ATTR, formatSvgSizeNumber(scaleFactor) || '1');
            scaleWrapper.setAttribute(
              'transform',
              `translate(${roundScaledCoordinate(baseCenterX)} ${roundScaledCoordinate(baseCenterY)}) scale(${roundScaledCoordinate(scaleFactor)}) translate(${-roundScaledCoordinate(baseCenterX)} ${-roundScaledCoordinate(baseCenterY)})`
            );
          }

          svgElement.setAttribute('data-orig-vbx', formatSvgCoordinateNumber(nextMinX) || '0');
          svgElement.setAttribute('data-orig-vby', formatSvgCoordinateNumber(nextMinY) || '0');
        }

        if (!baseBox && viewBox) {
          svgElement.setAttribute('data-orig-vbx', formatSvgCoordinateNumber(viewBox.minX) || '0');
          svgElement.setAttribute('data-orig-vby', formatSvgCoordinateNumber(viewBox.minY) || '0');
        }

        svgElement.setAttribute('data-orig-vbw', serializedWidth);
        svgElement.setAttribute('data-orig-vbh', serializedHeight);

        if (currentWidthAttribute && currentWidthAttribute.unit !== '%') {
          svgElement.setAttribute('width', `${serializedWidth}${currentWidthAttribute.unit}`);
        }

        if (currentHeightAttribute && currentHeightAttribute.unit !== '%') {
          svgElement.setAttribute('height', `${serializedHeight}${currentHeightAttribute.unit}`);
        }

        return new XMLSerializer().serializeToString(svgElement);
      }
    } catch (error) {
      // Fall through to the string-based fallback below.
    }
  }

  const setOrInsertAttribute = (currentMarkup, attributeName, attributeValue) => {
    const escapedAttributeName = String(attributeName || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const attributePattern = new RegExp(`\\b${escapedAttributeName}=["'][^"']*["']`, 'i');

    if (attributePattern.test(currentMarkup)) {
      return currentMarkup.replace(attributePattern, `${attributeName}="${attributeValue}"`);
    }

    return currentMarkup.replace(/<svg\b/i, `<svg ${attributeName}="${attributeValue}"`);
  };

  let nextMarkup = setOrInsertAttribute(rawMarkup, 'data-orig-vbw', serializedWidth);
  nextMarkup = setOrInsertAttribute(nextMarkup, 'data-orig-vbh', serializedHeight);
  return nextMarkup;
};

const normalizeUploadSvgCanvasSize = (markup) => {
  const rawMarkup = String(markup || '').trim();
  if (!rawMarkup) {
    return rawMarkup;
  }

  const currentBox = parseUploadSvgGeometryBox(rawMarkup);
  const nextWidth = Number(currentBox.width);
  const nextHeight = Number(currentBox.height);

  if (!Number.isFinite(nextWidth) || nextWidth <= 0 || !Number.isFinite(nextHeight) || nextHeight <= 0) {
    return rawMarkup;
  }

  const nextMinX = Number.isFinite(Number(currentBox.minX)) ? Number(currentBox.minX) : 0;
  const nextMinY = Number.isFinite(Number(currentBox.minY)) ? Number(currentBox.minY) : 0;
  const serializedWidth = formatSvgSizeNumber(nextWidth);
  const serializedHeight = formatSvgSizeNumber(nextHeight);
  const serializedMinX = formatSvgCoordinateNumber(nextMinX) || '0';
  const serializedMinY = formatSvgCoordinateNumber(nextMinY) || '0';

  if (!serializedWidth || !serializedHeight) {
    return rawMarkup;
  }

  if (typeof DOMParser !== 'undefined' && typeof XMLSerializer !== 'undefined') {
    try {
      const parser = new DOMParser();
      const parsedDocument = parser.parseFromString(rawMarkup, 'image/svg+xml');
      const parseError = parsedDocument.querySelector('parsererror');
      const svgElement = parsedDocument.documentElement;

      if (!parseError && svgElement && svgElement.nodeName.toLowerCase() === 'svg') {
        const currentWidthAttribute = parseSvgLengthValue(svgElement.getAttribute('width') || '');
        const currentHeightAttribute = parseSvgLengthValue(svgElement.getAttribute('height') || '');

        svgElement.setAttribute('viewBox', `${serializedMinX} ${serializedMinY} ${serializedWidth} ${serializedHeight}`);
        svgElement.setAttribute('data-orig-vbx', serializedMinX);
        svgElement.setAttribute('data-orig-vby', serializedMinY);
        svgElement.setAttribute('data-orig-vbw', serializedWidth);
        svgElement.setAttribute('data-orig-vbh', serializedHeight);
        svgElement.setAttribute('data-padx', '0');
        svgElement.setAttribute('data-pady', '0');

        if (currentWidthAttribute && currentWidthAttribute.unit && currentWidthAttribute.unit !== '%') {
          svgElement.setAttribute('width', `${serializedWidth}${currentWidthAttribute.unit}`);
        } else {
          svgElement.setAttribute('width', serializedWidth);
        }

        if (currentHeightAttribute && currentHeightAttribute.unit && currentHeightAttribute.unit !== '%') {
          svgElement.setAttribute('height', `${serializedHeight}${currentHeightAttribute.unit}`);
        } else {
          svgElement.setAttribute('height', serializedHeight);
        }

        return new XMLSerializer().serializeToString(svgElement);
      }
    } catch (error) {
      // Fall through to the original markup.
    }
  }

  return rawMarkup;
};

const normalizeWiringGuideConnections = (rawConnections) => {
  if (Array.isArray(rawConnections)) {
    return rawConnections;
  }

  if (typeof rawConnections !== 'string') {
    return [];
  }

  try {
    const parsedConnections = JSON.parse(rawConnections);
    return Array.isArray(parsedConnections) ? parsedConnections : [];
  } catch (error) {
    return [];
  }
};

const looksLikeUploadedWiringDiagram = (svgContent) => {
  const normalizedSvgContent = String(svgContent || '').trim().toLowerCase();

  return normalizedSvgContent.includes('<image') || normalizedSvgContent.includes('data:image/');
};

const getWiringGuideSensorName = (guide) => (
  guide?.sensor_name
  || guide?.sensor_board_name
  || guide?.sensorBoardName
  || ''
);

const getWiringGuideMicrocontrollerName = (guide) => (
  guide?.microcontroller_name
  || guide?.microcontroller_board_name
  || guide?.microcontrollerBoardName
  || ''
);

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 32px;
  padding: 20px 0;
`;

const PaginationButton = styled.button`
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  background: ${props => props.$active ? '#2c9fa4' : 'white'};
  color: ${props => props.$active ? 'white' : '#64748b'};
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 40px;
  
  &:hover {
    background: ${props => props.$active ? '#2c9fa4' : '#f1f5f9'};
    border-color: #2c9fa4;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PaginationInfo = styled.div`
  font-size: 14px;
  color: #64748b;
  margin: 0 16px;
`;

const AdminContainer = styled.div`
  padding: 0;
  min-height: 100vh;
  background: #f8fafc;

  @media (max-width: 768px) {
    padding: 0 0;
  }
`;

const AdminHeader = styled.div`
  background: white;
  border-bottom: 1px solid #e2e8f0;
  padding: 20px 0;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    padding: 16px 0;
    margin-bottom: 24px;
  }
`;

const HeaderContent = styled.div`
  width: min(var(--site-max-width), calc(100% - (var(--site-gutter) * 2)));
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const AdminTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    font-size: 26px;
  }
`;


const AdminContent = styled.div`
  width: min(var(--site-max-width), calc(100% - (var(--site-gutter) * 2)));
  margin: 0 auto;
`;

const AdminShell = styled.div`
  display: grid;
  grid-template-columns: minmax(240px, 280px) minmax(0, 1fr);
  gap: 24px;
  align-items: start;

  @media (max-width: 960px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const SidebarToggleBar = styled.div`
  display: none;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  background: white;
  padding: 14px 16px;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  @media (max-width: 960px) {
    display: flex;
  }
`;

const SidebarToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  border: none;
  background: #0f172a;
  color: white;
  border-radius: 10px;
  padding: 10px 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #1e293b;
  }
`;

const SidebarToggleMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  min-width: 0;
`;

const SidebarToggleLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
`;

const SidebarToggleValue = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  text-align: right;
`;

const SidebarOverlay = styled.button`
  display: none;
  border: none;
  padding: 0;
  margin: 0;
  background: rgba(15, 23, 42, 0.45);

  @media (max-width: 960px) {
    display: ${props => (props.$isOpen ? 'block' : 'none')};
    position: fixed;
    inset: 0;
    z-index: 20;
  }
`;

const Sidebar = styled.aside`
  background: white;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 24px;

  @media (max-width: 960px) {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    width: min(84vw, 320px);
    max-width: 320px;
    border-radius: 0 20px 20px 0;
    padding: 24px 16px;
    z-index: 30;
    overflow-y: auto;
    transform: translateX(${props => (props.$isOpen ? '0' : '-105%')});
    transition: transform 0.24s ease;
  }
`;

const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
`;

const SidebarHeading = styled.p`
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
`;

const SidebarCloseButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 10px;
  background: #f1f5f9;
  color: #1e293b;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #e2e8f0;
  }

  @media (max-width: 960px) {
    display: inline-flex;
  }
`;

const SidebarNav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SidebarNavItem = styled.button`
  width: 100%;
  padding: 14px 16px;
  border: 1px solid ${props => (props.$active ? '#99f6e4' : 'transparent')};
  background: ${props => (props.$active ? '#ecfeff' : 'transparent')};
  color: ${props => (props.$active ? '#0f766e' : '#475569')};
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: left;

  &:hover {
    background: ${props => (props.$active ? '#ccfbf1' : '#f8fafc')};
    color: ${props => (props.$active ? '#115e59' : '#1e293b')};
  }
`;

const SidebarNavText = styled.span`
  min-width: 0;
`;

const AdminMain = styled.div`
  min-width: 0;
`;

const TabContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 20px 16px;
    border-radius: 14px;
  }
`;

const LoginForm = styled.form`
  max-width: 400px;
  margin: 0 auto;
  background: white;
  padding: 40px;
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 32px 24px;
  }
`;

const FormTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #1e293b;
  text-align: center;
  margin-bottom: 32px;
`;

const LoginLogo = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const LoginLogoImage = styled.img.attrs({
  width: BRAND_LOGO_WIDTH,
  height: BRAND_LOGO_HEIGHT,
  decoding: 'async',
})`
  height: 60px;
  width: auto;
  max-width: 200px;
  object-fit: contain;
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #2c9fa4;
  }
`;

const SelectInput = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  background: white;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #2c9fa4;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 110px;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #2c9fa4;
  }
`;

const AltNameCalloutColorSection = styled.div`
  display: grid;
  gap: 6px;
`;

const AltNameCalloutColorLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
`;

const AltNameCalloutColorRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;

const AltNameCalloutColorButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !['$selected', '$swatch'].includes(prop),
})`
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 999px;
  border: 2px solid ${props => props.$selected ? '#0f172a' : '#ffffff'};
  background: ${props => props.$swatch};
  box-shadow: ${props => props.$selected
    ? '0 0 0 2px rgba(15, 23, 42, 0.14)'
    : '0 1px 3px rgba(15, 23, 42, 0.18)'};
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(15, 23, 42, 0.18);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.24);
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 12px 24px;
  background: #2c9fa4;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #2c9fa4;
  }
  
  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

const SearchAndFilterSection = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
`;

const UploadPanel = styled(SearchAndFilterSection)`
  display: grid;
  gap: 18px;
`;

const UploadPanelHeader = styled.div`
  display: grid;
  gap: 6px;
`;

const UploadPanelTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
`;

const UploadPanelDescription = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: #64748b;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  margin-bottom: 16px;
  
  &:focus {
    outline: none;
    border-color: #2c9fa4;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  &::placeholder {
    color: #94a3b8;
  }
`;

const FilterRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FilterLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  min-width: 120px;
  
  &:focus {
    outline: none;
    border-color: #2c9fa4;
  }
`;

const ResultsCount = styled.div`
  font-size: 14px;
  color: #64748b;
  margin-left: auto;
`;

const ClearFiltersButton = styled.button`
  padding: 8px 16px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e2e8f0;
    color: #374151;
  }
`;

const BoardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
`;

const BoardCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  
  &:hover {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const BoardTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 0;
  overflow: hidden;
  
  & > span:first-child {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const PublishStatus = styled.span`
  font-size: 12px;
  font-weight: 500;
  padding: 4px 8px;
  border-radius: 12px;
  background: ${props => props.published ? '#dcfce7' : '#fef3c7'};
  color: ${props => props.published ? '#166534' : '#92400e'};
  border: 1px solid ${props => props.published ? '#bbf7d0' : '#fde68a'};
`;

const StatusPillRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

const PopularStatus = styled.span`
  font-size: 12px;
  font-weight: 500;
  padding: 4px 8px;
  border-radius: 12px;
  background: ${props => props.$active ? '#e0f2fe' : '#f8fafc'};
  color: ${props => props.$active ? '#075985' : '#64748b'};
  border: 1px solid ${props => props.$active ? '#bae6fd' : '#e2e8f0'};
`;

const BoardInfo = styled.div`
  color: #64748b;
  font-size: 14px;
  margin-bottom: 16px;
`;

const BoardMetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const PopularToggle = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid #dbe4f0;
  border-radius: 10px;
  background: #f8fafc;
  font-size: 12px;
  font-weight: 500;
  color: #334155;
  cursor: ${props => (props.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.$disabled ? 0.65 : 1)};

  input {
    margin: 0;
  }
`;

const BoardActions = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 12px;
`;

const ActionButton = styled.button`
  padding: 6px 10px;
  border: 1px solid #e2e8f0;
  background: white;
  color: #64748b;
  border-radius: 6px;
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
  
  &:hover {
    background: #f1f5f9;
  }
  
  &.edit {
    color: #2c9fa4;
    border-color: #2c9fa4;
    
    &:hover {
      background: #eff6ff;
    }
  }
  
  &.view {
    color: #10b981;
    border-color: #10b981;
    
    &:hover {
      background: #ecfdf5;
    }
  }
  
  &.delete {
    color: #ef4444;
    border-color: #ef4444;
    
    &:hover {
      background: #fef2f2;
    }
  }
  
  &.publish {
    color: #10b981;
    border-color: #10b981;
    
    &:hover {
      background: #ecfdf5;
    }
  }
  
  &.unpublish {
    color: #f59e0b;
    border-color: #f59e0b;
    
    &:hover {
      background: #fffbeb;
    }
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    
    &:hover {
      background: white;
    }
  }
`;

const UploadArea = styled.div`
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  padding: 40px;
  text-align: center;
  background: #f9fafb;
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    border-color: #2c9fa4;
    background: #eff6ff;
  }
  
  &.dragover {
    border-color: #1c6568;
    background: #eff6ff;
  }
`;

const UploadIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  color: #9ca3af;
`;

const UploadText = styled.div`
  font-size: 16px;
  color: #6b7280;
  margin-bottom: 8px;
`;

const UploadSubtext = styled.div`
  font-size: 14px;
  color: #9ca3af;
`;

const FileInput = styled.input`
  display: none;
`;

const Modal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  max-width: ${props => props.$wide ? '1280px' : '600px'};
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  
  &:hover {
    background: #f1f5f9;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormSectionHeading = styled.h4`
  margin: 8px 0 0;
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
`;

const FormSectionHint = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: #64748b;
`;

const CheckboxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 10px;
  margin-top: 4px;
`;

const CheckboxChip = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid #dbe4f0;
  border-radius: 10px;
  background: #f8fafc;
  font-size: 13px;
  color: #334155;
  cursor: pointer;

  input {
    margin: 0;
  }
`;

const FieldHelperText = styled.div`
  margin-top: 6px;
  font-size: 12px;
  line-height: 1.5;
  color: #64748b;
`;

const FieldActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-bottom: 8px;
  flex-wrap: wrap;
`;

const FieldActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #ffffff;
  color: #1e40af;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #eff6ff;
    border-color: #93c5fd;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const SuccessMessage = styled.div`
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #16a34a;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const BoardsLoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: 3rem 2rem;
`;

const BoardsLoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
`;

const SpinnerRing = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(45deg, #2c9fa4, #8b5cf6);
  animation: pulse 2s ease-in-out infinite;
  
  &::before {
    content: '';
    position: absolute;
    top: 4px;
    left: 4px;
    right: 4px;
    bottom: 4px;
    background: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 20px;
    height: 20px;
    border: 3px solid #2c9fa4;
    border-top: 3px solid transparent;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes pulse {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.05); opacity: 0.8; }
  }
  
  @keyframes spin {
    0% { transform: translate(-50%, -50%) rotate(0deg); }
    100% { transform: translate(-50%, -50%) rotate(360deg); }
  }
`;

const LoadingContent = styled.div`
  text-align: center;
  color: #4a5568;
`;

const LoadingTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: #1e293b;
`;

const LoadingText = styled.p`
  font-size: 1rem;
  margin: 0 0 1rem 0;
  color: #64748b;
`;

const LoadingDots = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  
  span {
    width: 8px;
    height: 8px;
    background: #2c9fa4;
    border-radius: 50%;
    animation: bounce 1.4s ease-in-out infinite both;
    
    &:nth-child(1) { animation-delay: -0.32s; }
    &:nth-child(2) { animation-delay: -0.16s; }
    &:nth-child(3) { animation-delay: 0s; }
  }
  
  @keyframes bounce {
    0%, 80%, 100% { transform: scale(0); }
    40% { transform: scale(1); }
  }
`;

const WiringGuideContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const WiringStep = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
`;

const StepTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BoardSelector = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const SelectorGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
`;

const SelectorLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`;

const BoardSelect = styled.select`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #2c9fa4;
  }
`;

const PinSelector = styled.select`
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #2c9fa4;
  }
`;

const ConnectionArrow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  font-size: 18px;
`;

const ConnectionRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 16px;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f1f5f9;
  
  &:last-child {
    border-bottom: none;
  }
`;

const AddConnectionButton = styled.button`
  padding: 8px 16px;
  background: #2c9fa4;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 16px;
  
  &:hover {
    background: #2563eb;
  }
`;

const RemoveConnectionButton = styled.button`
  padding: 4px 8px;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #dc2626;
  }
`;

const GenerateButton = styled.button`
  padding: 12px 24px;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 24px;
  
  &:hover {
    background: #059669;
  }
  
  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

const WiringGuideContent = () => {
  const location = useLocation();
  const [viewMode, setViewMode] = useState('create'); // 'create', 'edit', or 'manage'
  
  // Creation state
  const [sensorBoards, setSensorBoards] = useState([]);
  const [microcontrollerBoards, setMicrocontrollerBoards] = useState([]);
  const [selectedSensor, setSelectedSensor] = useState('');
  const [selectedMicrocontroller, setSelectedMicrocontroller] = useState('');
  const [selectedSensorBoard, setSelectedSensorBoard] = useState(null);
  const [selectedMicrocontrollerBoard, setSelectedMicrocontrollerBoard] = useState(null);
  const [sensorPins, setSensorPins] = useState([]);
  const [microcontrollerPins, setMicrocontrollerPins] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generatedSVG, setGeneratedSVG] = useState('');
  const [latestManualGeneratedSVG, setLatestManualGeneratedSVG] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [description, setDescription] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [seoKeywords, setSeoKeywords] = useState('');
  const [tutorialLinks, setTutorialLinks] = useState([]);
  const [currentWiringGuide, setCurrentWiringGuide] = useState(null);
  const [uploadingWiring, setUploadingWiring] = useState(false);
  const [uploadedSVG, setUploadedSVG] = useState(null); // Store uploaded SVG/image from file
  const [usingUploadedSVG, setUsingUploadedSVG] = useState(false); // Flag to use uploaded SVG instead of generated
 // 0, 90, 180, 270 degrees
  
  // Management state
  const [wiringGuides, setWiringGuides] = useState([]);
  const [manageLoading, setManageLoading] = useState(true);
  const [manageError, setManageError] = useState(null);
  const [editingGuide, setEditingGuide] = useState(null);
  const [loadingGuideForEdit, setLoadingGuideForEdit] = useState(false);
  const [preserveLoadedEditSvg, setPreserveLoadedEditSvg] = useState(false);
  const [handledRequestedEditGuideId, setHandledRequestedEditGuideId] = useState('');
  
  // Pan/Zoom state
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const isEditMode = viewMode === 'edit';
  const activeGuide = isEditMode ? editingGuide : currentWiringGuide;
  const requestedEditGuideId = useMemo(() => {
    const urlParams = new URLSearchParams(location.search);
    return String(urlParams.get('editGuide') || '');
  }, [location.search]);
  const clearGeneratedGuideReference = useCallback(() => {
    if (!isEditMode) {
      setCurrentWiringGuide(null);
    }
  }, [isEditMode]);

  const resetEditorState = useCallback(() => {
    setSelectedSensor('');
    setSelectedMicrocontroller('');
    setSelectedSensorBoard(null);
    setSelectedMicrocontrollerBoard(null);
    setSensorPins([]);
    setMicrocontrollerPins([]);
    setConnections([]);
    setGeneratedSVG('');
    setLatestManualGeneratedSVG('');
    setShowPreview(false);
    setDescription('');
    setMetaDescription('');
    setSeoKeywords('');
    setTutorialLinks([]);
    setCurrentWiringGuide(null);
    setEditingGuide(null);
    setUploadedSVG(null);
    setUsingUploadedSVG(false);
    setPreserveLoadedEditSvg(false);
    setZoomLevel(1);
    setPanX(0);
    setPanY(0);
  }, []);

  const openCreateMode = useCallback(() => {
    resetEditorState();
    setViewMode('create');
  }, [resetEditorState]);

  const openManageMode = useCallback(() => {
    setEditingGuide(null);
    setPreserveLoadedEditSvg(false);
    setViewMode('manage');
  }, []);

  // Legacy selector handlers remain as no-ops while the new drag-to-connect editor fully replaces Step 2.
  const addConnection = useCallback(() => {}, []);
  const removeConnection = useCallback(() => {}, []);
  const updateConnection = useCallback(() => {}, []);

  const getCompleteConnections = () => {
    return connections.filter(connection => connection.sensorPin && connection.microcontrollerPin);
  };

  const handleConnectionsChange = useCallback((nextConnectionsOrUpdater) => {
    setPreserveLoadedEditSvg(false);
    setConnections((currentConnections) => {
      const nextConnections = typeof nextConnectionsOrUpdater === 'function'
        ? nextConnectionsOrUpdater(currentConnections)
        : nextConnectionsOrUpdater;

      return Array.isArray(nextConnections) ? nextConnections : [];
    });
    clearGeneratedGuideReference();
  }, [clearGeneratedGuideReference]);

  const handleManualGeneratedSvgChange = useCallback((nextSvg) => {
    if (usingUploadedSVG) {
      return;
    }

    const normalizedSvg = String(nextSvg || '');

    console.log(
      'Admin received manual SVG metadata:',
      normalizedSvg.includes('data-connection-index'),
      normalizedSvg.includes('wiring-guide-wire')
    );

    setLatestManualGeneratedSVG(normalizedSvg);

    if (preserveLoadedEditSvg) {
      return;
    }

    setGeneratedSVG(normalizedSvg);
    clearGeneratedGuideReference();
  }, [clearGeneratedGuideReference, preserveLoadedEditSvg, usingUploadedSVG]);

  const handleDiagramInteraction = useCallback(() => {
    setPreserveLoadedEditSvg(false);
  }, []);

  const buildUploadedDiagramSVG = (imageData) => {
    const canvasWidth = 1200;
    const canvasHeight = 800;
    const imageFrameX = 80;
    const imageFrameY = 130;
    const imageFrameWidth = 1040;
    const imageFrameHeight = 540;

    return `
      <svg xmlns="http://www.w3.org/2000/svg" width="${canvasWidth}" height="${canvasHeight}" viewBox="0 0 ${canvasWidth} ${canvasHeight}">
        <rect width="${canvasWidth}" height="${canvasHeight}" fill="#ffffff" />
        <image
          href="${imageData}"
          x="${imageFrameX}"
          y="${imageFrameY}"
          width="${imageFrameWidth}"
          height="${imageFrameHeight}"
          preserveAspectRatio="xMidYMid meet"
        />
      </svg>
    `.trim();
  };

  useEffect(() => {
    fetchBoards();
  }, []);

  const fetchBoards = async () => {
    try {
      // Fetch sensor boards
      const { adminBoardsAPI } = await import('../services/adminApi');
      const sensorResponse = await adminBoardsAPI.getByCategory('sensor-module');
      const microcontrollerResponse = await adminBoardsAPI.getByCategory('microcontroller-8bit,microcontroller-16bit,microcontroller-32bit,development-board');
      
      if (sensorResponse.status === 200 && microcontrollerResponse.status === 200) {
        const sensors = sensorResponse.data;
        const microcontrollers = microcontrollerResponse.data;
        
        setSensorBoards(sensors);
        setMicrocontrollerBoards(microcontrollers);
      } else {
        // Fallback to old method if new API fails
        const response = await adminBoardsAPI.getAll();
        
        if (response.status === 200) {
          const boards = response.data;
          
          // Filter boards by category field or fallback to name-based heuristics
          const sensors = boards.filter(board => 
            board.category === 'sensor-module' ||
            board.name.toLowerCase().includes('sensor') || 
            board.name.toLowerCase().includes('module') ||
            board.name.toLowerCase().includes('shield')
          );
          
          const microcontrollers = boards.filter(board => 
            ['microcontroller-8bit', 'microcontroller-16bit', 'microcontroller-32bit', 'development-board'].includes(board.category) ||
            board.name.toLowerCase().includes('arduino') ||
            board.name.toLowerCase().includes('esp') ||
            board.name.toLowerCase().includes('raspberry') ||
            board.name.toLowerCase().includes('microcontroller') ||
            board.name.toLowerCase().includes('board')
          );
          
          setSensorBoards(sensors);
          setMicrocontrollerBoards(microcontrollers);
        }
      }
    } catch (error) {
      console.error('Failed to fetch boards:', error);
    }
  };

  const loadBoardSelection = useCallback(async (boardId, type) => {
    const resetSelection = () => {
      if (type === 'sensor') {
        setSelectedSensorBoard(null);
        setSensorPins([]);
      } else {
        setSelectedMicrocontrollerBoard(null);
        setMicrocontrollerPins([]);
      }
    };

    if (!boardId) {
      resetSelection();
      return;
    }

    try {
      const { adminBoardsAPI } = await import('../services/adminApi');
      const [boardResponse, pinsResponse] = await Promise.all([
        adminBoardsAPI.getById(boardId),
        adminBoardsAPI.getPins(boardId),
      ]);

      if (boardResponse.status === 200 && pinsResponse.status === 200) {
        const boardData = boardResponse.data;
        const pins = pinsResponse.data;

        if (type === 'sensor') {
          setSelectedSensorBoard(boardData);
          setSensorPins(pins);
        } else {
          setSelectedMicrocontrollerBoard(boardData);
          setMicrocontrollerPins(pins);
        }
        return;
      }

      resetSelection();
    } catch (error) {
      console.error(`Failed to load ${type} board data:`, error);
      resetSelection();
    }
  }, []);

  const handleSensorChange = (e) => {
    const boardId = e.target.value;
    setPreserveLoadedEditSvg(false);
    setSelectedSensor(boardId);
    loadBoardSelection(boardId, 'sensor');
    setConnections([]);
    // Reset uploaded SVG when boards change
    setUploadedSVG(null);
    setUsingUploadedSVG(false);
    setGeneratedSVG('');
    setLatestManualGeneratedSVG('');
    clearGeneratedGuideReference();
    setShowPreview(false);
  };

  const handleMicrocontrollerChange = (e) => {
    const boardId = e.target.value;
    setPreserveLoadedEditSvg(false);
    setSelectedMicrocontroller(boardId);
    loadBoardSelection(boardId, 'microcontroller');
    setConnections([]);
    // Reset uploaded SVG when boards change
    setUploadedSVG(null);
    setUsingUploadedSVG(false);
    setGeneratedSVG('');
    setLatestManualGeneratedSVG('');
    clearGeneratedGuideReference();
    setShowPreview(false);
  };

  const handleWiringDiagramUpload = async (file) => {
    if (!selectedSensor || !selectedMicrocontroller) {
      alert('Please select both sensor and microcontroller boards first');
      return;
    }

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a valid image file (PNG, JPG, SVG, GIF, or WebP)');
      return;
    }

    setUploadingWiring(true);
    try {
      // For image files, we'll convert them to base64 or use FileReader to display
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const imageData = e.target.result;

        const uploadedDiagramSVG = buildUploadedDiagramSVG(imageData);
        setPreserveLoadedEditSvg(false);
        setUploadedSVG(uploadedDiagramSVG);
        setUsingUploadedSVG(true);
        setGeneratedSVG(uploadedDiagramSVG);
        setLatestManualGeneratedSVG('');
        clearGeneratedGuideReference();
        
        setShowPreview(true);
        // Reset pan/zoom when new image is loaded
        setZoomLevel(1);
        setPanX(0);
        setPanY(0);
        setUploadingWiring(false);
        alert('Wiring diagram image uploaded successfully!');
      };
      
      reader.onerror = () => {
        setUploadingWiring(false);
        alert('Failed to read image file. Please try again.');
      };
      
      // Read as data URL for display
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading wiring diagram:', error);
      alert('Failed to upload wiring diagram: ' + (error.message || 'Unknown error'));
      setUploadingWiring(false);
    }
  };

  const handleWiringFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleWiringDiagramUpload(file);
    }
  };

  const handleWiringDrop = (e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleWiringDiagramUpload(files[0]);
    }
  };

  const handleWiringDragOver = (e) => {
    e.preventDefault();
  };

  const removeUploadedDiagram = () => {
    setPreserveLoadedEditSvg(false);
    setUploadedSVG(null);
    setUsingUploadedSVG(false);
    setGeneratedSVG('');
    setLatestManualGeneratedSVG('');
    setShowPreview(false);
    alert('Uploaded diagram removed. You can now add connections manually or upload a new image.');
  };

  const generatePreview = async () => {
    const completeConnections = getCompleteConnections();

    // If using uploaded SVG, don't generate a new one
    if (usingUploadedSVG && uploadedSVG) {
      setShowPreview(true);
      return;
    }

    if (!selectedSensor || !selectedMicrocontroller || completeConnections.length === 0) {
      alert('Please select both boards and add at least one connection');
      return;
    }

    if (!generatedSVG) {
      alert('The manual wiring preview is still being prepared. Try moving a board or creating a connection again.');
      return;
    }

    clearGeneratedGuideReference();
    setShowPreview(true);
    // Reset pan/zoom when new SVG is generated
    setZoomLevel(1);
    setPanX(0);
    setPanY(0);
  };

  const buildWiringGuideRequestData = () => {
    const completeConnections = getCompleteConnections();
    const finalDiagram = usingUploadedSVG
      ? (uploadedSVG || generatedSVG || '')
      : (latestManualGeneratedSVG || (preserveLoadedEditSvg ? '' : generatedSVG) || '');
    const normalizedTutorialLinks = normalizeAffiliateLinks(tutorialLinks);
    const tutorialLinksValidationError = validateAffiliateLinks(tutorialLinks, 'Tutorial link');

    if (!selectedSensor || !selectedMicrocontroller) {
      alert('Please select both boards');
      return null;
    }

    if (completeConnections.length === 0 && !usingUploadedSVG) {
      alert('Please add at least one connection or upload a wiring diagram image');
      return null;
    }

    if (!finalDiagram) {
      alert('The manual wiring preview is not ready yet. Please try again after the editor finishes rendering.');
      return null;
    }

    if (tutorialLinksValidationError) {
      alert(tutorialLinksValidationError);
      return null;
    }

    console.log(
      'Admin saving finalDiagram metadata:',
      finalDiagram.includes('data-connection-index'),
      finalDiagram.includes('wiring-guide-wire')
    );

    return {
      sensorBoardId: selectedSensor,
      microcontrollerBoardId: selectedMicrocontroller,
      svgContent: finalDiagram,
      connections: completeConnections,
      description,
      metaDescription,
      seoKeywords,
      tutorialLinks: normalizedTutorialLinks,
      useUploadedSVG: usingUploadedSVG
    };
  };

  const saveAsDraft = async () => {
    const requestData = buildWiringGuideRequestData();

    if (!requestData) {
      return;
    }

    setLoading(true);
    try {
      const { adminWiringGuideAPI } = await import('../services/adminApi');

      const response = await adminWiringGuideAPI.generate(requestData);

      const result = response.data;
      const generatedGuideSeo = {
        sensor_name: selectedSensorBoard?.name || '',
        microcontroller_name: selectedMicrocontrollerBoard?.name || '',
        description,
        meta_description: result?.data?.meta_description ?? requestData.metaDescription,
        seo_keywords: result?.data?.seo_keywords ?? requestData.seoKeywords,
      };
      setCurrentWiringGuide({
        id: result.data.wiringGuideId,
        published: false,
        slug: result.data.slug,
        description,
        meta_description: deriveWiringGuideSeoDescription(generatedGuideSeo),
        seo_keywords: deriveWiringGuideSeoKeywords(generatedGuideSeo),
        tutorial_links: requestData.tutorialLinks
      });
      alert('Wiring saved as draft successfully!');
      // Refresh the wiring guides list
      fetchWiringGuides();
    } catch (error) {
      console.error('Error saving draft:', error);
      alert('Failed to save as draft');
    } finally {
      setLoading(false);
    }
  };

  const publishWiringGuide = async () => {
    const requestData = buildWiringGuideRequestData();

    if (!requestData) {
      return;
    }

    setLoading(true);
    try {
      const { adminWiringGuideAPI } = await import('../services/adminApi');

      const response = await adminWiringGuideAPI.generate(requestData);

      const result = response.data;
      const generatedGuideSeo = {
        sensor_name: selectedSensorBoard?.name || '',
        microcontroller_name: selectedMicrocontrollerBoard?.name || '',
        description,
        meta_description: result?.data?.meta_description ?? requestData.metaDescription,
        seo_keywords: result?.data?.seo_keywords ?? requestData.seoKeywords,
      };
      setCurrentWiringGuide({
        id: result.data.wiringGuideId,
        published: true,
        slug: result.data.slug,
        description,
        meta_description: deriveWiringGuideSeoDescription(generatedGuideSeo),
        seo_keywords: deriveWiringGuideSeoKeywords(generatedGuideSeo),
        tutorial_links: requestData.tutorialLinks
      });
      
      // Publish the guide
      const publishResponse = await adminFetch(`/admin/wiring-guide/${result.data.wiringGuideId}/publish`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ published: true })
      });

      if (publishResponse.ok) {
        alert('Wiring published successfully!');
        // Refresh the wiring guides list
        fetchWiringGuides();
      } else {
        alert('Failed to publish wiring');
      }
    } catch (error) {
      console.error('Error publishing wiring:', error);
      alert('Failed to publish wiring');
    } finally {
      setLoading(false);
    }
  };

  const togglePublishStatus = async (publish) => {
    if (!currentWiringGuide) return;

    setLoading(true);
    try {
      const response = await adminFetch(`/admin/wiring-guide/${currentWiringGuide.id}/publish`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ published: publish })
      });

      if (response.ok) {
        setCurrentWiringGuide(prev => ({ ...prev, published: publish }));
        alert(publish ? 'Wiring published successfully!' : 'Wiring unpublished successfully!');
        // Refresh the wiring guides list
        fetchWiringGuides();
      } else {
        alert(`Failed to ${publish ? 'publish' : 'unpublish'} wiring`);
      }
    } catch (error) {
      console.error(`Error ${publish ? 'publishing' : 'unpublishing'} wiring:`, error);
      alert(`Failed to ${publish ? 'publish' : 'unpublish'} wiring`);
    } finally {
      setLoading(false);
    }
  };

  // Pan/Zoom functions
  const zoomWiringGuide = (factor) => {
    setZoomLevel(prev => Math.max(0.1, Math.min(5, prev * factor)));
  };

  const resetWiringGuideZoom = useCallback(() => {
    setZoomLevel(1);
    setPanX(0);
    setPanY(0);
  }, []);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - panX, y: e.clientY - panY });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setPanX(e.clientX - dragStart.x);
      setPanY(e.clientY - dragStart.y);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    zoomWiringGuide(delta);
  };


  // Management functions
  const fetchWiringGuides = async () => {
    try {
      setManageLoading(true);
      const { adminWiringGuidesAPI } = await import('../services/adminApi');
      const response = await adminWiringGuidesAPI.getAll();
      
      if (response.status === 200) {
        const data = response.data;
        setWiringGuides(data);
        setManageError(null);
      } else {
        setManageError('Failed to load wiring');
      }
    } catch (err) {
      console.error('Error fetching wiring:', err);
      setManageError('Failed to load wiring');
    } finally {
      setManageLoading(false);
    }
  };

  const togglePublish = async (id, currentStatus) => {
    try {
      const response = await adminFetch(`/admin/wiring-guide/${id}/publish`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ published: !currentStatus })
      });

      if (response.ok) {
        setWiringGuides(prev => 
          prev.map(guide => 
            guide.id === id ? { ...guide, published: !currentStatus } : guide
          )
        );
      } else {
        alert('Failed to update wiring status');
      }
    } catch (error) {
      console.error('Error updating wiring:', error);
      alert('Failed to update wiring status');
    }
  };

  const deleteWiringGuide = async (id) => {
    if (!window.confirm('Are you sure you want to delete this wiring?')) return;
    
    try {
      const response = await adminFetch(`/admin/wiring-guide/${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setWiringGuides(prev => prev.filter(guide => guide.id !== id));
        alert('Wiring deleted successfully');
      } else {
        alert('Failed to delete wiring');
      }
    } catch (error) {
      console.error('Error deleting wiring:', error);
      alert('Failed to delete wiring');
    }
  };

  const openEditPage = useCallback(async (guide) => {
    if (!guide?.id) {
      return;
    }

    setLoadingGuideForEdit(true);
    setViewMode('edit');
    setCurrentWiringGuide(null);

    try {
      const { adminWiringGuidesAPI } = await import('../services/adminApi');
      const response = await adminWiringGuidesAPI.getById(guide.id);
      const guideData = response.data?.data || response.data;
      const normalizedConnections = normalizeWiringGuideConnections(guideData?.connections);
      const normalizedTutorialLinks = normalizeAffiliateLinks(guideData?.tutorial_links || guideData?.tutorialLinks);
      const uploadedDiagram = looksLikeUploadedWiringDiagram(guideData?.svg_content);
      const sensorBoardId = String(guideData?.sensor_board_id || '');
      const microcontrollerBoardId = String(guideData?.microcontroller_board_id || '');
      const normalizedGuideSeo = {
        ...guideData,
        meta_description: deriveWiringGuideSeoDescription(guideData),
        seo_keywords: deriveWiringGuideSeoKeywords(guideData),
      };

      setSelectedSensor(sensorBoardId);
      setSelectedMicrocontroller(microcontrollerBoardId);

      await Promise.all([
        loadBoardSelection(sensorBoardId, 'sensor'),
        loadBoardSelection(microcontrollerBoardId, 'microcontroller')
      ]);

      setConnections(normalizedConnections);
      setDescription(normalizedGuideSeo.description || '');
      setMetaDescription(normalizedGuideSeo.meta_description || '');
      setSeoKeywords(normalizedGuideSeo.seo_keywords || '');
      setTutorialLinks(normalizedTutorialLinks);
      setEditingGuide({
        ...normalizedGuideSeo,
        connections: normalizedConnections,
        tutorial_links: normalizedTutorialLinks,
        sensor_name: getWiringGuideSensorName(normalizedGuideSeo) || getWiringGuideSensorName(guide),
        microcontroller_name: getWiringGuideMicrocontrollerName(normalizedGuideSeo) || getWiringGuideMicrocontrollerName(guide)
      });
      setUploadedSVG(uploadedDiagram ? (guideData?.svg_content || '') : null);
      setUsingUploadedSVG(uploadedDiagram);
      setGeneratedSVG(guideData?.svg_content || '');
      setLatestManualGeneratedSVG('');
      setShowPreview(Boolean(guideData?.svg_content));
      setPreserveLoadedEditSvg(Boolean(guideData?.svg_content) && !uploadedDiagram);
      resetWiringGuideZoom();
    } catch (error) {
      console.error('Error loading wiring for editing:', error);
      alert('Failed to load wiring for editing');
      openManageMode();
    } finally {
      setLoadingGuideForEdit(false);
    }
  }, [loadBoardSelection, openManageMode, resetWiringGuideZoom]);

  useEffect(() => {
    if (!requestedEditGuideId) {
      if (handledRequestedEditGuideId) {
        setHandledRequestedEditGuideId('');
      }
      return;
    }

    if (loadingGuideForEdit || handledRequestedEditGuideId === requestedEditGuideId) {
      return;
    }

    if (editingGuide && String(editingGuide.id) === requestedEditGuideId) {
      setHandledRequestedEditGuideId(requestedEditGuideId);
      return;
    }

    setHandledRequestedEditGuideId(requestedEditGuideId);
    openEditPage({ id: requestedEditGuideId });
  }, [
    editingGuide,
    handledRequestedEditGuideId,
    loadingGuideForEdit,
    openEditPage,
    requestedEditGuideId
  ]);

  const closeEditPage = () => {
    resetEditorState();
    setViewMode('manage');
  };

  const saveEditedWiringGuide = async (publishedOverride = null) => {
    if (!editingGuide) return;

    const requestData = buildWiringGuideRequestData();

    if (!requestData) {
      return;
    }

    const updatePayload = {
      ...requestData,
      sensor_board_id: requestData.sensorBoardId,
      microcontroller_board_id: requestData.microcontrollerBoardId
    };

    if (typeof publishedOverride === 'boolean') {
      updatePayload.published = publishedOverride;
    }

    setLoading(true);
    try {
      const { adminWiringGuidesAPI } = await import('../services/adminApi');
      const response = await adminWiringGuidesAPI.update(editingGuide.id, updatePayload);
      const updatedGuide = response.data?.data || response.data || {};
      const normalizedConnections = normalizeWiringGuideConnections(updatedGuide.connections || requestData.connections);
      const normalizedTutorialLinks = normalizeAffiliateLinks(updatedGuide.tutorial_links || requestData.tutorialLinks);
      const normalizedMetaDescription = deriveWiringGuideSeoDescription({
        ...editingGuide,
        ...updatedGuide,
        description: updatedGuide.description ?? requestData.description,
        meta_description: updatedGuide.meta_description ?? requestData.metaDescription ?? '',
      });
      const normalizedSeoKeywords = deriveWiringGuideSeoKeywords({
        ...editingGuide,
        ...updatedGuide,
        seo_keywords: updatedGuide.seo_keywords ?? requestData.seoKeywords ?? '',
      });
      const nextGuide = {
        ...editingGuide,
        ...updatedGuide,
        connections: normalizedConnections,
        tutorial_links: normalizedTutorialLinks,
        meta_description: normalizedMetaDescription,
        seo_keywords: normalizedSeoKeywords,
        sensor_name: getWiringGuideSensorName(updatedGuide) || getWiringGuideSensorName(editingGuide),
        microcontroller_name: getWiringGuideMicrocontrollerName(updatedGuide) || getWiringGuideMicrocontrollerName(editingGuide),
        description: updatedGuide.description ?? requestData.description,
        published: typeof publishedOverride === 'boolean'
          ? publishedOverride
          : (updatedGuide.published ?? editingGuide.published)
      };
      const nextSvgContent = updatedGuide.svg_content || requestData.svgContent;
      const nextUsesUploadedDiagram = looksLikeUploadedWiringDiagram(nextSvgContent);

      setEditingGuide(nextGuide);
      setDescription(nextGuide.description || '');
      setMetaDescription(nextGuide.meta_description || '');
      setSeoKeywords(nextGuide.seo_keywords || '');
      setTutorialLinks(normalizedTutorialLinks);
      setConnections(normalizedConnections);
      setUploadedSVG(nextUsesUploadedDiagram ? nextSvgContent : null);
      setUsingUploadedSVG(nextUsesUploadedDiagram);
      setGeneratedSVG(nextSvgContent || '');
      setLatestManualGeneratedSVG(nextUsesUploadedDiagram ? '' : (nextSvgContent || ''));
      setShowPreview(Boolean(nextSvgContent));
      setPreserveLoadedEditSvg(false);

      setWiringGuides(prev =>
        prev.map(guideItem =>
          guideItem.id === nextGuide.id
            ? {
                ...guideItem,
                ...nextGuide,
                sensor_name: getWiringGuideSensorName(nextGuide),
                microcontroller_name: getWiringGuideMicrocontrollerName(nextGuide),
                description: nextGuide.description,
                meta_description: nextGuide.meta_description,
                seo_keywords: nextGuide.seo_keywords,
                tutorial_links: normalizedTutorialLinks
              }
            : guideItem
        )
      );

      fetchWiringGuides();
      alert(
        publishedOverride === true
          ? 'Wiring saved and published successfully'
          : publishedOverride === false
            ? 'Wiring saved and unpublished successfully'
            : 'Wiring updated successfully'
      );
    } catch (error) {
      console.error('Error updating wiring:', error);
      alert('Failed to update wiring');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Load wiring guides when switching to manage mode
  useEffect(() => {
    if (viewMode === 'manage') {
      fetchWiringGuides();
    }
  }, [viewMode]);

  return (
    <WiringGuideContainer>
      {/* Header with mode toggle */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px',
        paddingBottom: '16px',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <div>
          <h2 style={{ margin: '0 0 8px 0', color: '#1f2937' }}>Wiring</h2>
          <p style={{ margin: '0', color: '#6b7280' }}>
            {viewMode === 'create'
              ? 'Create new wiring and manage connections'
              : viewMode === 'edit'
                ? 'Edit existing wiring with the full builder'
                : 'Manage existing wiring'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={openCreateMode}
            style={{
              padding: '8px 16px',
              background: viewMode === 'create' ? '#2c9fa4' : '#f3f4f6',
              color: viewMode === 'create' ? 'white' : '#374151',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Link size={16} />
            Create
          </button>
          <button
            onClick={openManageMode}
            style={{
              padding: '8px 16px',
              background: viewMode === 'manage' ? '#2c9fa4' : '#f3f4f6',
              color: viewMode === 'manage' ? 'white' : '#374151',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <FileText size={16} />
            Manage
          </button>
          {viewMode === 'edit' && (
            <button
              type="button"
              style={{
                padding: '8px 16px',
                background: '#2c9fa4',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'default',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <Edit size={16} />
              Editing
            </button>
          )}
        </div>
      </div>

      {viewMode !== 'manage' ? (
        <>
          {loadingGuideForEdit ? (
            <WiringStep>
              <div style={{ textAlign: 'center', padding: '20px 0', color: '#6b7280', fontSize: '16px' }}>
                Loading wiring editor...
              </div>
            </WiringStep>
          ) : (
            <>
          {isEditMode && editingGuide && (
            <WiringStep>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '12px',
                flexWrap: 'wrap'
              }}>
                <div>
                  <div style={{ fontSize: '12px', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#64748b', marginBottom: '6px' }}>
                    Editing Guide
                  </div>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: '#1f2937' }}>
                    {getWiringGuideSensorName(editingGuide) || `Sensor Board ID: ${editingGuide.sensor_board_id}`} to {getWiringGuideMicrocontrollerName(editingGuide) || `Microcontroller Board ID: ${editingGuide.microcontroller_board_id}`}
                  </div>
                  <div style={{ marginTop: '6px', fontSize: '13px', color: '#6b7280' }}>
                    Guide ID: {editingGuide.id}
                  </div>
                </div>
                <button
                  onClick={closeEditPage}
                  style={{
                    padding: '8px 14px',
                    background: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Back to Manage
                </button>
              </div>
            </WiringStep>
          )}
          <WiringStep>
            <StepTitle>
              <Link size={20} />
              Step 1: Select Boards
            </StepTitle>
            <BoardSelector>
              <SelectorGroup>
                <SelectorLabel>Sensor Board</SelectorLabel>
                <BoardSelect value={selectedSensor} onChange={handleSensorChange}>
                  <option value="">Select a sensor board...</option>
                  {sensorBoards.map(board => (
                    <option key={board.id} value={board.id}>
                      {board.name} ({board.manufacturer})
                    </option>
                  ))}
                </BoardSelect>
              </SelectorGroup>
              
              <SelectorGroup>
                <SelectorLabel>Microcontroller Board</SelectorLabel>
                <BoardSelect value={selectedMicrocontroller} onChange={handleMicrocontrollerChange}>
                  <option value="">Select a microcontroller board...</option>
                  {microcontrollerBoards.map(board => (
                    <option key={board.id} value={board.id}>
                      {board.name} ({board.manufacturer})
                    </option>
                  ))}
                </BoardSelect>
              </SelectorGroup>
            </BoardSelector>
          </WiringStep>

          {selectedSensor && selectedMicrocontroller && (
            <WiringStep>
              <StepTitle>
                <Link size={20} />
                Step 2: Connect Pins
              </StepTitle>

              <WiringConnectionEditor
                sensorBoard={selectedSensorBoard}
                microcontrollerBoard={selectedMicrocontrollerBoard}
                sensorPins={sensorPins}
                microcontrollerPins={microcontrollerPins}
                connections={connections}
                onConnectionsChange={handleConnectionsChange}
                onGeneratedSvgChange={handleManualGeneratedSvgChange}
                onDiagramInteraction={handleDiagramInteraction}
                initialDiagramSvg={preserveLoadedEditSvg && !usingUploadedSVG ? (editingGuide?.svg_content || '') : ''}
                restoreInitialDiagram={preserveLoadedEditSvg && !usingUploadedSVG}
                disabled={loading || uploadingWiring}
              />
              
              {false && (
                <>
              {connections.map((connection, index) => (
                <ConnectionRow key={index}>
                  <PinSelector
                    value={connection.sensorPin}
                    onChange={(e) => updateConnection(index, 'sensorPin', e.target.value)}
                  >
                    <option value="">Select sensor pin...</option>
                    {sensorPins.map(pin => (
                      <option key={pin.id} value={pin.id}>
                        {pin.pin_name} ({pin.pin_number})
                      </option>
                    ))}
                  </PinSelector>
                  
                  <ConnectionArrow>→</ConnectionArrow>
                  
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <PinSelector
                      value={connection.microcontrollerPin}
                      onChange={(e) => updateConnection(index, 'microcontrollerPin', e.target.value)}
                    >
                      <option value="">Select microcontroller pin...</option>
                      {microcontrollerPins.map(pin => (
                        <option key={pin.id} value={pin.id}>
                          {pin.pin_name} ({pin.pin_number})
                        </option>
                      ))}
                    </PinSelector>
                    
                    <RemoveConnectionButton onClick={() => removeConnection(index)}>
                      ×
                    </RemoveConnectionButton>
                  </div>
                </ConnectionRow>
              ))}
              
              {/* Pin Connections Summary Table - Shows as connections are added */}
              {connections.length > 0 && connections.some(c => c.sensorPin && c.microcontrollerPin) && (
                <div style={{
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '20px',
                  marginTop: '20px',
                  marginBottom: '16px'
                }}>
                  <h3 style={{ 
                    color: '#1f2937', 
                    margin: '0 0 16px 0', 
                    fontSize: '1.25rem', 
                    fontWeight: '600' 
                  }}>
                    Pin Connections Summary
                  </h3>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '14px'
                  }}>
                    <thead style={{ background: '#f9fafb' }}>
                      <tr>
                        <th style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          fontWeight: '600',
                          color: '#374151',
                          borderBottom: '1px solid #e5e7eb'
                        }}>
                          Sensor Board Pin
                        </th>
                        <th style={{
                          padding: '12px 16px',
                          textAlign: 'center',
                          fontWeight: '600',
                          color: '#374151',
                          borderBottom: '1px solid #e5e7eb'
                        }}>
                          Connection
                        </th>
                        <th style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          fontWeight: '600',
                          color: '#374151',
                          borderBottom: '1px solid #e5e7eb'
                        }}>
                          Microcontroller Pin
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {connections
                        .filter(c => c.sensorPin && c.microcontrollerPin)
                        .map((connection, index) => {
                          const sensorPin = sensorPins.find(p => String(p.id) === String(connection.sensorPin));
                          const microcontrollerPin = microcontrollerPins.find(p => String(p.id) === String(connection.microcontrollerPin));
                          return (
                            <tr key={index} style={{
                              borderBottom: '1px solid #e5e7eb'
                            }}>
                              <td style={{
                                padding: '12px 16px',
                                color: '#374151',
                                fontWeight: '500'
                              }}>
                                {sensorPin ? `${sensorPin.pin_name} (${sensorPin.pin_number})` : 'N/A'}
                              </td>
                              <td style={{
                                padding: '12px 16px',
                                textAlign: 'center',
                                color: '#6b7280'
                              }}>
                                →
                              </td>
                              <td style={{
                                padding: '12px 16px',
                                color: '#374151',
                                fontWeight: '500'
                              }}>
                                {microcontrollerPin ? `${microcontrollerPin.pin_name} (${microcontrollerPin.pin_number})` : 'N/A'}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              )}
                </>
              )}
               
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginTop: '16px', flexWrap: 'wrap' }}>
                {false && (
                  <AddConnectionButton onClick={addConnection}>
                    + Add Connection
                  </AddConnectionButton>
                )}

                <div
                  style={{
                    border: `2px dashed ${uploadingWiring ? '#9ca3af' : '#d1d5db'}`,
                    borderRadius: '8px',
                    padding: '12px 16px',
                    background: uploadingWiring ? '#f3f4f6' : '#f9fafb',
                    cursor: uploadingWiring ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flex: '1',
                    minWidth: '200px',
                    position: 'relative'
                  }}
                  onDrop={handleWiringDrop}
                  onDragOver={handleWiringDragOver}
                  onClick={() => !uploadingWiring && document.getElementById('wiringFileInput').click()}
                  onMouseEnter={(e) => {
                    if (!uploadingWiring) {
                      e.currentTarget.style.borderColor = '#2c9fa4';
                      e.currentTarget.style.background = '#eff6ff';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!uploadingWiring) {
                      e.currentTarget.style.borderColor = '#d1d5db';
                      e.currentTarget.style.background = '#f9fafb';
                    }
                  }}
                >
                  <Upload size={20} style={{ color: '#9ca3af' }} />
                  <span style={{ color: '#6b7280', fontSize: '14px', fontWeight: '500' }}>
                    {uploadingWiring ? 'Uploading...' : 'Upload Image Diagram'}
                  </span>
                  <FileInput
                    id="wiringFileInput"
                    type="file"
                    accept="image/png, image/jpeg, image/jpg, image/svg+xml, image/gif, image/webp"
                    onChange={handleWiringFileSelect}
                    disabled={uploadingWiring}
                  />
                </div>
                {usingUploadedSVG && (
                  <button
                    onClick={removeUploadedDiagram}
                    style={{
                      padding: '8px 12px',
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <X size={16} /> Remove Image
                  </button>
                )}
              </div>
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#9ca3af' }}>
                Optional: upload an image (PNG, JPG, SVG, GIF, WebP) to display as the wiring diagram instead of the generated board-to-board view.
              </div>
              {usingUploadedSVG && (
                <div style={{ marginTop: '8px', padding: '8px 12px', background: '#ecfdf5', border: '1px solid #bbf7d0', borderRadius: '6px', fontSize: '12px', color: '#166534' }}>
                  ✓ Using uploaded image diagram. This image will be displayed instead of a generated diagram.
                </div>
              )}
            </WiringStep>
          )}

          {(connections.length > 0 || usingUploadedSVG) && (
            <WiringStep>
              <StepTitle>
                <Link size={20} />
                Step 3: {isEditMode ? 'Review Changes' : usingUploadedSVG ? 'Review Wiring' : 'Generate Wiring'}
              </StepTitle>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  Description (optional):
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter a description for this wiring..."
                  style={{
                    width: '100%',
                    minHeight: '80px',
                    padding: '8px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  SEO Meta Description (optional):
                </label>
                <textarea
                  value={metaDescription}
                  onChange={(e) => setMetaDescription(e.target.value)}
                  placeholder="Short plain-text summary used for the public page meta description..."
                  style={{
                    width: '100%',
                    minHeight: '80px',
                    padding: '8px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                />
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
                  Keep this concise and descriptive. Search engines usually show around 150 to 160 characters.
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  SEO Keywords (optional):
                </label>
                <input
                  type="text"
                  value={seoKeywords}
                  onChange={(e) => setSeoKeywords(e.target.value)}
                  placeholder="bme280 wiring, esp32, i2c, sensor wiring"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
                  Separate keywords with commas.
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  Tutorial Links (optional):
                </label>
                <AffiliateLinksEditor
                  value={tutorialLinks}
                  onChange={setTutorialLinks}
                  itemNameSingular="tutorial link"
                  itemNamePlural="tutorial links"
                  addButtonLabel="Add Tutorial Link"
                  helperText="Add tutorial links that help users understand this wiring. Each one needs visible link text and a full URL."
                  emptyStateText="No tutorial links added yet."
                  linkTextPlaceholder="BME280 wiring walkthrough"
                  urlPlaceholder="https://example.com/tutorial"
                />
              </div>
              
              <div style={{ display: 'flex', gap: '12px' }}>
                {!usingUploadedSVG && (
                  <GenerateButton onClick={generatePreview} disabled={loading}>
                    {loading ? 'Preparing...' : 'Show Manual Preview'}
                  </GenerateButton>
                )}
                {usingUploadedSVG && (
                  <div style={{ padding: '12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', color: '#166534', fontSize: '14px' }}>
                    {isEditMode
                      ? 'Using uploaded wiring diagram image. Save your changes when you are ready.'
                      : 'Using uploaded wiring diagram image. Click "Save as Draft" or "Publish" to save.'}
                  </div>
                )}
              </div>
            </WiringStep>
          )}

          {showPreview && (generatedSVG || uploadedSVG) && (
            <WiringStep>
              <StepTitle>
                <Link size={20} />
                Wiring Preview
                {usingUploadedSVG && (
                  <span style={{ 
                    marginLeft: '12px', 
                    padding: '4px 8px', 
                    borderRadius: '4px', 
                    fontSize: '12px', 
                    fontWeight: 'bold',
                    background: '#2c9fa4',
                    color: 'white'
                  }}>
                    FROM UPLOADED IMAGE
                  </span>
                )}
                {activeGuide && (
                  <span style={{ 
                    marginLeft: '12px', 
                    padding: '4px 8px', 
                    borderRadius: '4px', 
                    fontSize: '12px', 
                    fontWeight: 'bold',
                    background: activeGuide.published ? '#10b981' : '#f59e0b',
                    color: 'white'
                  }}>
                    {activeGuide.published ? 'PUBLISHED' : 'DRAFT'}
                  </span>
                )}
              </StepTitle>
              
              {/* Pin Connections Table */}
              {connections.length > 0 && (
                <div style={{
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '20px',
                  marginBottom: '20px'
                }}>
                  <h3 style={{ 
                    color: '#1f2937', 
                    margin: '0 0 16px 0', 
                    fontSize: '1.25rem', 
                    fontWeight: '600' 
                  }}>
                    Pin Connections
                  </h3>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '14px'
                  }}>
                    <thead style={{ background: '#f9fafb' }}>
                      <tr>
                        <th style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          fontWeight: '600',
                          color: '#374151',
                          borderBottom: '1px solid #e5e7eb'
                        }}>
                          Sensor Board Pin
                        </th>
                        <th style={{
                          padding: '12px 16px',
                          textAlign: 'center',
                          fontWeight: '600',
                          color: '#374151',
                          borderBottom: '1px solid #e5e7eb'
                        }}>
                          Connection
                        </th>
                        <th style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          fontWeight: '600',
                          color: '#374151',
                          borderBottom: '1px solid #e5e7eb'
                        }}>
                          Microcontroller Pin
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {connections
                        .filter(c => c.sensorPin && c.microcontrollerPin)
                        .map((connection, index) => {
                          const sensorPin = sensorPins.find(p => String(p.id) === String(connection.sensorPin));
                          const microcontrollerPin = microcontrollerPins.find(p => String(p.id) === String(connection.microcontrollerPin));
                          return (
                            <tr key={index} style={{
                              borderBottom: '1px solid #e5e7eb'
                            }}>
                              <td style={{
                                padding: '12px 16px',
                                color: '#374151',
                                fontWeight: '500'
                              }}>
                                {sensorPin ? `${sensorPin.pin_name} (${sensorPin.pin_number})` : 'N/A'}
                              </td>
                              <td style={{
                                padding: '12px 16px',
                                textAlign: 'center',
                                color: '#6b7280'
                              }}>
                                →
                              </td>
                              <td style={{
                                padding: '12px 16px',
                                color: '#374151',
                                fontWeight: '500'
                              }}>
                                {microcontrollerPin ? `${microcontrollerPin.pin_name} (${microcontrollerPin.pin_number})` : 'N/A'}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              )}
              
              <div style={{ 
                border: '1px solid #e2e8f0', 
                borderRadius: '8px', 
                padding: '16px', 
                background: 'white',
                overflow: 'hidden',
                maxHeight: '800px',
                position: 'relative'
              }}>
                <div 
                  id="wiring-guide-svg-container"
                  style={{ 
                    width: '100%', 
                    minHeight: '400px',
                    maxHeight: '600px',
                    height: 'auto',
                    position: 'relative',
                    overflow: 'hidden',
                    cursor: isDragging ? 'grabbing' : 'grab',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                  onWheel={handleWheel}
                >
                  <div
                    style={{
                      transform: `translate(${panX}px, ${panY}px) scale(${zoomLevel})`,
                      transformOrigin: 'center center',
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                    dangerouslySetInnerHTML={{ __html: uploadedSVG || generatedSVG }} 
                  />
                </div>
                
                {/* Pan/Zoom Controls */}
                <div style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  zIndex: 10
                }}>
                  {/* Zoom Level Indicator */}
                  <div style={{
                    background: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    minWidth: '60px'
                  }}>
                    {Math.round(zoomLevel * 100)}%
                  </div>
                  <button
                    onClick={() => zoomWiringGuide(1.2)}
                    style={{
                      width: '40px',
                      height: '40px',
                      background: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                    title="Zoom In"
                  >
                    +
                  </button>
                  <button
                    onClick={() => zoomWiringGuide(0.8)}
                    style={{
                      width: '40px',
                      height: '40px',
                      background: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                    title="Zoom Out"
                  >
                    −
                  </button>
                  <button
                    onClick={() => resetWiringGuideZoom()}
                    style={{
                      width: '40px',
                      height: '40px',
                      background: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                    title="Reset Zoom"
                  >
                    ⌂
                  </button>
                </div>
              </div>
              <div style={{ marginTop: '16px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                {isEditMode ? (
                  <>
                    <button 
                      onClick={() => saveEditedWiringGuide()}
                      disabled={loading}
                      style={{
                        padding: '8px 16px',
                        background: '#2c9fa4',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        opacity: loading ? 0.6 : 1
                      }}
                    >
                      Save Changes
                    </button>
                    <button 
                      onClick={() => saveEditedWiringGuide(activeGuide?.published ? false : true)}
                      disabled={loading}
                      style={{
                        padding: '8px 16px',
                        background: activeGuide?.published ? '#f59e0b' : '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        opacity: loading ? 0.6 : 1
                      }}
                    >
                      {activeGuide?.published ? 'Save & Unpublish' : 'Save & Publish'}
                    </button>
                    {activeGuide?.published && (
                      <button 
                        onClick={() => window.open(appUrl(`/wiring-guide/${activeGuide.slug}`), '_blank')}
                        style={{
                          padding: '8px 16px',
                          background: '#2c9fa4',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                      >
                        View Public
                      </button>
                    )}
                  </>
                ) : (
                  <>
                {/* Show Save as Draft and Publish buttons when there's only a preview (no currentWiringGuide) */}
                {!currentWiringGuide && (
                  <>
                    <button 
                      onClick={saveAsDraft}
                      disabled={loading}
                      style={{
                        padding: '8px 16px',
                        background: '#6b7280',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        opacity: loading ? 0.6 : 1
                      }}
                    >
                      💾 Save as Draft
                    </button>
                    <button 
                      onClick={publishWiringGuide}
                      disabled={loading}
                      style={{
                        padding: '8px 16px',
                        background: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        opacity: loading ? 0.6 : 1
                      }}
                    >
                      📢 Publish
                    </button>
                  </>
                )}
                {/* Show existing buttons when there's a currentWiringGuide */}
                {currentWiringGuide && !currentWiringGuide.published && (
                  <button 
                    onClick={() => togglePublishStatus(true)}
                    style={{
                      padding: '8px 16px',
                      background: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    📢 Publish
                  </button>
                )}
                {currentWiringGuide && (
                  <button 
                    onClick={() => saveAsDraft()}
                    style={{
                      padding: '8px 16px',
                      background: '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    💾 Save as Draft
                  </button>
                )}
                {currentWiringGuide && currentWiringGuide.published && (
                  <button 
                    onClick={() => togglePublishStatus(false)}
                    style={{
                      padding: '8px 16px',
                      background: '#f59e0b',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    📝 Unpublish
                  </button>
                )}
                {currentWiringGuide && currentWiringGuide.published && (
                  <button 
                    onClick={() => window.open(appUrl(`/wiring-guide/${currentWiringGuide.slug}`), '_blank')}
                    style={{
                      padding: '8px 16px',
                      background: '#2c9fa4',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    👁️ View Public
                  </button>
                )}
                  </>
                )}
                {usingUploadedSVG && (
                  <button 
                    onClick={() => {
                      setUploadedSVG(null);
                      setUsingUploadedSVG(false);
                      setShowPreview(false);
                      setGeneratedSVG('');
                      setLatestManualGeneratedSVG('');
                    }}
                    style={{
                      padding: '8px 16px',
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    Remove Uploaded Diagram
                  </button>
                )}
                <button 
                  onClick={() => setShowPreview(false)}
                  style={{
                    padding: '8px 16px',
                    background: '#6b7280',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  Close Preview
                </button>
              </div>
            </WiringStep>
          )}
            </>
          )}
        </>
      ) : (
        // Management view
        <>
          {manageLoading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ fontSize: '18px', color: '#6b7280' }}>Loading wiring...</div>
            </div>
          ) : manageError ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ fontSize: '18px', color: '#dc2626' }}>{manageError}</div>
              <button 
                onClick={fetchWiringGuides}
                style={{
                  marginTop: '16px',
                  padding: '8px 16px',
                  background: '#2c9fa4',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Retry
              </button>
            </div>
          ) : wiringGuides.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '60px 20px',
              background: '#f9fafb',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <FileText size={48} style={{ color: '#9ca3af', marginBottom: '16px' }} />
              <h3 style={{ margin: '0 0 8px 0', color: '#6b7280' }}>No wiring found</h3>
              <p style={{ margin: '0', color: '#9ca3af' }}>Create your first wiring using the "Create" tab.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '16px' }}>
              {wiringGuides.map((guide) => (
                <div
                  key={guide.id}
                  style={{
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '20px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      <h3 style={{ margin: '0', color: '#1f2937', fontSize: '16px' }}>
                        {getWiringGuideSensorName(guide)} → {getWiringGuideMicrocontrollerName(guide)}
                      </h3>
                      <span style={{
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        background: '#6b7280',
                        color: 'white'
                      }}>
                        ID: {guide.id}
                      </span>
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        background: guide.published ? '#10b981' : '#f59e0b',
                        color: 'white'
                      }}>
                        {guide.published ? 'PUBLISHED' : 'DRAFT'}
                      </span>
                    </div>
                    {guide.description && (
                      <p style={{ margin: '0 0 8px 0', color: '#6b7280', fontSize: '14px' }}>
                        {guide.description}
                      </p>
                    )}
                    <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                      Created: {formatDate(guide.created_at)}
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      onClick={() => openEditPage(guide)}
                      style={{
                        padding: '6px 12px',
                        background: '#6b7280',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <Edit size={14} />
                      Edit
                    </button>
                    
                    <button
                      onClick={() => togglePublish(guide.id, guide.published)}
                      style={{
                        padding: '6px 12px',
                        background: guide.published ? '#f59e0b' : '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      {guide.published ? <EyeOff size={14} /> : <Globe size={14} />}
                      {guide.published ? 'Unpublish' : 'Publish'}
                    </button>
                    
                    {!!guide.published && (
                      <button
                        onClick={() => window.open(appUrl(`/wiring-guide/${guide.slug}`), '_blank')}
                        style={{
                          padding: '6px 12px',
                          background: '#2c9fa4',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <Eye size={14} />
                        View
                      </button>
                    )}
                    
                    <button
                      onClick={() => deleteWiringGuide(guide.id)}
                      style={{
                        padding: '6px 12px',
                        background: '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Legacy edit modal kept disabled in favor of the full-page editor */}
      {false && editingGuide && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '500px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'auto',
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h3 style={{ margin: 0, color: '#1f2937' }}>
                Edit Wiring
              </h3>
              <button
                onClick={closeEditPage}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '500',
                color: '#374151'
              }}>
                Wiring
              </label>
              <div style={{
                padding: '12px',
                background: '#f9fafb',
                borderRadius: '6px',
                color: '#6b7280',
                fontSize: '14px'
              }}>
                {getWiringGuideSensorName(editingGuide) || `Sensor Board ID: ${editingGuide.sensor_board_id}`} → {getWiringGuideMicrocontrollerName(editingGuide) || `Microcontroller Board ID: ${editingGuide.microcontroller_board_id}`}
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '500',
                color: '#374151'
              }}>
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter a description for this wiring..."
                style={{
                  width: '100%',
                  minHeight: '100px',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '500',
                color: '#374151'
              }}>
                Tutorial Links
              </label>
              <AffiliateLinksEditor
                value={tutorialLinks}
                onChange={setTutorialLinks}
                disabled={loading}
                itemNameSingular="tutorial link"
                itemNamePlural="tutorial links"
                addButtonLabel="Add Tutorial Link"
                helperText="Add one or more tutorials that can help readers understand or build this wiring setup."
                emptyStateText="No tutorial links added yet."
                linkTextPlaceholder="Official setup guide"
                urlPlaceholder="https://example.com/tutorial"
              />
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={closeEditPage}
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => saveEditedWiringGuide()}
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  background: loading ? '#9ca3af' : '#2c9fa4',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                {loading && <div style={{
                  width: '12px',
                  height: '12px',
                  border: '2px solid transparent',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />}
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </WiringGuideContainer>
  );
};


const Admin = () => {
  console.log('🔍 Admin component loaded - debugging is working!');
  const { isAuthenticated, token, login } = useAuth();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('boards');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [boards, setBoards] = useState([]);
  const [filteredBoards, setFilteredBoards] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [manufacturerFilter, setManufacturerFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [creatingPublishedBoard, setCreatingPublishedBoard] = useState(false);
  const [boardsLoading, setBoardsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBoards, setTotalBoards] = useState(0);
  const [pageSize] = useState(12); // Fixed page size for consistency
  
  // Cache state
  const [boardsCache, setBoardsCache] = useState(new Map());
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const [loadingOperations, setLoadingOperations] = useState(new Set());
  const [showModal, setShowModal] = useState(false);

  // Debug modal state changes
  useEffect(() => {
    console.log('🔍 showModal state changed:', showModal);
  }, [showModal]);
  const [fritzingData, setFritzingData] = useState(null);
  const [pendingModalOpen, setPendingModalOpen] = useState(false);
  const [uploadRotation, setUploadRotation] = useState(0);
  const [uploadPinCallouts, setUploadPinCallouts] = useState([]);
  const [uploadPins, setUploadPins] = useState([]);
  const [uploadBoardMetadataJson, setUploadBoardMetadataJson] = useState('');
  const [uploadBoardMetadataSummary, setUploadBoardMetadataSummary] = useState('');
  const [uploadPinMetadataJson, setUploadPinMetadataJson] = useState('');
  const [uploadPinMetadataSummary, setUploadPinMetadataSummary] = useState('');
  const [selectedUploadPinKey, setSelectedUploadPinKey] = useState('');
  const uploadAlternateNameDefaultsAppliedRef = useRef('');
  const [uploadBulkElectricalValues, setUploadBulkElectricalValues] = useState(EMPTY_BULK_ELECTRICAL_VALUES);
  const [applyUploadBulkElectricalValues, setApplyUploadBulkElectricalValues] = useState(false);
  const [uploadSvgSizeDraft, setUploadSvgSizeDraft] = useState({
    width: '',
    height: ''
  });
  const [uploadSvgSizeMode, setUploadSvgSizeMode] = useState(SVG_SIZE_INPUT_MODE_UNITS);
  const [uploadSvgPercentageDraft, setUploadSvgPercentageDraft] = useState('100');
  const [uploadSvgBaseBox, setUploadSvgBaseBox] = useState(EMPTY_UPLOAD_SVG_BASE_BOX);
  const [uploadSvgDetectedSize, setUploadSvgDetectedSize] = useState(EMPTY_UPLOAD_SVG_SIZE_INFO);
  const navigate = useNavigate();
  const hasUnsavedBoardDraft = Boolean(fritzingData);

  const resetCreateBoardDraft = useCallback(() => {
    setShowModal(false);
    setPendingModalOpen(false);
    setBoardForm(createEmptyBoardForm());
    setFritzingData(null);
    setUploadRotation(0);
    setUploadPinCallouts([]);
    setUploadPins([]);
    setUploadBoardMetadataJson('');
    setUploadBoardMetadataSummary('');
    setUploadPinMetadataJson('');
    setUploadPinMetadataSummary('');
    setSelectedUploadPinKey('');
    uploadAlternateNameDefaultsAppliedRef.current = '';
    setUploadBulkElectricalValues(EMPTY_BULK_ELECTRICAL_VALUES);
    setApplyUploadBulkElectricalValues(false);
    setUploadSvgBaseBox(EMPTY_UPLOAD_SVG_BASE_BOX);
    setUploadSvgDetectedSize(EMPTY_UPLOAD_SVG_SIZE_INFO);
    setUploadSvgSizeMode(SVG_SIZE_INPUT_MODE_UNITS);
    setUploadSvgPercentageDraft('100');
    setError(null);
  }, []);

  // Handle URL parameters to set active tab
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tab = urlParams.get('tab');
    const normalizedTab = tab === 'upload' || tab === 'pinouts' ? 'boards' : tab;
    const nextTab = ADMIN_SECTIONS.some((section) => section.id === normalizedTab) ? normalizedTab : 'boards';
    setActiveTab(nextTab);
    setIsSidebarOpen(false);
  }, [location.search]);

  const [loginForm, setLoginForm] = useState({
    username: '',
    password: ''
  });

  const [boardForm, setBoardForm] = useState(createEmptyBoardForm);

  const pinCalloutValidation = useMemo(
    () => validatePinCallouts(uploadPins, uploadPinCallouts),
    [uploadPins, uploadPinCallouts]
  );
  const uploadPublicPreviewSvg = useMemo(() => {
    if (!fritzingData?.displaySVG) {
      return '';
    }

    return buildPinoutPreviewSvg(fritzingData.displaySVG, uploadPins, pinCalloutValidation.callouts, uploadRotation, {
      viewBoundsMode: 'board',
      normalizeCalloutSize: false,
    });
  }, [fritzingData?.displaySVG, pinCalloutValidation.callouts, uploadPins, uploadRotation]);

  const selectedUploadPin = useMemo(
    () => uploadPins.find((pin) => getPinLayoutKey(pin) === selectedUploadPinKey) || null,
    [selectedUploadPinKey, uploadPins]
  );
  const uploadBulkEligiblePinCount = useMemo(
    () => countEligibleBulkElectricalPins(uploadPins),
    [uploadPins]
  );
  const selectedUploadPinAlternateNameEntries = useMemo(
    () => getEditablePinAlternateNameEntries(selectedUploadPin),
    [selectedUploadPin]
  );
  const uploadAlternateNamePositionSuggestionsByPinKey = useMemo(() => {
    const calloutsByKey = new Map(
      (Array.isArray(pinCalloutValidation.callouts) ? pinCalloutValidation.callouts : []).map((callout) => [
        getCalloutLayoutKey(callout),
        callout,
      ])
    );

    return Object.fromEntries(
      (Array.isArray(uploadPins) ? uploadPins : []).map((pin) => {
        const callout = calloutsByKey.get(getPinLayoutKey(pin));
        const editableEntries = getEditablePinAlternateNameEntries(pin);
        const suggestions = editableEntries.map((entry, index) => (
          getSuggestedAlternateNamePosition({
            pin,
            callout,
            index,
            alternateNameEntries: editableEntries,
          })
        ));

        return [getPinLayoutKey(pin), suggestions];
      })
    );
  }, [pinCalloutValidation.callouts, uploadPins]);

  useEffect(() => {
    setUploadBulkElectricalValues(EMPTY_BULK_ELECTRICAL_VALUES);
    setApplyUploadBulkElectricalValues(false);
  }, [fritzingData?.filename, fritzingData?.requestId]);

  const uploadSvgIntrinsicSize = useMemo(
    () => parseUploadSvgIntrinsicSize(fritzingData?.displaySVG),
    [fritzingData?.displaySVG]
  );
  const uploadSvgCurrentScalePercent = useMemo(() => {
    const baseWidth = Number(uploadSvgDetectedSize.width);
    const currentWidth = Number(uploadSvgIntrinsicSize.width);

    if (!Number.isFinite(baseWidth) || baseWidth <= 0 || !Number.isFinite(currentWidth) || currentWidth <= 0) {
      return null;
    }

    return (currentWidth / baseWidth) * 100;
  }, [uploadSvgDetectedSize.width, uploadSvgIntrinsicSize.width]);

  useEffect(() => {
    const nextUploadPins = prepareUploadedPins(fritzingData?.pins);
    setUploadPins(nextUploadPins);
    setSelectedUploadPinKey(nextUploadPins[0] ? getPinLayoutKey(nextUploadPins[0]) : '');
  }, [fritzingData]);

  useEffect(() => {
    const uploadKey = String(fritzingData?.requestId ?? fritzingData?.filename ?? '');

    if (
      !uploadKey
      || uploadAlternateNameDefaultsAppliedRef.current === uploadKey
      || !Array.isArray(uploadPins)
      || uploadPins.length === 0
    ) {
      return;
    }

    uploadAlternateNameDefaultsAppliedRef.current = uploadKey;

    setUploadPins((currentPins) => {
      let hasChanges = false;
      const nextPins = (Array.isArray(currentPins) ? currentPins : []).map((pin) => {
        const pinKey = getPinLayoutKey(pin);
        const currentEntries = getEditablePinAlternateNameEntries(pin);
        const currentSerialized = serializePinAlternateNames(currentEntries);
        const nextSerialized = serializePinAlternateNames(
          applySuggestedAlternateNameDefaults(
            currentEntries,
            uploadAlternateNamePositionSuggestionsByPinKey[pinKey] || []
          )
        );

        if (nextSerialized === currentSerialized) {
          return pin;
        }

        hasChanges = true;
        return {
          ...pin,
          alternate_pin_names: nextSerialized,
        };
      });

      return hasChanges ? nextPins : currentPins;
    });
  }, [
    fritzingData?.filename,
    fritzingData?.requestId,
    uploadAlternateNamePositionSuggestionsByPinKey,
    uploadPins,
  ]);

  useEffect(() => {
    setUploadSvgSizeDraft({
      width: uploadSvgIntrinsicSize.widthText,
      height: uploadSvgIntrinsicSize.heightText
    });
  }, [fritzingData?.requestId, uploadSvgIntrinsicSize.heightText, uploadSvgIntrinsicSize.widthText]);

  useEffect(() => {
    setUploadSvgPercentageDraft(
      uploadSvgCurrentScalePercent !== null
        ? formatSvgSizeNumber(uploadSvgCurrentScalePercent)
        : ''
    );
  }, [fritzingData?.requestId, uploadSvgCurrentScalePercent]);

  // Handle pending modal open after form is updated
  useEffect(() => {
    console.log('🔍 Modal useEffect triggered - pendingModalOpen:', pendingModalOpen, 'fritzingData:', !!fritzingData);
    if (pendingModalOpen && fritzingData) {
      console.log('🔍 Opening modal with fritzingData:', fritzingData);
      setShowModal(true);
      setPendingModalOpen(false);
    }
  }, [pendingModalOpen, fritzingData]);


  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const result = await login(loginForm.username, loginForm.password);
      
      if (result.success) {
        fetchBoards();
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (error) {
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchBoards = useCallback(async () => {
    // Check cache first (cache for 60 seconds)
    const now = Date.now();
    if (boardsCache.has('all-boards') && (now - lastFetchTime) < 60000) {
      const cachedData = boardsCache.get('all-boards');
      setBoards(cachedData);
      setTotalBoards(cachedData.length);
      setBoardsLoading(false);
      return;
    }

    setBoardsLoading(true);
    setError(null);
    
    try {
      // Load all boards at once for fast client-side filtering
      const { adminBoardsAPI } = await import('../services/adminApi');
      const response = await adminBoardsAPI.getAll();

      const data = response.data;
      
      // Handle both array and paginated response formats
      const rawBoardsData = Array.isArray(data) ? data : (data.boards || data.pinouts || []);
      const boardsData = normalizeAdminBoardCollection(rawBoardsData);
      
      setBoards(boardsData);
      setTotalBoards(boardsData.length);
      
      // Cache the result
      setBoardsCache(prev => new Map(prev).set('all-boards', boardsData));
      setLastFetchTime(now);
      setError(null);
    } catch (error) {
      console.error('Failed to fetch boards:', error);
      setError('Failed to load boards. Please check your connection.');
    } finally {
      setBoardsLoading(false);
    }
  }, [boardsCache, lastFetchTime]);

  useEffect(() => {
    if (token && isAuthenticated) {
      fetchBoards();
    }
  }, [token, isAuthenticated, fetchBoards]);

  // Initial load
  useEffect(() => {
    if (isAuthenticated) {
      fetchBoards();
    }
  }, [isAuthenticated, fetchBoards]);

  // Refetch boards when switching to boards tab
  useEffect(() => {
    if ((activeTab === 'boards' || activeTab === 'project-builder') && isAuthenticated) {
      fetchBoards();
    }
  }, [activeTab, isAuthenticated, fetchBoards]);

  const currentSection = useMemo(
    () => ADMIN_SECTIONS.find((section) => section.id === activeTab) || ADMIN_SECTIONS[0],
    [activeTab]
  );

  const handleSectionSelect = (sectionId) => {
    setActiveTab(sectionId);
    setIsSidebarOpen(false);

    if (sectionId === 'boards' || sectionId === 'project-builder') {
      setBoardsCache(new Map());
      if (isAuthenticated && activeTab === sectionId) {
        fetchBoards();
      }
    }

    const nextSearch = sectionId === 'boards' ? '' : `?tab=${sectionId}`;
    if (location.search !== nextSearch) {
      navigate({
        pathname: location.pathname,
        search: nextSearch,
      });
    }
  };

  // Client-side filtering with pagination
  useEffect(() => {
    let filtered = boards;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(board =>
        board.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        board.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        board.manufacturer?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(board => {
        switch (statusFilter) {
          case 'published':
            return board.published === 1;
          case 'draft':
            return board.published === 0;
          default:
            return true;
        }
      });
    }

    // Apply manufacturer filter
    if (manufacturerFilter !== 'all') {
      filtered = filtered.filter(board =>
        board.manufacturer?.toLowerCase().includes(manufacturerFilter.toLowerCase())
      );
    }

    // Calculate pagination
    const totalFiltered = filtered.length;
    const totalPages = Math.ceil(totalFiltered / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedBoards = filtered.slice(startIndex, endIndex);

    setFilteredBoards(paginatedBoards);
    setTotalPages(totalPages);
    setTotalBoards(totalFiltered);
  }, [boards, searchTerm, statusFilter, manufacturerFilter, currentPage, pageSize]);

  // Reset to page 1 when search or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, manufacturerFilter]);

  // Get unique manufacturers for filter dropdown
  const manufacturers = [...new Set(boards.map(board => board.manufacturer).filter(Boolean))];
  const popularBoardsCount = useMemo(
    () => boards.reduce((count, board) => count + (Number(board.popular_on_home) ? 1 : 0), 0),
    [boards]
  );

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setManufacturerFilter('all');
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFileUpload = async (file) => {
    console.log('🔍 handleFileUpload called with file:', file.name);
    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('fritzingFile', file);

      console.log('🔍 Sending request to upload API...');
      const { adminUploadAPI } = await import('../services/adminApi');
      const response = await adminUploadAPI.uploadFritzing(formData);

      console.log('🔍 API response received:', response);
      const data = response.data;
      console.log('🔍 Response data structure:', data);
      console.log('🔍 Looking for boardMetadata in:', data.data);

      if (response.status === 200) {
        const parsedFritzingData = extractParsedFritzingData(data.data);
        const detectedUploadSvgSize = parseUploadSvgIntrinsicSize(parsedFritzingData.displaySVG);
        const detectedSvgBaseBox = parseUploadSvgGeometryBox(parsedFritzingData.displaySVG);
        setFritzingData(parsedFritzingData);
        setUploadRotation(0);
        setUploadPinCallouts([]);
        setUploadSvgBaseBox(detectedSvgBaseBox);
        setUploadSvgDetectedSize(detectedUploadSvgSize);
        setUploadSvgSizeMode(SVG_SIZE_INPUT_MODE_UNITS);
        setUploadSvgPercentageDraft('100');
        setUploadBoardMetadataJson('');
        setUploadBoardMetadataSummary('');
        setUploadPinMetadataJson('');
        setUploadPinMetadataSummary('');
        
        // Pre-fill form with extracted board metadata
        if (parsedFritzingData.boardMetadata) {
          const metadata = parsedFritzingData.boardMetadata;
          console.log('🔍 Board metadata received:', metadata);
          
          // Clean up HTML from description
          const cleanDescription = metadata.description 
            ? metadata.description.replace(/<[^>]*>/g, '').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&').trim()
            : '';
          
          const newFormData = {
            ...createEmptyBoardForm(),
            name: metadata.title || '',
            description: cleanDescription,
            manufacturer: metadata.manufacturer || '',
            package_type: metadata.package_type || '',
            cpu: metadata.cpu || '',
            clock_speed: metadata.clock_speed || '',
            flash_memory: metadata.flash_memory || '',
            ram: metadata.ram || '',
            rom: metadata.rom || ''
          };
          newFormData.meta_description = deriveBoardSeoDescription(newFormData);
          newFormData.seo_keywords = deriveBoardSeoKeywords(newFormData);
          
          console.log('🔍 Setting form data:', newFormData);
          setBoardForm(newFormData);
          
          // Verify the form was set immediately
          console.log('🔍 Form data being set to:', newFormData);
        } else {
          console.log('❌ No boardMetadata found in response');
        }
        
        setSuccess('Fritzing file parsed successfully!');
        console.log('🔍 Setting pendingModalOpen to true');
        setPendingModalOpen(true);
      } else {
        setError(data.error || 'Failed to parse Fritzing file');
      }
    } catch (error) {
      console.error('🔍 Upload error:', error);
      console.error('🔍 Error response:', error.response);
      setError('Failed to upload file. Please try again.');
    } finally {
      setLoading(false);
    }
  };


  const handleDrop = (e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleUploadRotationChange = (delta) => {
    if (!fritzingData?.displaySVG) {
      return;
    }

    setUploadRotation((currentRotation) => {
      const nextRotation = normalizeRotation(currentRotation + delta);
      setUploadPinCallouts((currentCallouts) => rotateCallouts(
        currentCallouts,
        fritzingData.displaySVG,
        currentRotation,
        nextRotation
      ));
      return nextRotation;
    });
  };

  const handleBoardProtocolToggle = useCallback((protocolValue) => {
    setBoardForm((currentForm) => ({
      ...currentForm,
      supported_protocols: toggleSupportedProtocolValue(currentForm.supported_protocols, protocolValue)
    }));
  }, []);

  const handleRegenerateBoardMetaDescription = useCallback(() => {
    setBoardForm((currentForm) => ({
      ...currentForm,
      meta_description: deriveBoardSeoDescription({
        ...currentForm,
        meta_description: '',
      }),
    }));
  }, []);

  const copyTextToClipboard = async (text) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.setAttribute('readonly', '');
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    textArea.style.pointerEvents = 'none';
    document.body.appendChild(textArea);
    textArea.select();

    try {
      const didCopy = document.execCommand('copy');
      if (!didCopy) {
        throw new Error('Legacy clipboard copy failed.');
      }
    } finally {
      textArea.remove();
    }
  };

  const buildUploadPinMetadataExportPayload = useCallback(() => (
    (Array.isArray(uploadPins) ? uploadPins : []).map((pin) => ({
      id: pin.id,
      pin_number: String(pin?.pin_number ?? pin?.pinNumber ?? ''),
      pin_name: String(pin?.pin_name ?? pin?.pinName ?? ''),
      pin_role: String(pin?.pin_role ?? pin?.pinRole ?? ''),
      functions: String(pin?.functions ?? ''),
      capability_tags: normalizeStringArray(pin?.capability_tags ?? pin?.capabilityTags),
      is_5v_tolerant: Boolean(pin?.is_5v_tolerant),
      use_with_caution: Boolean(pin?.use_with_caution),
      caution_reason: String(pin?.caution_reason ?? ''),
      alternate_pin_names: buildAdminExportableAlternateNameEntries(pin),
      alternate_functions: String(pin?.alternate_functions ?? ''),
      description: String(pin?.description ?? ''),
      voltage_min_v: normalizeComparableNumber(pin?.voltage_min_v),
      voltage_max_v: normalizeComparableNumber(pin?.voltage_max_v),
      current_limit_ma: normalizeComparableNumber(pin?.current_limit_ma),
      position_x: normalizeComparableNumber(pin?.position_x),
      position_y: normalizeComparableNumber(pin?.position_y),
      svg_id: String(pin?.svg_id ?? pin?.svgId ?? ''),
    }))
  ), [uploadPins]);

  const loadCurrentUploadBoardMetadataJson = () => {
    setUploadBoardMetadataJson(JSON.stringify(buildAdminBoardMetadataExportPayload(boardForm), null, 2));
    setUploadBoardMetadataSummary('Loaded current board draft as JSON.');
  };

  const handleCopyCurrentUploadBoardMetadataJson = async () => {
    try {
      await copyTextToClipboard(JSON.stringify(buildAdminBoardMetadataExportPayload(boardForm), null, 2));
      setUploadBoardMetadataSummary('Copied current board draft JSON to the clipboard.');
    } catch (copyError) {
      setError(copyError?.message || 'Failed to copy current board draft JSON.');
    }
  };

  const handleApplyUploadBoardMetadataJson = () => {
    setUploadBoardMetadataSummary('');

    const trimmedJson = String(uploadBoardMetadataJson || '').trim();
    if (!trimmedJson) {
      setError('Paste a JSON object of board metadata updates before applying.');
      return;
    }

    let parsedInput;

    try {
      parsedInput = JSON.parse(trimmedJson);
    } catch (parseError) {
      setError(`Board details JSON could not be parsed: ${parseError.message}`);
      return;
    }

    const importEntry = normalizeBoardMetadataImportEntry(parsedInput);
    if (!importEntry) {
      setError('Board details JSON must be an object, or an object with a nested "board" object.');
      return;
    }

    try {
      setBoardForm((currentForm) => {
        const nextForm = {
          ...currentForm,
        };

        if (hasImportField(importEntry, ['manufacturer'])) {
          nextForm.manufacturer = normalizeImportString(readImportField(importEntry, ['manufacturer'])) || '';
        }

        if (hasImportField(importEntry, ['package_type', 'packageType', 'package', 'form_factor', 'formFactor'])) {
          nextForm.package_type = normalizeImportString(readImportField(importEntry, ['package_type', 'packageType', 'package', 'form_factor', 'formFactor'])) || '';
        }

        if (hasImportField(importEntry, ['cpu'])) {
          nextForm.cpu = normalizeImportString(readImportField(importEntry, ['cpu'])) || '';
        }

        if (hasImportField(importEntry, ['supply_voltage_min_v', 'supplyVoltageMinV'])) {
          nextForm.supply_voltage_min_v = normalizeImportNumber(
            readImportField(importEntry, ['supply_voltage_min_v', 'supplyVoltageMinV']),
            'supply_voltage_min_v'
          );
        }

        if (hasImportField(importEntry, ['supply_voltage_max_v', 'supplyVoltageMaxV'])) {
          nextForm.supply_voltage_max_v = normalizeImportNumber(
            readImportField(importEntry, ['supply_voltage_max_v', 'supplyVoltageMaxV']),
            'supply_voltage_max_v'
          );
        }

        if (hasImportField(importEntry, ['logic_voltage_min_v', 'logicVoltageMinV'])) {
          nextForm.logic_voltage_min_v = normalizeImportNumber(
            readImportField(importEntry, ['logic_voltage_min_v', 'logicVoltageMinV']),
            'logic_voltage_min_v'
          );
        }

        if (hasImportField(importEntry, ['logic_voltage_max_v', 'logicVoltageMaxV'])) {
          nextForm.logic_voltage_max_v = normalizeImportNumber(
            readImportField(importEntry, ['logic_voltage_max_v', 'logicVoltageMaxV']),
            'logic_voltage_max_v'
          );
        }

        if (hasImportField(importEntry, ['current_draw_typ_ma', 'currentDrawTypMa', 'typical_current_draw_ma', 'typicalCurrentDrawMa'])) {
          nextForm.current_draw_typ_ma = normalizeImportNumber(
            readImportField(importEntry, ['current_draw_typ_ma', 'currentDrawTypMa', 'typical_current_draw_ma', 'typicalCurrentDrawMa']),
            'current_draw_typ_ma'
          );
        }

        if (hasImportField(importEntry, ['current_draw_max_ma', 'currentDrawMaxMa', 'max_current_draw_ma', 'maxCurrentDrawMa'])) {
          nextForm.current_draw_max_ma = normalizeImportNumber(
            readImportField(importEntry, ['current_draw_max_ma', 'currentDrawMaxMa', 'max_current_draw_ma', 'maxCurrentDrawMa']),
            'current_draw_max_ma'
          );
        }

        if (hasImportField(importEntry, ['rail_3v3_max_ma', 'rail3v3MaxMa', 'rail_3v3_limit_ma', 'rail3v3LimitMa'])) {
          nextForm.rail_3v3_max_ma = normalizeImportNumber(
            readImportField(importEntry, ['rail_3v3_max_ma', 'rail3v3MaxMa', 'rail_3v3_limit_ma', 'rail3v3LimitMa']),
            'rail_3v3_max_ma'
          );
        }

        if (hasImportField(importEntry, ['rail_5v_max_ma', 'rail5vMaxMa', 'rail_5v_limit_ma', 'rail5vLimitMa'])) {
          nextForm.rail_5v_max_ma = normalizeImportNumber(
            readImportField(importEntry, ['rail_5v_max_ma', 'rail5vMaxMa', 'rail_5v_limit_ma', 'rail5vLimitMa']),
            'rail_5v_max_ma'
          );
        }

        if (hasImportField(importEntry, ['clock_speed', 'clockSpeed'])) {
          nextForm.clock_speed = normalizeImportString(readImportField(importEntry, ['clock_speed', 'clockSpeed'])) || '';
        }

        if (hasImportField(importEntry, ['flash_memory', 'flashMemory', 'flash'])) {
          nextForm.flash_memory = normalizeImportString(readImportField(importEntry, ['flash_memory', 'flashMemory', 'flash'])) || '';
        }

        if (hasImportField(importEntry, ['ram'])) {
          nextForm.ram = normalizeImportString(readImportField(importEntry, ['ram'])) || '';
        }

        if (hasImportField(importEntry, ['rom'])) {
          nextForm.rom = normalizeImportString(readImportField(importEntry, ['rom'])) || '';
        }

        if (hasImportField(importEntry, ['supported_protocols', 'supportedProtocols'])) {
          nextForm.supported_protocols = normalizeSupportedProtocols(
            readImportField(importEntry, ['supported_protocols', 'supportedProtocols'])
          );
        }

        if (hasImportField(importEntry, ['documentation_url', 'documentationUrl', 'link', 'url'])) {
          nextForm.link = normalizeImportString(
            readImportField(importEntry, ['documentation_url', 'documentationUrl', 'link', 'url'])
          ) || '';
        }

        if (hasImportField(importEntry, ['datasheet_url', 'datasheetUrl'])) {
          nextForm.datasheet_url = normalizeImportString(
            readImportField(importEntry, ['datasheet_url', 'datasheetUrl'])
          ) || '';
        }

        if (hasImportField(importEntry, ['verification_status', 'verificationStatus'])) {
          nextForm.verification_status = normalizeBoardVerificationStatus(
            readImportField(importEntry, ['verification_status', 'verificationStatus'])
          );
        }

        if (hasImportField(importEntry, ['description'])) {
          nextForm.description = normalizeImportString(readImportField(importEntry, ['description'])) || '';
        }

        return nextForm;
      });

      setError(null);
      setUploadBoardMetadataSummary('Applied JSON metadata to the current board draft.');
    } catch (importError) {
      setError(importError?.message || 'Board details JSON could not be applied.');
    }
  };

  const loadCurrentUploadPinMetadataJson = () => {
    setUploadPinMetadataJson(JSON.stringify(buildUploadPinMetadataExportPayload(), null, 2));
    setUploadPinMetadataSummary('Loaded current pin draft metadata as JSON.');
  };

  const handleCopyCurrentUploadPinMetadataJson = async () => {
    try {
      await copyTextToClipboard(JSON.stringify(buildUploadPinMetadataExportPayload(), null, 2));
      setUploadPinMetadataSummary('Copied current pin draft metadata JSON to the clipboard.');
    } catch (copyError) {
      setError(copyError?.message || 'Failed to copy current pin draft metadata JSON.');
    }
  };

  const handleApplyUploadPinMetadataJson = () => {
    if (!uploadPins.length) {
      setError('No uploaded pins are available to update yet.');
      return;
    }

    setUploadPinMetadataSummary('');

    const trimmedJson = String(uploadPinMetadataJson || '').trim();
    if (!trimmedJson) {
      setError('Paste a JSON array of pin metadata updates before applying.');
      return;
    }

    let parsedInput;

    try {
      parsedInput = JSON.parse(trimmedJson);
    } catch (parseError) {
      setError(`Pin metadata JSON could not be parsed: ${parseError.message}`);
      return;
    }

    const importEntries = normalizePinMetadataImportEntries(parsedInput);
    if (!importEntries) {
      setError('Pin metadata JSON must be an array, or an object with a nested "pins" array.');
      return;
    }

    const resolveUploadPinFromEntry = (entry, entryIndex) => {
      const candidateId = String(readImportField(entry, ['id']) ?? '').trim();
      if (candidateId) {
        const matchedById = uploadPins.find((pin) => String(pin?.id ?? '').trim() === candidateId);
        if (matchedById) {
          return matchedById;
        }
      }

      const candidateSvgId = String(readImportField(entry, ['svg_id', 'svgId']) ?? '').trim();
      if (candidateSvgId) {
        const matchedBySvgId = uploadPins.find((pin) => String(pin?.svg_id ?? pin?.svgId ?? '').trim() === candidateSvgId);
        if (matchedBySvgId) {
          return matchedBySvgId;
        }
      }

      const candidatePinNumber = String(readImportField(entry, ['pin_number', 'pinNumber']) ?? '').trim();
      if (candidatePinNumber) {
        const matchedByPinNumber = uploadPins.find((pin) => String(pin?.pin_number ?? pin?.pinNumber ?? '').trim() === candidatePinNumber);
        if (matchedByPinNumber) {
          return matchedByPinNumber;
        }
      }

      const candidatePinName = String(readImportField(entry, ['pin_name', 'pinName']) ?? '').trim();
      if (candidatePinName) {
        const matchedByPinName = uploadPins.find((pin) => String(pin?.pin_name ?? pin?.pinName ?? '').trim() === candidatePinName);
        if (matchedByPinName) {
          return matchedByPinName;
        }
      }

      throw new Error(`Entry ${entryIndex + 1} could not be matched to an uploaded pin. Include id, svg_id, pin_number, or pin_name.`);
    };

    try {
      const nextPinsByKey = new Map((uploadPins || []).map((pin) => [getPinLayoutKey(pin), pin]));
      const touchedPinKeys = new Set();
      let nextSelectedUploadPinKey = selectedUploadPinKey;

      importEntries.forEach((entry, entryIndex) => {
        if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
          throw new Error(`Entry ${entryIndex + 1} must be an object.`);
        }

        const currentPin = resolveUploadPinFromEntry(entry, entryIndex);
        const currentPinKey = getPinLayoutKey(currentPin);

        if (touchedPinKeys.has(currentPinKey)) {
          throw new Error(`Pin "${currentPin.pin_name || currentPin.pin_number || currentPin.id}" appears more than once in the import JSON.`);
        }

        touchedPinKeys.add(currentPinKey);

        let nextPin = { ...currentPin };

        if (hasImportField(entry, ['pin_name', 'pinName'])) {
          nextPin = updatePinWithFunctionAutofill(
            nextPin,
            'pin_name',
            normalizeImportString(readImportField(entry, ['pin_name', 'pinName'])) || ''
          );
        }

        if (hasImportField(entry, ['functions', 'primary_function', 'primaryFunction'])) {
          nextPin = updatePinWithFunctionAutofill(
            nextPin,
            'functions',
            normalizeImportString(readImportField(entry, ['functions', 'primary_function', 'primaryFunction'])) || ''
          );
        }

        if (hasImportField(entry, ['pin_role', 'pinRole'])) {
          nextPin = updatePinWithFunctionAutofill(
            nextPin,
            'pin_role',
            normalizeImportString(readImportField(entry, ['pin_role', 'pinRole'])) || ''
          );
        }

        if (hasImportField(entry, ['capability_tags', 'capabilityTags'])) {
          nextPin = updatePinWithFunctionAutofill(
            nextPin,
            'capability_tags',
            normalizeStringArray(readImportField(entry, ['capability_tags', 'capabilityTags']))
          );
        }

        if (hasImportField(entry, ['alternate_functions', 'alternateFunctions', 'alternate_function', 'alternateFunction'])) {
          nextPin = updatePinWithFunctionAutofill(
            nextPin,
            'alternate_functions',
            normalizeImportString(readImportField(entry, ['alternate_functions', 'alternateFunctions', 'alternate_function', 'alternateFunction'])) || ''
          );
        }

        const nextUseWithCaution = hasImportField(entry, ['use_with_caution', 'useWithCaution'])
          ? normalizeBooleanFlag(readImportField(entry, ['use_with_caution', 'useWithCaution']))
          : Boolean(nextPin.use_with_caution);
        const importedAlternateNameEntries = hasImportField(entry, ['alternate_pin_names', 'alternatePinNames'])
          ? applySuggestedAlternateNameDefaults(
              readImportField(entry, ['alternate_pin_names', 'alternatePinNames']),
              uploadAlternateNamePositionSuggestionsByPinKey[currentPinKey] || []
            )
          : null;

        nextPin = {
          ...nextPin,
          alternate_pin_names: hasImportField(entry, ['alternate_pin_names', 'alternatePinNames'])
            ? serializePinAlternateNames(importedAlternateNameEntries)
            : nextPin.alternate_pin_names,
          alternate_functions: nextPin.alternate_functions,
          description: hasImportField(entry, ['description'])
            ? normalizeImportString(readImportField(entry, ['description'])) || ''
            : nextPin.description,
          pin_role: nextPin.pin_role,
          current_limit: hasImportField(entry, ['current_limit', 'currentLimit'])
            ? normalizeImportNumber(readImportField(entry, ['current_limit', 'currentLimit']), `current_limit for entry ${entryIndex + 1}`)
            : nextPin.current_limit,
          voltage_min_v: hasImportField(entry, PIN_VOLTAGE_MIN_IMPORT_KEYS)
            ? normalizeImportNumber(readImportField(entry, PIN_VOLTAGE_MIN_IMPORT_KEYS), `voltage_min_v for entry ${entryIndex + 1}`)
            : nextPin.voltage_min_v,
          voltage_max_v: hasImportField(entry, PIN_VOLTAGE_MAX_IMPORT_KEYS)
            ? normalizeImportNumber(readImportField(entry, PIN_VOLTAGE_MAX_IMPORT_KEYS), `voltage_max_v for entry ${entryIndex + 1}`)
            : nextPin.voltage_max_v,
          current_limit_ma: hasImportField(entry, PIN_CURRENT_LIMIT_IMPORT_KEYS)
            ? normalizeImportNumber(readImportField(entry, PIN_CURRENT_LIMIT_IMPORT_KEYS), `current_limit_ma for entry ${entryIndex + 1}`)
            : nextPin.current_limit_ma,
          capability_tags: nextPin.capability_tags,
          is_5v_tolerant: hasImportField(entry, ['is_5v_tolerant', 'is5vTolerant'])
            ? normalizeBooleanFlag(readImportField(entry, ['is_5v_tolerant', 'is5vTolerant']))
            : Boolean(nextPin.is_5v_tolerant),
          use_with_caution: nextUseWithCaution,
          caution_reason: nextUseWithCaution
            ? (
                hasImportField(entry, ['caution_reason', 'cautionReason'])
                  ? normalizeImportString(readImportField(entry, ['caution_reason', 'cautionReason'])) || ''
                  : nextPin.caution_reason
              )
            : '',
          position_x: hasImportField(entry, ['position_x', 'positionX'])
            ? normalizeImportNumber(readImportField(entry, ['position_x', 'positionX']), `position_x for entry ${entryIndex + 1}`)
            : nextPin.position_x,
          position_y: hasImportField(entry, ['position_y', 'positionY'])
            ? normalizeImportNumber(readImportField(entry, ['position_y', 'positionY']), `position_y for entry ${entryIndex + 1}`)
            : nextPin.position_y,
          svg_id: hasImportField(entry, ['svg_id', 'svgId'])
            ? normalizeImportString(readImportField(entry, ['svg_id', 'svgId'])) || ''
            : (nextPin.svg_id ?? nextPin.svgId ?? ''),
        };

        nextPinsByKey.set(currentPinKey, nextPin);

        if (nextSelectedUploadPinKey === currentPinKey) {
          nextSelectedUploadPinKey = getPinLayoutKey(nextPin);
        }
      });

      setUploadPins((currentPins) => currentPins.map((pin) => nextPinsByKey.get(getPinLayoutKey(pin)) || pin));
      setSelectedUploadPinKey(nextSelectedUploadPinKey);
      setError(null);
      setUploadPinMetadataSummary(`Applied JSON metadata to ${importEntries.length} pin${importEntries.length === 1 ? '' : 's'}.`);
    } catch (importError) {
      setError(importError?.message || 'Pin metadata JSON could not be applied.');
    }
  };

  const handleUploadPinFieldChange = useCallback((pinKey, field, value) => {
    setUploadPins((currentPins) => currentPins.map((pin) => (
      getPinLayoutKey(pin) === pinKey
        ? updatePinWithFunctionAutofill(pin, field, value)
        : pin
    )));
  }, []);

  const applyUploadBulkElectricalDefaults = useCallback((nextValues) => {
    if (!hasCompleteBulkElectricalValues(nextValues)) {
      return;
    }

    setUploadPins((currentPins) => applyBulkElectricalValuesToPins(currentPins, nextValues));
  }, []);

  const handleUploadBulkElectricalValueChange = useCallback((field, value) => {
    const nextValues = {
      ...uploadBulkElectricalValues,
      [field]: value,
    };

    setUploadBulkElectricalValues(nextValues);

    if (applyUploadBulkElectricalValues) {
      applyUploadBulkElectricalDefaults(nextValues);
    }
  }, [applyUploadBulkElectricalValues, applyUploadBulkElectricalDefaults, uploadBulkElectricalValues]);

  const handleApplyUploadBulkElectricalValuesChange = useCallback((checked) => {
    setApplyUploadBulkElectricalValues(checked);

    if (checked) {
      applyUploadBulkElectricalDefaults(uploadBulkElectricalValues);
    }
  }, [applyUploadBulkElectricalDefaults, uploadBulkElectricalValues]);

  const handleUploadPinAlternateNameChange = useCallback((pinKey, index, field, value) => {
    setUploadPins((currentPins) => currentPins.map((pin) => {
      if (getPinLayoutKey(pin) !== pinKey) {
        return pin;
      }

      const nextAlternateNames = getEditablePinAlternateNameEntries(pin);
      nextAlternateNames[index] = {
        ...nextAlternateNames[index],
        [field]: value,
      };
      const alternateNamesWithDefaults = field === 'name'
        ? applySuggestedAlternateNameDefaults(
            nextAlternateNames,
            uploadAlternateNamePositionSuggestionsByPinKey[pinKey] || []
          )
        : nextAlternateNames;

      return {
        ...pin,
        alternate_pin_names: serializePinAlternateNames(alternateNamesWithDefaults)
      };
    }));
  }, [uploadAlternateNamePositionSuggestionsByPinKey]);

  const applyUploadSvgIntrinsicSize = useCallback((nextWidth, nextHeight) => {
    const baseWidth = Number(uploadSvgDetectedSize.width);
    const baseHeight = Number(uploadSvgDetectedSize.height);
    const geometryBaseWidth = Number(uploadSvgBaseBox.width);
    const geometryBaseHeight = Number(uploadSvgBaseBox.height);
    const currentWidth = Number(uploadSvgIntrinsicSize.width);
    const currentHeight = Number(uploadSvgIntrinsicSize.height);
    const currentSvgMarkup = String(fritzingData?.displaySVG || '').trim();

    if (
      !currentSvgMarkup
      || !fritzingData?.displaySVG
      || !Number.isFinite(baseWidth)
      || baseWidth <= 0
      || !Number.isFinite(baseHeight)
      || baseHeight <= 0
      || !Number.isFinite(geometryBaseWidth)
      || geometryBaseWidth <= 0
      || !Number.isFinite(geometryBaseHeight)
      || geometryBaseHeight <= 0
      || !Number.isFinite(nextWidth)
      || nextWidth <= 0
      || !Number.isFinite(nextHeight)
      || nextHeight <= 0
    ) {
      return;
    }

    const nextScaleRatio = nextWidth / baseWidth;
    const currentScaleRatio = Number.isFinite(currentWidth) && currentWidth > 0
      ? currentWidth / baseWidth
      : 1;
    const incrementalRatio = nextScaleRatio / currentScaleRatio;
    const nextSvgMarkup = updateUploadSvgIntrinsicSize(currentSvgMarkup, nextWidth, nextHeight);

    if (!nextSvgMarkup || nextSvgMarkup === currentSvgMarkup) {
      return;
    }

    const baseCenter = {
      x: Number(uploadSvgBaseBox.minX) + (geometryBaseWidth / 2),
      y: Number(uploadSvgBaseBox.minY) + (geometryBaseHeight / 2),
    };
    setFritzingData((currentData) => (
      currentData
        ? {
            ...currentData,
            displaySVG: nextSvgMarkup,
          }
        : currentData
    ));

    if (Math.abs(incrementalRatio - 1) < 0.000001 && currentWidth === nextWidth && currentHeight === nextHeight) {
      return;
    }

    setUploadPins((currentPins) => scaleUploadedPinsGeometry(currentPins, incrementalRatio, baseCenter));
  }, [
    fritzingData?.displaySVG,
    uploadSvgBaseBox.height,
    uploadSvgBaseBox.minX,
    uploadSvgBaseBox.minY,
    uploadSvgBaseBox.width,
    uploadSvgDetectedSize.height,
    uploadSvgDetectedSize.width,
    uploadSvgIntrinsicSize.height,
    uploadSvgIntrinsicSize.width,
  ]);

  const handleUploadSvgSizeFieldChange = useCallback((field, value) => {
    setUploadSvgSizeDraft((currentDraft) => {
      const baseWidth = Number(uploadSvgBaseBox.width);
      const baseHeight = Number(uploadSvgBaseBox.height);
      const aspectRatio = Number.isFinite(baseWidth) && baseWidth > 0 && Number.isFinite(baseHeight) && baseHeight > 0
        ? baseWidth / baseHeight
        : null;
      const nextDraft = {
        ...currentDraft,
        [field]: value
      };
      let nextWidth = Number(nextDraft.width);
      let nextHeight = Number(nextDraft.height);

      if (aspectRatio) {
        if (field === 'width' && Number.isFinite(nextWidth) && nextWidth > 0) {
          nextHeight = nextWidth / aspectRatio;
          nextDraft.height = formatSvgSizeNumber(nextHeight);
        } else if (field === 'height' && Number.isFinite(nextHeight) && nextHeight > 0) {
          nextWidth = nextHeight * aspectRatio;
          nextDraft.width = formatSvgSizeNumber(nextWidth);
        }
      }

      if (Number.isFinite(nextWidth) && nextWidth > 0 && Number.isFinite(nextHeight) && nextHeight > 0) {
        applyUploadSvgIntrinsicSize(nextWidth, nextHeight);
      }

      return nextDraft;
    });
  }, [applyUploadSvgIntrinsicSize, uploadSvgBaseBox.height, uploadSvgBaseBox.width]);

  const handleUploadSvgSizeFieldBlur = useCallback(() => {
    setUploadSvgSizeDraft({
      width: uploadSvgIntrinsicSize.widthText,
      height: uploadSvgIntrinsicSize.heightText
    });
  }, [uploadSvgIntrinsicSize.heightText, uploadSvgIntrinsicSize.widthText]);

  const handleUploadSvgPercentageChange = useCallback((value) => {
    setUploadSvgPercentageDraft(value);

    const nextPercentage = Number(value);
    const baseWidth = Number(uploadSvgDetectedSize.width);
    const baseHeight = Number(uploadSvgDetectedSize.height);

    if (
      !Number.isFinite(nextPercentage)
      || nextPercentage <= 0
      || !Number.isFinite(baseWidth)
      || baseWidth <= 0
      || !Number.isFinite(baseHeight)
      || baseHeight <= 0
    ) {
      return;
    }

    const ratio = nextPercentage / 100;
    applyUploadSvgIntrinsicSize(baseWidth * ratio, baseHeight * ratio);
  }, [applyUploadSvgIntrinsicSize, uploadSvgDetectedSize.height, uploadSvgDetectedSize.width]);

  const handleUploadSvgPercentageBlur = useCallback(() => {
    setUploadSvgPercentageDraft(
      uploadSvgCurrentScalePercent !== null
        ? formatSvgSizeNumber(uploadSvgCurrentScalePercent)
        : ''
    );
  }, [uploadSvgCurrentScalePercent]);

  const handleNormalizeUploadSvgCanvasSize = useCallback(() => {
    setFritzingData((currentData) => {
      if (!currentData?.displaySVG) {
        return currentData;
      }

      const normalizedSvgMarkup = normalizeUploadSvgCanvasSize(currentData.displaySVG);
      if (!normalizedSvgMarkup || normalizedSvgMarkup === currentData.displaySVG) {
        return currentData;
      }

      return {
        ...currentData,
        displaySVG: normalizedSvgMarkup,
      };
    });
  }, []);

  const createBoardFromDraft = async (publishAfterCreate = false) => {
    if (loading) {
      return false;
    }

    setLoading(true);
    setCreatingPublishedBoard(Boolean(publishAfterCreate));
    setError(null);

    let createdBoard = false;

      try {
        const affiliateLinksValidationError = validateAffiliateLinks(boardForm.affiliate_links || []);
        const tutorialUrlsValidationError = validateTutorialUrls(boardForm.tutorial_urls || []);

        if (affiliateLinksValidationError) {
          setError(affiliateLinksValidationError);
          return false;
        }

        if (tutorialUrlsValidationError) {
          setError(tutorialUrlsValidationError);
          return false;
        }

        if (pinCalloutValidation.errors.length > 0) {
          setError(pinCalloutValidation.errors[0]);
        return false;
      }

      const { adminBoardsAPI } = await import('../services/adminApi');
      const publicPinoutPngUpload = await createBoardPublicPinoutImageUpload({
        svgMarkup: uploadPublicPreviewSvg,
        pins: uploadPins,
        fileName: `${boardForm?.slug || boardForm?.name || 'board'}-pinout.jpg`,
      });
      const uploadPayload = fritzingData
        ? {
            ...fritzingData,
            pins: uploadPins
          }
        : null;

        const response = await adminBoardsAPI.createFromFritzing({
          ...boardForm,
          affiliate_links: normalizeAffiliateLinks(boardForm.affiliate_links),
          tutorial_urls: normalizeTutorialUrls(boardForm.tutorial_urls),
          svg_rotation_deg: uploadRotation,
          pin_callouts: pinCalloutValidation.callouts,
        }, uploadPayload);

      const data = response.data;

      if (response.status !== 200) {
        setError(data.error || 'Failed to create board');
        return false;
      }

      createdBoard = true;
      const createdBoardId = data?.data?.boardId || data?.data?.id || data?.data?.slug || data?.boardId || data?.id || data?.slug;

      if (publicPinoutPngUpload) {
        if (!createdBoardId) {
          throw new Error('Board was created, but no board identifier was returned for the public PNG upload.');
        }

        const formData = new FormData();
        formData.append('public_pinout_png', publicPinoutPngUpload.blob, publicPinoutPngUpload.fileName);

        try {
          await adminBoardsAPI.uploadPublicPinoutPng(createdBoardId, formData);
        } catch (uploadError) {
          const uploadErrorMessage = uploadError?.response?.data?.error || uploadError?.message || 'Failed to upload the public pinout PNG.';
          throw new Error(`Board created, but the public pinout PNG upload failed: ${uploadErrorMessage}`);
        }
      }

      if (publishAfterCreate) {
        if (!createdBoardId) {
          throw new Error('Board was created, but no board identifier was returned for publishing.');
        }

        try {
          await adminBoardsAPI.publish(createdBoardId, true);
        } catch (publishError) {
          const publishErrorMessage = publishError?.response?.data?.error || publishError?.message || 'Failed to publish the board.';
          throw new Error(`Board created, but publishing failed: ${publishErrorMessage}`);
        }
      }

      setSuccess(publishAfterCreate ? 'Board created and published successfully!' : 'Board created as draft successfully!');
      resetCreateBoardDraft();
      setBoardsCache(new Map());
      await fetchBoards();
      return true;
    } catch (error) {
      if (createdBoard) {
        setBoardsCache(new Map());
        await fetchBoards().catch(() => {});
      }

      setError(error?.response?.data?.error || error?.message || 'Failed to create board. Please try again.');
      return false;
    } finally {
      setLoading(false);
      setCreatingPublishedBoard(false);
    }
  };

  const handleCreateBoard = async (e) => {
    e.preventDefault();
    await createBoardFromDraft(false);
  };

  const handleCreateAndPublishBoard = async () => {
    await createBoardFromDraft(true);
  };

  const { confirmNavigation: confirmCreateBoardExit } = useUnsavedChangesGuard({
    enabled: hasUnsavedBoardDraft,
    saveMessage: 'You have a new board draft. Save it before leaving?',
    discardMessage: 'Leave without saving this new board?',
    onSave: createBoardFromDraft,
    onDiscard: resetCreateBoardDraft,
  });

  const handleDeleteBoard = async (boardId) => {
    if (!window.confirm('Are you sure you want to delete this board?')) {
      return;
    }

    // Prevent double-clicks
    if (loadingOperations.has(`delete-${boardId}`)) {
      return;
    }

    console.log('Deleting board:', boardId);
    
    // Add to loading operations
    setLoadingOperations(prev => new Set(prev).add(`delete-${boardId}`));
    
    // Store the board to be deleted for potential rollback
    const boardToDelete = boards.find(board => board.id === boardId);
    
    // Optimistically remove the board from the UI
    setBoards(prevBoards => prevBoards.filter(board => board.id !== boardId));

    try {
      const { adminBoardsAPI } = await import('../services/adminApi');
      const response = await adminBoardsAPI.delete(boardId);

      console.log('Delete response status:', response.status);

      if (response.status === 200) {
        setSuccess('Board deleted successfully!');
        console.log('Board deleted successfully, refreshing data...');
        // Clear cache and refresh data to ensure consistency
        setBoardsCache(new Map());
        setLastFetchTime(0); // Force fresh fetch
        
        // Force a fresh fetch by calling the API directly
        try {
          const { adminBoardsAPI } = await import('../services/adminApi');
          const refreshResponse = await adminBoardsAPI.getAll();
          
          if (refreshResponse.status === 200) {
            const refreshData = refreshResponse.data;
            const rawBoardsData = Array.isArray(refreshData) ? refreshData : (refreshData.boards || refreshData.pinouts || []);
            const boardsData = normalizeAdminBoardCollection(rawBoardsData);
            console.log('Fresh data fetched:', boardsData.length, 'boards');
            setBoards(boardsData);
            setTotalBoards(boardsData.length);
            setBoardsCache(prev => new Map(prev).set('all-boards', boardsData));
            setLastFetchTime(Date.now());
          }
        } catch (refreshError) {
          console.error('Error refreshing data:', refreshError);
          await fetchBoards(); // Fallback to original method
        }
        
        console.log('Data refreshed after delete');
      } else {
        console.error('Delete failed:', response.data);
        setError('Failed to delete board');
        // Revert optimistic update on error
        if (boardToDelete) {
          setBoards(prevBoards => [...prevBoards, boardToDelete]);
        }
      }
    } catch (error) {
      console.error('Delete error:', error);
      setError('Failed to delete board. Please try again.');
      // Revert optimistic update on error
      if (boardToDelete) {
        setBoards(prevBoards => [...prevBoards, boardToDelete]);
      }
    } finally {
      // Remove from loading operations
      setLoadingOperations(prev => {
        const newSet = new Set(prev);
        newSet.delete(`delete-${boardId}`);
        return newSet;
      });
    }
  };

  const handleTogglePublish = async (boardId, published) => {
    // Prevent double-clicks
    if (loadingOperations.has(`publish-${boardId}`)) {
      return;
    }

    console.log('Toggle publish:', boardId, 'to', published);
    
    // Find the original board to get the current state
    const originalBoard = boards.find(board => board.id === boardId);
    if (!originalBoard) {
      console.error('Board not found:', boardId);
      return;
    }

    // Add to loading operations
    setLoadingOperations(prev => new Set(prev).add(`publish-${boardId}`));

    // Optimistically update the UI
    setBoards(prevBoards => 
      prevBoards.map(board => 
        board.id === boardId ? { ...board, published: published ? 1 : 0 } : board
      )
    );

    try {
      const { adminBoardsAPI } = await import('../services/adminApi');
      const response = await adminBoardsAPI.publish(boardId, published);

      console.log('Publish response status:', response.status);

      if (response.status === 200) {
        setSuccess(`Board ${published ? 'published' : 'unpublished'} successfully!`);
        // Clear cache and refresh data to ensure consistency
        setBoardsCache(new Map());
        setLastFetchTime(0); // Force fresh fetch
        
        // Force a fresh fetch by calling the API directly
        try {
          const { adminBoardsAPI } = await import('../services/adminApi');
          const refreshResponse = await adminBoardsAPI.getAll();
          
          if (refreshResponse.status === 200) {
            const refreshData = refreshResponse.data;
            const rawBoardsData = Array.isArray(refreshData) ? refreshData : (refreshData.boards || refreshData.pinouts || []);
            const boardsData = normalizeAdminBoardCollection(rawBoardsData);
            console.log('Fresh data fetched after publish:', boardsData.length, 'boards');
            setBoards(boardsData);
            setTotalBoards(boardsData.length);
            setBoardsCache(prev => new Map(prev).set('all-boards', boardsData));
            setLastFetchTime(Date.now());
          }
        } catch (refreshError) {
          console.error('Error refreshing data after publish:', refreshError);
          await fetchBoards(); // Fallback to original method
        }
        
        console.log('Data refreshed after publish toggle');
      } else {
        const errorData = await response.json();
        console.error('Publish toggle failed:', errorData);
        setError(`Failed to ${published ? 'publish' : 'unpublish'} board`);
        // Revert optimistic update on error
        setBoards(prevBoards => 
          prevBoards.map(board => 
            board.id === boardId ? { ...board, published: originalBoard.published } : board
          )
        );
      }
    } catch (error) {
      console.error('Publish toggle error:', error);
      setError(`Failed to ${published ? 'publish' : 'unpublish'} board. Please try again.`);
      // Revert optimistic update on error
      setBoards(prevBoards => 
        prevBoards.map(board => 
          board.id === boardId ? { ...board, published: originalBoard.published } : board
        )
      );
    } finally {
      // Remove from loading operations
      setLoadingOperations(prev => {
        const newSet = new Set(prev);
        newSet.delete(`publish-${boardId}`);
        return newSet;
      });
    }
  };

  const handleTogglePopularOnHome = async (boardId, popularOnHome) => {
    if (loadingOperations.has(`popular-${boardId}`)) {
      return;
    }

    const originalBoard = boards.find(board => board.id === boardId);
    if (!originalBoard) {
      return;
    }

    const isCurrentlyPopular = Boolean(Number(originalBoard.popular_on_home));
    if (popularOnHome && !isCurrentlyPopular && popularBoardsCount >= HOME_POPULAR_BOARD_LIMIT) {
      setError(`You can feature up to ${HOME_POPULAR_BOARD_LIMIT} boards on the home page.`);
      return;
    }

    setLoadingOperations(prev => new Set(prev).add(`popular-${boardId}`));

    setBoards(prevBoards =>
      prevBoards.map(board =>
        board.id === boardId ? { ...board, popular_on_home: popularOnHome ? 1 : 0 } : board
      )
    );

    try {
      const { adminBoardsAPI } = await import('../services/adminApi');
      const response = await adminBoardsAPI.togglePopularOnHome(boardId, popularOnHome);

      if (response.status === 200) {
        setSuccess(
          popularOnHome
            ? 'Board added to the home popular list successfully!'
            : 'Board removed from the home popular list successfully!'
        );
        setBoardsCache(new Map());
        setLastFetchTime(0);

        try {
          const refreshResponse = await adminBoardsAPI.getAll();

          if (refreshResponse.status === 200) {
            const refreshData = refreshResponse.data;
            const rawBoardsData = Array.isArray(refreshData) ? refreshData : (refreshData.boards || refreshData.pinouts || []);
            const boardsData = normalizeAdminBoardCollection(rawBoardsData);
            setBoards(boardsData);
            setTotalBoards(boardsData.length);
            setBoardsCache(prev => new Map(prev).set('all-boards', boardsData));
            setLastFetchTime(Date.now());
          }
        } catch (refreshError) {
          console.error('Error refreshing data after popular board update:', refreshError);
          await fetchBoards();
        }
      } else {
        setError(`Failed to update the home popular list for "${originalBoard.name}"`);
        setBoards(prevBoards =>
          prevBoards.map(board =>
            board.id === boardId ? { ...board, popular_on_home: originalBoard.popular_on_home } : board
          )
        );
      }
    } catch (error) {
      console.error('Popular board toggle error:', error);
      setError('Failed to update the home popular list. Please try again.');
      setBoards(prevBoards =>
        prevBoards.map(board =>
          board.id === boardId ? { ...board, popular_on_home: originalBoard.popular_on_home } : board
        )
      );
    } finally {
      setLoadingOperations(prev => {
        const newSet = new Set(prev);
        newSet.delete(`popular-${boardId}`);
        return newSet;
      });
    }
  };

  const CurrentSectionIcon = currentSection.icon;

  if (!isAuthenticated) {
    return (
      <AdminContainer>
        <div className="container">
          <LoginForm onSubmit={handleLogin}>
            <LoginLogo>
              <LoginLogoImage src={assetPath('/logo.png')} alt="MicroPinouts" />
            </LoginLogo>
            <FormTitle>Admin Login</FormTitle>
            
            {error && <ErrorMessage>{error}</ErrorMessage>}
            
            <FormGroup>
              <Label>Username</Label>
              <Input
                type="text"
                value={loginForm.username}
                onChange={(e) => setLoginForm({...loginForm, username: e.target.value})}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Password</Label>
              <Input
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                required
              />
            </FormGroup>
            
            <Button type="submit" disabled={loading}>
              {loading ? 'Logging in...' : 'Login'}
            </Button>
          </LoginForm>
        </div>
      </AdminContainer>
    );
  }

  return (
    <AdminContainer>
      <AdminHeader>
        <HeaderContent>
          <AdminTitle>
            <Settings size={32} />
            Admin Panel
          </AdminTitle>
        </HeaderContent>
      </AdminHeader>

      <AdminContent>
        {error && !boardsLoading && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}

        <SidebarOverlay
          type="button"
          $isOpen={isSidebarOpen}
          aria-label="Close admin navigation"
          onClick={() => setIsSidebarOpen(false)}
        />

        <AdminShell>
          <Sidebar $isOpen={isSidebarOpen}>
            <SidebarHeader>
              <SidebarHeading>Admin Sections</SidebarHeading>
              <SidebarCloseButton
                type="button"
                aria-label="Close admin navigation"
                onClick={() => setIsSidebarOpen(false)}
              >
                <X size={18} />
              </SidebarCloseButton>
            </SidebarHeader>

            <SidebarNav>
              {ADMIN_SECTIONS.map((section) => {
                const SectionIcon = section.icon;

                return (
                  <SidebarNavItem
                    key={section.id}
                    type="button"
                    $active={activeTab === section.id}
                    aria-pressed={activeTab === section.id}
                    onClick={() => handleSectionSelect(section.id)}
                  >
                    <SectionIcon size={18} />
                    <SidebarNavText>{section.label}</SidebarNavText>
                  </SidebarNavItem>
                );
              })}
            </SidebarNav>
          </Sidebar>

          <AdminMain>
            <SidebarToggleBar>
              <SidebarToggleButton
                type="button"
                aria-label="Open admin navigation"
                onClick={() => setIsSidebarOpen(true)}
              >
                <Menu size={18} />
                Menu
              </SidebarToggleButton>
              <SidebarToggleMeta>
                <SidebarToggleLabel>Current Section</SidebarToggleLabel>
                <SidebarToggleValue>
                  <CurrentSectionIcon size={16} />
                  {currentSection.label}
                </SidebarToggleValue>
              </SidebarToggleMeta>
            </SidebarToggleBar>

            {activeTab === 'boards' && (
              <TabContent>
            <SearchAndFilterSection>
              <SearchInput
                type="text"
                placeholder="Search pinouts by name, description, or manufacturer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <FilterRow>
                <FilterGroup>
                  <FilterLabel>Status</FilterLabel>
                  <FilterSelect
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">All Status</option>
                    <option value="published">Published</option>
                    <option value="draft">Draft</option>
                  </FilterSelect>
                </FilterGroup>
                <FilterGroup>
                  <FilterLabel>Manufacturer</FilterLabel>
                  <FilterSelect
                    value={manufacturerFilter}
                    onChange={(e) => setManufacturerFilter(e.target.value)}
                  >
                    <option value="all">All Manufacturers</option>
                    {manufacturers.map(manufacturer => (
                      <option key={manufacturer} value={manufacturer}>
                        {manufacturer}
                      </option>
                    ))}
                  </FilterSelect>
                </FilterGroup>
                <ResultsCount>
                  {totalBoards} pinouts total
                  {` | Popular on home ${popularBoardsCount}/${HOME_POPULAR_BOARD_LIMIT}`}
                  {totalPages > 1 && ` • Page ${currentPage} of ${totalPages}`}
                </ResultsCount>
                <ClearFiltersButton onClick={clearFilters}>
                  Clear Filters
                </ClearFiltersButton>
              </FilterRow>
            </SearchAndFilterSection>
            <UploadPanel>
              <UploadPanelHeader>
                <UploadPanelTitle>Upload FZPZ</UploadPanelTitle>
                <UploadPanelDescription>
                  Import a Fritzing `.fzpz` file to start a new board draft, then review the parsed pins and metadata before saving.
                </UploadPanelDescription>
              </UploadPanelHeader>
              <UploadArea
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() => document.getElementById('fileInput').click()}
              >
                <UploadIcon>
                  <Upload size={48} />
                </UploadIcon>
                <UploadText>Drop Fritzing .fzpz file here or click to browse</UploadText>
                <UploadSubtext>Supports .fzpz files up to 10MB</UploadSubtext>
                <FileInput
                  id="fileInput"
                  type="file"
                  accept=".fzpz"
                  onChange={handleFileSelect}
                />
              </UploadArea>
            </UploadPanel>
            {boardsLoading ? (
              <BoardsLoadingContainer>
                <BoardsLoadingSpinner>
                  <SpinnerRing />
                  <LoadingContent>
                    <LoadingTitle>Loading Pinouts</LoadingTitle>
                    <LoadingText>Fetching data from the database...</LoadingText>
                    <LoadingDots>
                      <span></span>
                      <span></span>
                      <span></span>
                    </LoadingDots>
                  </LoadingContent>
                </BoardsLoadingSpinner>
              </BoardsLoadingContainer>
            ) : filteredBoards.length > 0 ? (
              <BoardsGrid>
                {filteredBoards.map(board => (
                  <BoardCard key={board.id}>
                    <BoardTitle>
                      <span>{board.name}</span>
                      <StatusPillRow>
                        <PublishStatus published={board.published}>
                          {board.published ? 'Published' : 'Draft'}
                        </PublishStatus>
                        <PopularStatus $active={Boolean(Number(board.popular_on_home))}>
                          {Number(board.popular_on_home) ? 'Popular on Home' : 'Not Popular'}
                        </PopularStatus>
                      </StatusPillRow>
                    </BoardTitle>
                    <BoardInfo>
                      {board.pin_count} pins • {board.manufacturer} • {board.package_type}
                    </BoardInfo>
                    <BoardMetaRow>
                      <PopularToggle
                        $disabled={
                          loadingOperations.has(`popular-${board.id}`)
                          || (!Number(board.popular_on_home) && popularBoardsCount >= HOME_POPULAR_BOARD_LIMIT)
                        }
                      >
                        <input
                          type="checkbox"
                          checked={Boolean(Number(board.popular_on_home))}
                          disabled={
                            loadingOperations.has(`popular-${board.id}`)
                            || (!Number(board.popular_on_home) && popularBoardsCount >= HOME_POPULAR_BOARD_LIMIT)
                          }
                          onChange={(event) => handleTogglePopularOnHome(board.id, event.target.checked)}
                        />
                        <span>
                          {loadingOperations.has(`popular-${board.id}`)
                            ? 'Updating home list...'
                            : 'Show in home popular boards'}
                        </span>
                      </PopularToggle>
                    </BoardMetaRow>
                    <BoardActions>
                      {(() => {
                        const boardIdentifier = board.slug || board.id;
                        const isPublished = Boolean(Number(board.published));
                        const boardViewPath = isPublished
                          ? `/board/${boardIdentifier}`
                          : `/board/preview/${boardIdentifier}`;

                        return (
                          <>
                      <ActionButton 
                        className="edit"
                        onClick={() => navigate(`/admin/boards/${board.slug || board.id}/edit`)}
                      >
                        <Edit size={16} />
                        Edit
                      </ActionButton>
                      <ActionButton 
                        className="view"
                        onClick={() => navigate(boardViewPath)}
                      >
                        <Eye size={16} />
                        {isPublished ? 'View' : 'Preview'}
                      </ActionButton>
                          </>
                        );
                      })()}
                      <ActionButton 
                        className={board.published ? "unpublish" : "publish"}
                        onClick={() => handleTogglePublish(board.id, !board.published)}
                        disabled={loadingOperations.has(`publish-${board.id}`)}
                      >
                        {board.published ? <EyeOff size={16} /> : <Eye size={16} />}
                        {loadingOperations.has(`publish-${board.id}`) ? 'Updating...' : (board.published ? 'Unpublish' : 'Publish')}
                      </ActionButton>
                      <ActionButton 
                        className="delete"
                        onClick={() => handleDeleteBoard(board.id)}
                        disabled={loadingOperations.has(`delete-${board.id}`)}
                      >
                        <Trash2 size={16} />
                        {loadingOperations.has(`delete-${board.id}`) ? 'Deleting...' : 'Delete'}
                      </ActionButton>
                    </BoardActions>
                  </BoardCard>
                ))}
              </BoardsGrid>
            ) : (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <h3 style={{ color: '#64748b', marginBottom: '16px' }}>No pinouts found</h3>
                <p style={{ color: '#94a3b8' }}>
                  {searchTerm || statusFilter !== 'all' || manufacturerFilter !== 'all'
                    ? 'Try adjusting your search term or filters to find what you\'re looking for.'
                    : 'No boards have been uploaded yet. Use the Upload FZPZ panel above to add one.'
                  }
                </p>
              </div>
            )}
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <PaginationContainer>
                <PaginationButton
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  ←
                </PaginationButton>
                
                {/* Page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <PaginationButton
                      key={pageNum}
                      $active={currentPage === pageNum}
                      onClick={() => handlePageChange(pageNum)}
                    >
                      {pageNum}
                    </PaginationButton>
                  );
                })}
                
                <PaginationButton
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  →
                </PaginationButton>
                
                <PaginationInfo>
                  Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalBoards)} of {totalBoards}
                </PaginationInfo>
              </PaginationContainer>
            )}
              </TabContent>
            )}

            {activeTab === 'project-builder' && (
              <TabContent>
                <ProjectBuilderAdminPanel
                  boards={boards}
                  boardsLoading={boardsLoading}
                  onBoardScaleSaved={(boardId, nextProjectBuilderSettings) => {
                    setBoards((currentBoards) =>
                      normalizeAdminBoardCollection(
                        currentBoards.map((board) =>
                          String(board.id) === String(boardId)
                            ? {
                                ...board,
                                ...nextProjectBuilderSettings,
                              }
                            : board
                        )
                      )
                    );
                    setBoardsCache(new Map());
                  }}
                />
              </TabContent>
            )}

            {activeTab === 'lessons' && (
              <TabContent>
                <ProjectBuilderLessonsManager />
              </TabContent>
            )}

            {activeTab === 'ads' && (
              <TabContent>
                <AdsAdminPanel />
              </TabContent>
            )}

            {activeTab === 'ic-pinouts' && (
              <TabContent>
            <ICPinouts />
              </TabContent>
            )}

            {activeTab === 'wiring' && (
              <TabContent>
            <WiringGuideContent />
              </TabContent>
            )}

            {activeTab === 'guides' && (
              <TabContent>
                <GuidesAdminPanel />
              </TabContent>
            )}

            <AnimatePresence>
          {showModal && fritzingData && (
            <Modal
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <ModalContent $wide>
                <ModalHeader>
                  <ModalTitle>Create Board from Fritzing Data</ModalTitle>
                  <CloseButton onClick={() => confirmCreateBoardExit()} disabled={loading}>
                    <X size={24} />
                  </CloseButton>
                </ModalHeader>

                <form onSubmit={handleCreateBoard}>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '12px',
                    marginBottom: '20px',
                    padding: '14px 16px',
                    borderRadius: '12px',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div style={{ fontSize: '13px', color: '#334155', fontWeight: 600 }}>
                      {fritzingData.filename || 'Uploaded FZPZ'}
                    </div>
                    <div style={{ fontSize: '13px', color: '#64748b' }}>
                      {fritzingData.totalPins || 0} detected pins
                    </div>
                    <div style={{ fontSize: '13px', color: '#64748b' }}>
                      Rotation: {uploadRotation}&deg;
                    </div>
                  </div>

                  <FormGrid>
                    <FormGroup>
                      <Label>Board Name *</Label>
                      <Input
                        type="text"
                        value={boardForm.name}
                        onChange={(e) => setBoardForm({...boardForm, name: e.target.value})}
                        required
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Manufacturer</Label>
                      <Input
                        type="text"
                        value={boardForm.manufacturer}
                        onChange={(e) => setBoardForm({...boardForm, manufacturer: e.target.value})}
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Package Type</Label>
                      <Input
                        type="text"
                        value={boardForm.package_type}
                        onChange={(e) => setBoardForm({...boardForm, package_type: e.target.value})}
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>CPU</Label>
                      <Input
                        type="text"
                        value={boardForm.cpu}
                        onChange={(e) => setBoardForm({...boardForm, cpu: e.target.value})}
                        placeholder="e.g., ATmega328P, ESP32-S3"
                      />
                    </FormGroup>

                    <FormGroup style={{ gridColumn: '1 / -1' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="checkbox"
                          checked={Boolean(boardForm.show_mcu_specs)}
                          onChange={(e) =>
                            setBoardForm({
                              ...boardForm,
                              show_mcu_specs: e.target.checked
                            })
                          }
                        />
                        Show MCU specs on public page
                      </label>
                      <FieldHelperText>
                        Turn this off for sensors/modules so CPU, Clock Speed, Flash Memory, ROM, and RAM cards are hidden.
                      </FieldHelperText>
                    </FormGroup>

                    <FormGroup>
                      <Label>Clock Speed</Label>
                      <Input
                        type="text"
                        value={boardForm.clock_speed}
                        onChange={(e) => setBoardForm({...boardForm, clock_speed: e.target.value})}
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Flash Memory</Label>
                      <Input
                        type="text"
                        value={boardForm.flash_memory}
                        onChange={(e) => setBoardForm({...boardForm, flash_memory: e.target.value})}
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>RAM</Label>
                      <Input
                        type="text"
                        value={boardForm.ram}
                        onChange={(e) => setBoardForm({...boardForm, ram: e.target.value})}
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>ROM</Label>
                      <Input
                        type="text"
                        value={boardForm.rom}
                        onChange={(e) => setBoardForm({...boardForm, rom: e.target.value})}
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Documentation URL</Label>
                      <Input
                        type="url"
                        value={boardForm.link}
                        onChange={(e) => setBoardForm({...boardForm, link: e.target.value})}
                        placeholder="https://example.com/board-documentation"
                      />
                    </FormGroup>

                      <FormGroup>
                        <Label>Datasheet URL</Label>
                        <Input
                          type="url"
                          value={boardForm.datasheet_url}
                          onChange={(e) => setBoardForm({ ...boardForm, datasheet_url: e.target.value })}
                          placeholder="https://..."
                        />
                      </FormGroup>

                      <FormGroup style={{ gridColumn: '1 / -1' }}>
                        <Label>Tutorials</Label>
                        <TutorialUrlsEditor
                          value={boardForm.tutorial_urls}
                          onChange={(tutorial_urls) => setBoardForm({ ...boardForm, tutorial_urls })}
                          disabled={loading}
                          itemNameSingular="tutorial URL"
                          itemNamePlural="tutorial URLs"
                          addButtonLabel="Add Tutorial URL"
                          helperText="Add one or more tutorial URLs for this board."
                          emptyStateText="No tutorial URLs added yet."
                          urlPlaceholder="https://example.com/tutorial"
                        />
                      </FormGroup>

                      <FormGroup>
                        <Label>Verification Status</Label>
                      <SelectInput
                        value={boardForm.verification_status}
                        onChange={(e) => setBoardForm({ ...boardForm, verification_status: e.target.value })}
                      >
                        {BOARD_VERIFICATION_STATUS_OPTIONS.map((statusOption) => (
                          <option key={statusOption.value} value={statusOption.value}>
                            {statusOption.label}
                          </option>
                        ))}
                      </SelectInput>
                    </FormGroup>

                    <FormGroup>
                      <Label>Category</Label>
                      <select
                        value={boardForm.category}
                        onChange={(e) => {
                          const nextCategory = e.target.value;

                          setBoardForm({
                            ...boardForm,
                            category: nextCategory,
                            show_mcu_specs: nextCategory === 'sensor-module'
                              ? false
                              : boardForm.show_mcu_specs
                          });
                        }}
                        style={{
                          padding: '12px 16px',
                          border: '2px solid #e2e8f0',
                          borderRadius: '8px',
                          fontSize: '16px',
                          background: 'white',
                          width: '100%'
                        }}
                      >
                        <option value="">Select a category...</option>
                        <option value="microcontroller-8bit">🔧 Microcontroller (8-bit)</option>
                        <option value="microcontroller-16bit">⚙️ Microcontroller (16-bit)</option>
                        <option value="microcontroller-32bit">🔩 Microcontroller (32-bit)</option>
                        <option value="development-board">📱 Development Board</option>
                        <option value="sensor-module">📡 Sensor & Module</option>
                        <option value="communication-ic">📶 Communication IC</option>
                        <option value="power-management">⚡ Power Management</option>
                        <option value="memory-storage">💾 Memory & Storage</option>
                        <option value="custom-other">🔧 Custom/Other</option>
                      </select>
                    </FormGroup>

                    <FormGroup style={{ gridColumn: '1 / -1' }}>
                      <FormSectionHeading>Smart Compare Fields</FormSectionHeading>
                      <FormSectionHint>
                        Use these structured values for future compatibility checks. The numeric voltage and current fields below are now the source of truth for new boards.
                      </FormSectionHint>
                    </FormGroup>

                    <FormGroup>
                      <Label>Supply Voltage Min (V)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.001"
                        value={boardForm.supply_voltage_min_v}
                        onChange={(e) => setBoardForm({ ...boardForm, supply_voltage_min_v: e.target.value })}
                        placeholder="e.g. 3.3"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Supply Voltage Max (V)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.001"
                        value={boardForm.supply_voltage_max_v}
                        onChange={(e) => setBoardForm({ ...boardForm, supply_voltage_max_v: e.target.value })}
                        placeholder="e.g. 5"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Logic Voltage Min (V)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.001"
                        value={boardForm.logic_voltage_min_v}
                        onChange={(e) => setBoardForm({ ...boardForm, logic_voltage_min_v: e.target.value })}
                        placeholder="e.g. 1.8"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Logic Voltage Max (V)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.001"
                        value={boardForm.logic_voltage_max_v}
                        onChange={(e) => setBoardForm({ ...boardForm, logic_voltage_max_v: e.target.value })}
                        placeholder="e.g. 3.3"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Typical Current Draw (mA)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.001"
                        value={boardForm.current_draw_typ_ma}
                        onChange={(e) => setBoardForm({ ...boardForm, current_draw_typ_ma: e.target.value })}
                        placeholder="e.g. 0.6"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Max Current Draw (mA)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.001"
                        value={boardForm.current_draw_max_ma}
                        onChange={(e) => setBoardForm({ ...boardForm, current_draw_max_ma: e.target.value })}
                        placeholder="e.g. 1.2"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>3.3V Rail Limit (mA)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.001"
                        value={boardForm.rail_3v3_max_ma}
                        onChange={(e) => setBoardForm({ ...boardForm, rail_3v3_max_ma: e.target.value })}
                        placeholder="For MCU/dev boards"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>5V Rail Limit (mA)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.001"
                        value={boardForm.rail_5v_max_ma}
                        onChange={(e) => setBoardForm({ ...boardForm, rail_5v_max_ma: e.target.value })}
                        placeholder="For MCU/dev boards"
                      />
                    </FormGroup>

                    <FormGroup style={{ gridColumn: '1 / -1' }}>
                      <Label>Supported Protocols</Label>
                      <CheckboxGrid>
                        {PROTOCOL_OPTIONS.map((protocol) => (
                          <CheckboxChip key={protocol.value}>
                            <input
                              type="checkbox"
                              checked={boardForm.supported_protocols.includes(protocol.value)}
                              onChange={() => handleBoardProtocolToggle(protocol.value)}
                            />
                            <span>{protocol.label}</span>
                          </CheckboxChip>
                        ))}
                      </CheckboxGrid>
                    </FormGroup>

                    <FormGroup style={{ gridColumn: '1 / -1' }}>
                      <Label>Protocol Notes</Label>
                      <TextArea
                        value={boardForm.protocol_notes}
                        onChange={(e) => setBoardForm({ ...boardForm, protocol_notes: e.target.value })}
                        placeholder="Optional notes about addresses, pull-ups, mode switching, level shifting, or anything else the compare engine should not guess."
                      />
                    </FormGroup>

                    <FormGroup style={{ gridColumn: '1 / -1' }}>
                      <Label>Intrinsic SVG Size</Label>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                        gap: '12px'
                      }}>
                        <div style={{ gridColumn: '1 / -1', maxWidth: '220px' }}>
                          <div style={{ fontSize: '13px', color: '#475569', marginBottom: '6px', fontWeight: 500 }}>
                            Input Mode
                          </div>
                          <SelectInput
                            value={uploadSvgSizeMode}
                            onChange={(e) => setUploadSvgSizeMode(e.target.value)}
                            disabled={loading || !fritzingData?.displaySVG}
                          >
                            <option value={SVG_SIZE_INPUT_MODE_UNITS}>SVG Units</option>
                            <option value={SVG_SIZE_INPUT_MODE_PERCENT}>Percentage</option>
                          </SelectInput>
                        </div>
                        {uploadSvgSizeMode === SVG_SIZE_INPUT_MODE_UNITS ? (
                          <>
                            <div>
                              <div style={{ fontSize: '13px', color: '#475569', marginBottom: '6px', fontWeight: 500 }}>
                                Width
                              </div>
                              <Input
                                type="number"
                                step="0.01"
                                value={uploadSvgSizeDraft.width}
                                onChange={(e) => handleUploadSvgSizeFieldChange('width', e.target.value)}
                                onBlur={handleUploadSvgSizeFieldBlur}
                                disabled={loading || !fritzingData?.displaySVG}
                              />
                            </div>
                            <div>
                              <div style={{ fontSize: '13px', color: '#475569', marginBottom: '6px', fontWeight: 500 }}>
                                Height
                              </div>
                              <Input
                                type="number"
                                step="0.01"
                                value={uploadSvgSizeDraft.height}
                                onChange={(e) => handleUploadSvgSizeFieldChange('height', e.target.value)}
                                onBlur={handleUploadSvgSizeFieldBlur}
                                disabled={loading || !fritzingData?.displaySVG}
                              />
                            </div>
                          </>
                        ) : (
                          <>
                            <div>
                              <div style={{ fontSize: '13px', color: '#475569', marginBottom: '6px', fontWeight: 500 }}>
                                Scale
                              </div>
                              <Input
                                type="number"
                                min="1"
                                step="0.1"
                                value={uploadSvgPercentageDraft}
                                onChange={(e) => handleUploadSvgPercentageChange(e.target.value)}
                                onBlur={handleUploadSvgPercentageBlur}
                                disabled={loading || !fritzingData?.displaySVG}
                              />
                            </div>
                            <div style={{
                              display: 'grid',
                              gap: '6px',
                              alignContent: 'end',
                              paddingBottom: '2px'
                            }}>
                              <div style={{ fontSize: '13px', color: '#475569', fontWeight: 500 }}>
                                Resulting Size
                              </div>
                              <div style={{ fontSize: '14px', color: '#0f172a', fontWeight: 600 }}>
                                {uploadSvgIntrinsicSize.widthText && uploadSvgIntrinsicSize.heightText
                                  ? `${uploadSvgIntrinsicSize.widthText} × ${uploadSvgIntrinsicSize.heightText}`
                                  : 'Not detected yet'}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                      <div style={{ fontSize: '13px', color: '#64748b', lineHeight: 1.5 }}>
                        Detected from {uploadSvgDetectedSize.sourceLabel}. Values use {uploadSvgDetectedSize.unitLabel.toLowerCase()}. The aspect ratio stays locked automatically, and the preview updates immediately so you can tame oversized uploads before using the public scale control.
                      </div>
                    </FormGroup>

                    <FormGroup>
                      <Label>Public SVG Scale</Label>
                      <Input
                        type="number"
                        min="0.25"
                        max="3"
                        step="0.05"
                        value={boardForm.svg_display_scale ?? 1}
                        onChange={(e) => setBoardForm({ ...boardForm, svg_display_scale: e.target.value })}
                      />
                      <div style={{ fontSize: '13px', color: '#64748b', lineHeight: 1.5 }}>
                        Use this after intrinsic sizing when you still need the board to render smaller or larger in public, compare, and embed views.
                      </div>
                    </FormGroup>

                    <FormGroup>
                      <Label>Board Detail Rotation</Label>
                      <SelectInput
                        value={boardForm.public_svg_rotation_deg ?? 0}
                        onChange={(e) => setBoardForm({
                          ...boardForm,
                          public_svg_rotation_deg: normalizeRotation(e.target.value)
                        })}
                      >
                        <option value="0">0°</option>
                        <option value="90">90°</option>
                        <option value="180">180°</option>
                        <option value="270">270°</option>
                      </SelectInput>
                      <div style={{ fontSize: '13px', color: '#64748b', lineHeight: 1.5 }}>
                        This only affects the public board detail page preview. Wiring and Builder keep using their own rotation controls.
                      </div>
                    </FormGroup>
                  </FormGrid>

                  <FormGroup>
                    <Label>Affiliate Links</Label>
                    <AffiliateLinksEditor
                      value={boardForm.affiliate_links}
                      onChange={(affiliate_links) => setBoardForm({ ...boardForm, affiliate_links })}
                      disabled={loading}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Description</Label>
                    <RichContentEditor
                      htmlValue={boardForm.description}
                      onChange={(value) => setBoardForm({ ...boardForm, description: value })}
                      richTextPlaceholder="Describe the board with formatting..."
                      htmlSourcePlaceholder="<p>Describe the board with raw HTML here...</p>"
                      markdownPlaceholder={`# Board overview

Use markdown here.

| Pin group | Notes |
| --- | --- |
| Power | Voltage and current details |
| GPIO | Helpful caveats or defaults |`}
                      conversionNote="Switch between Rich Text, raw HTML, Markdown, and Preview. The saved board description stays HTML, so Markdown and Rich Text both flow into the published board page."
                      htmlHint="Use raw HTML here when you need markup the rich-text editor cannot safely preserve, such as custom tables."
                      tableProtectionHint="This board description currently contains a table. Rich Text mode is temporarily disabled because it can strip table markup. Keep editing the table in HTML or Markdown until you remove it."
                      markdownHint="Use headings, lists, links, blockquotes, code blocks, and GitHub-style tables here. The saved board description is still converted into HTML automatically."
                      previewHint="This is the rendered board description preview."
                      previewEmptyState="Nothing to preview yet. Start writing in Rich Text, HTML, or Markdown and the rendered board description will appear here."
                      height={220}
                      disabled={loading}
                    />
                  </FormGroup>

                  <div style={{ marginTop: '12px' }}>
                    <FormSectionHeading>SEO</FormSectionHeading>
                    <FormSectionHint>
                      These fields populate the public board page&apos;s meta description and keyword tags.
                    </FormSectionHint>
                  </div>

                  <FormGroup>
                    <Label>SEO Meta Description</Label>
                    <FieldActionRow>
                      <FieldActionButton
                        type="button"
                        onClick={handleRegenerateBoardMetaDescription}
                        disabled={loading}
                      >
                        Regenerate From Description
                      </FieldActionButton>
                    </FieldActionRow>
                    <TextArea
                      value={boardForm.meta_description}
                      onChange={(e) => setBoardForm({ ...boardForm, meta_description: e.target.value })}
                      placeholder="Short plain-text summary used for search results..."
                    />
                    <FieldHelperText>
                      Plain text works best here. Aim for a concise summary of the board.
                    </FieldHelperText>
                  </FormGroup>

                  <FormGroup>
                    <Label>SEO Keywords</Label>
                    <Input
                      type="text"
                      value={boardForm.seo_keywords}
                      onChange={(e) => setBoardForm({ ...boardForm, seo_keywords: e.target.value })}
                      placeholder="arduino nano, pinout, atmega328p, development board"
                    />
                    <FieldHelperText>
                      Separate keywords with commas.
                    </FieldHelperText>
                  </FormGroup>

                  <FormGroup>
                    <Label>Image Gallery</Label>
                    <BoardGalleryEditor
                      value={boardForm.gallery_images}
                      onChange={(gallery_images) => setBoardForm({ ...boardForm, gallery_images })}
                      disabled={loading}
                    />
                  </FormGroup>

                  <div style={{
                    gridColumn: '1 / -1',
                    padding: '20px',
                    borderRadius: '16px',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div style={{ fontSize: '18px', fontWeight: 600, color: '#0f172a', marginBottom: '8px' }}>
                      Board Details JSON
                    </div>
                    <div style={{ fontSize: '14px', color: '#475569', lineHeight: 1.6, marginBottom: '16px' }}>
                      Paste a JSON object here to update the current board draft quickly, or export the current draft as JSON and round-trip it back after editing.
                    </div>
                    <TextArea
                      value={uploadBoardMetadataJson}
                      onChange={(e) => setUploadBoardMetadataJson(e.target.value)}
                      placeholder={ADMIN_BOARD_METADATA_JSON_EXAMPLE}
                      spellCheck={false}
                      disabled={loading}
                      style={{ minHeight: '220px' }}
                    />
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '14px' }}>
                      <FieldActionButton
                        type="button"
                        onClick={handleApplyUploadBoardMetadataJson}
                        disabled={loading || !(uploadBoardMetadataJson || '').trim()}
                      >
                        Apply JSON to Draft
                      </FieldActionButton>
                      <FieldActionButton
                        type="button"
                        onClick={loadCurrentUploadBoardMetadataJson}
                        disabled={loading}
                      >
                        Load Current Data
                      </FieldActionButton>
                      <FieldActionButton
                        type="button"
                        onClick={handleCopyCurrentUploadBoardMetadataJson}
                        disabled={loading}
                      >
                        Copy Current JSON
                      </FieldActionButton>
                      <FieldActionButton
                        type="button"
                        onClick={() => {
                          setUploadBoardMetadataJson(ADMIN_BOARD_METADATA_JSON_EXAMPLE);
                          setUploadBoardMetadataSummary('');
                        }}
                        disabled={loading}
                      >
                        Load Example
                      </FieldActionButton>
                      <FieldActionButton
                        type="button"
                        onClick={() => {
                          setUploadBoardMetadataJson('');
                          setUploadBoardMetadataSummary('');
                        }}
                        disabled={loading || (!(uploadBoardMetadataJson || '').trim() && !uploadBoardMetadataSummary)}
                      >
                        Clear
                      </FieldActionButton>
                    </div>
                    <FieldHelperText>
                      Supported fields include <code>manufacturer</code>, <code>package_type</code>, <code>cpu</code>,
                      {' '}<code>supply_voltage_min_v</code>, <code>supply_voltage_max_v</code>, <code>logic_voltage_min_v</code>,
                      {' '}<code>logic_voltage_max_v</code>, <code>current_draw_typ_ma</code>, <code>current_draw_max_ma</code>,
                      {' '}<code>rail_3v3_max_ma</code>, <code>rail_5v_max_ma</code>, <code>clock_speed</code>, <code>flash</code>,
                      {' '}<code>ram</code>, <code>rom</code>, <code>supported_protocols</code>, <code>documentation_url</code>,
                      {' '}<code>datasheet_url</code>, <code>verification_status</code>, and <code>description</code>.
                    </FieldHelperText>
                    <details style={{ marginTop: '12px' }}>
                      <summary style={{ cursor: 'pointer', color: '#1e40af', fontSize: '13px', fontWeight: 600 }}>
                        Example JSON
                      </summary>
                      <pre style={{
                        margin: '10px 0 0',
                        padding: '14px',
                        borderRadius: '10px',
                        background: '#ffffff',
                        border: '1px solid #dbe4f0',
                        fontSize: '12px',
                        lineHeight: 1.6,
                        whiteSpace: 'pre-wrap',
                        overflowX: 'auto'
                      }}>
                        {ADMIN_BOARD_METADATA_JSON_EXAMPLE}
                      </pre>
                    </details>
                    {uploadBoardMetadataSummary && (
                      <FieldHelperText style={{ marginTop: '12px', color: '#166534' }}>
                        {uploadBoardMetadataSummary}
                      </FieldHelperText>
                    )}
                  </div>

                  <div style={{ marginTop: '28px', marginBottom: '18px' }}>
                    <div style={{ fontSize: '18px', fontWeight: 600, color: '#0f172a', marginBottom: '8px' }}>
                      Pinout Layout
                    </div>
                    <div style={{ fontSize: '14px', color: '#475569', lineHeight: 1.6 }}>
                      Finalize the board rotation, then place and adjust the clickable pin labels. You can place detected pin labels from the upload or add fully custom callouts for anything the FZPZ did not expose cleanly.
                    </div>
                  </div>

                  {pinCalloutValidation.errors.length > 0 && (
                    <ErrorMessage>{pinCalloutValidation.errors[0]}</ErrorMessage>
                  )}

                  {pinCalloutValidation.warnings.length > 0 && (
                    <div style={{
                      background: '#fffbeb',
                      border: '1px solid #fde68a',
                      color: '#92400e',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      marginBottom: '16px',
                      fontSize: '14px'
                    }}>
                      {pinCalloutValidation.warnings.join(' ')}
                    </div>
                  )}

                  <PinoutCalloutEditor
                    svgContent={fritzingData.displaySVG}
                    pins={uploadPins}
                    rotation={uploadRotation}
                    callouts={uploadPinCallouts}
                    onRotationChange={handleUploadRotationChange}
                    onCalloutsChange={setUploadPinCallouts}
                    selectedPinKey={selectedUploadPinKey}
                    onSelectedPinKeyChange={setSelectedUploadPinKey}
                    onNormalizeCanvasSize={handleNormalizeUploadSvgCanvasSize}
                    disabled={loading}
                  />

                  <div style={{
                    marginTop: '24px',
                    paddingTop: '24px',
                    borderTop: '1px solid #e2e8f0'
                  }}>
                    <div style={{ fontSize: '18px', fontWeight: 600, color: '#0f172a', marginBottom: '8px' }}>
                      Public SVG Preview
                    </div>
                    <div style={{ fontSize: '14px', color: '#475569', lineHeight: 1.6, marginBottom: '16px' }}>
                      This uses the same board SVG, callouts, display scale, and board-detail rotation that the public board page will use.
                    </div>
                    <BoardPublicSvgPreview
                      svgContent={uploadPublicPreviewSvg}
                      pins={uploadPins}
                      displayScale={boardForm.svg_display_scale ?? 1}
                      artworkRotation={boardForm.public_svg_rotation_deg ?? 0}
                      showAlternatePinCalloutColors={true}
                    />
                  </div>

                  {uploadPins.length > 0 && (
                    <div style={{
                      marginTop: '24px',
                      padding: '24px',
                      borderRadius: '16px',
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0'
                    }}>
                      <div style={{ fontSize: '18px', fontWeight: 600, color: '#0f172a', marginBottom: '8px' }}>
                        Pin Label Metadata
                      </div>
                      <div style={{ fontSize: '14px', color: '#475569', lineHeight: 1.6, marginBottom: '18px' }}>
                        Select a detected label to edit the pin metadata that will be saved with this new board.
                      </div>

                      <PinBulkElectricalDefaultsPanel
                        values={uploadBulkElectricalValues}
                        applyToAll={applyUploadBulkElectricalValues}
                        onValueChange={handleUploadBulkElectricalValueChange}
                        onApplyToAllChange={handleApplyUploadBulkElectricalValuesChange}
                        applyCount={uploadBulkEligiblePinCount}
                        disabled={loading}
                        title="Bulk Pin Defaults"
                        description="Fill all three values, then check Apply to All to stamp them onto detected non-power pins before saving this board."
                      />

                      <div style={{
                        marginTop: '18px',
                        marginBottom: '18px',
                        padding: '18px',
                        borderRadius: '14px',
                        background: '#ffffff',
                        border: '1px solid #dbe4f0'
                      }}>
                        <div style={{ fontSize: '16px', fontWeight: 600, color: '#0f172a', marginBottom: '8px' }}>
                          Bulk Pin Metadata JSON
                        </div>
                        <div style={{ fontSize: '14px', color: '#475569', lineHeight: 1.6, marginBottom: '14px' }}>
                          Paste a JSON array of pin patches here to update the uploaded pin draft immediately, or export the current pins as JSON and round-trip them back after editing.
                        </div>
                        <TextArea
                          value={uploadPinMetadataJson}
                          onChange={(e) => setUploadPinMetadataJson(e.target.value)}
                          placeholder={ADMIN_PIN_METADATA_JSON_EXAMPLE}
                          spellCheck={false}
                          disabled={loading}
                          style={{ minHeight: '220px' }}
                        />
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '14px' }}>
                          <FieldActionButton
                            type="button"
                            onClick={handleApplyUploadPinMetadataJson}
                            disabled={loading || !(uploadPinMetadataJson || '').trim()}
                          >
                            Apply JSON to Pins
                          </FieldActionButton>
                          <FieldActionButton
                            type="button"
                            onClick={loadCurrentUploadPinMetadataJson}
                            disabled={loading || uploadPins.length === 0}
                          >
                            Load Current Data
                          </FieldActionButton>
                          <FieldActionButton
                            type="button"
                            onClick={handleCopyCurrentUploadPinMetadataJson}
                            disabled={loading || uploadPins.length === 0}
                          >
                            Copy Current JSON
                          </FieldActionButton>
                          <FieldActionButton
                            type="button"
                            onClick={() => {
                              setUploadPinMetadataJson(ADMIN_PIN_METADATA_JSON_EXAMPLE);
                              setUploadPinMetadataSummary('');
                            }}
                            disabled={loading}
                          >
                            Load Example
                          </FieldActionButton>
                          <FieldActionButton
                            type="button"
                            onClick={() => {
                              setUploadPinMetadataJson('');
                              setUploadPinMetadataSummary('');
                            }}
                            disabled={loading || (!(uploadPinMetadataJson || '').trim() && !uploadPinMetadataSummary)}
                          >
                            Clear
                          </FieldActionButton>
                        </div>
                        <FieldHelperText>
                          Supported patch fields include <code>pin_role</code>, <code>functions</code> or <code>primary_function</code>,
                          {' '}<code>capability_tags</code>, <code>is_5v_tolerant</code>, <code>use_with_caution</code>,
                          {' '}<code>caution_reason</code>, <code>alternate_pin_names</code>, <code>alternate_functions</code> or
                          {' '}<code>alternate_function</code>, <code>description</code>, <code>position_x</code>,
                          {' '}<code>position_y</code>, and <code>svg_id</code>.
                        </FieldHelperText>
                        <details style={{ marginTop: '12px' }}>
                          <summary style={{ cursor: 'pointer', color: '#1e40af', fontSize: '13px', fontWeight: 600 }}>
                            Example JSON
                          </summary>
                          <pre style={{
                            margin: '10px 0 0',
                            padding: '14px',
                            borderRadius: '10px',
                            background: '#f8fafc',
                            border: '1px solid #dbe4f0',
                            fontSize: '12px',
                            lineHeight: 1.6,
                            whiteSpace: 'pre-wrap',
                            overflowX: 'auto'
                          }}>
                            {ADMIN_PIN_METADATA_JSON_EXAMPLE}
                          </pre>
                        </details>
                        {uploadPinMetadataSummary && (
                          <FieldHelperText style={{ marginTop: '12px', color: '#166534' }}>
                            {uploadPinMetadataSummary}
                          </FieldHelperText>
                        )}
                      </div>

                      <FormGrid>
                        <FormGroup style={{ gridColumn: '1 / -1' }}>
                          <Label>Select Label</Label>
                          <SelectInput
                            value={selectedUploadPin ? selectedUploadPinKey : ''}
                            onChange={(e) => setSelectedUploadPinKey(e.target.value)}
                          >
                            <option value="">Select a detected label...</option>
                            {uploadPins.map((pin) => {
                              const pinKey = getPinLayoutKey(pin);
                              const pinName = pin.pin_name || pin.pinName || `Pin ${pin.pin_number || pin.pinNumber || ''}`.trim();
                              const pinNumber = pin.pin_number || pin.pinNumber || 'N/A';

                              return (
                                <option key={pinKey} value={pinKey}>
                                  {`${pinName} (Pin ${pinNumber})`}
                                </option>
                              );
                            })}
                          </SelectInput>
                        </FormGroup>

                        {selectedUploadPin && (
                          <>
                            <FormGroup>
                              <Label>Pin Role</Label>
                              <SelectInput
                                value={selectedUploadPin.pin_role || ''}
                                onChange={(e) => handleUploadPinFieldChange(selectedUploadPinKey, 'pin_role', e.target.value)}
                              >
                                {PIN_ROLE_OPTIONS.map((option) => (
                                  <option key={option.value || 'empty'} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </SelectInput>
                            </FormGroup>

                            <FormGroup>
                              <Label>Primary Function</Label>
                              <Input
                                type="text"
                                value={selectedUploadPin.functions || ''}
                                onChange={(e) => handleUploadPinFieldChange(selectedUploadPinKey, 'functions', e.target.value)}
                                placeholder="e.g. Digital I/O"
                              />
                            </FormGroup>

                            <FormGroup>
                              <Label>Current Limit (mA)</Label>
                              <Input
                                type="number"
                                min="0"
                                step="0.001"
                                value={selectedUploadPin.current_limit_ma ?? ''}
                                onChange={(e) => handleUploadPinFieldChange(selectedUploadPinKey, 'current_limit_ma', e.target.value)}
                                placeholder="e.g. 20"
                              />
                            </FormGroup>

                            <FormGroup>
                              <Label>Voltage Min (V)</Label>
                              <Input
                                type="number"
                                min="0"
                                step="0.001"
                                value={selectedUploadPin.voltage_min_v ?? ''}
                                onChange={(e) => handleUploadPinFieldChange(selectedUploadPinKey, 'voltage_min_v', e.target.value)}
                                placeholder="e.g. 0"
                              />
                            </FormGroup>

                            <FormGroup>
                              <Label>Voltage Max (V)</Label>
                              <Input
                                type="number"
                                min="0"
                                step="0.001"
                                value={selectedUploadPin.voltage_max_v ?? ''}
                                onChange={(e) => handleUploadPinFieldChange(selectedUploadPinKey, 'voltage_max_v', e.target.value)}
                                placeholder="e.g. 3.3"
                              />
                            </FormGroup>

                            <FormGroup>
                              <Label>5V Tolerant</Label>
                              <CheckboxChip style={{ marginTop: '2px' }}>
                                <input
                                  type="checkbox"
                                  checked={Boolean(selectedUploadPin.is_5v_tolerant)}
                                  onChange={(e) => handleUploadPinFieldChange(selectedUploadPinKey, 'is_5v_tolerant', e.target.checked)}
                                />
                                <span>Safe for 5V logic on this pin</span>
                              </CheckboxChip>
                            </FormGroup>

                            <FormGroup>
                              <Label>Pin With Caution</Label>
                              <CheckboxChip style={{ marginTop: '2px' }}>
                                <input
                                  type="checkbox"
                                  checked={Boolean(selectedUploadPin.use_with_caution)}
                                  onChange={(e) => handleUploadPinFieldChange(selectedUploadPinKey, 'use_with_caution', e.target.checked)}
                                />
                                <span>Include in public caution list</span>
                              </CheckboxChip>
                            </FormGroup>

                            <FormGroup style={{ gridColumn: '1 / -1' }}>
                              <Label>Capability Tags</Label>
                              <CapabilityTagsField
                                value={selectedUploadPin.capability_tags}
                                onChange={(nextTags) => handleUploadPinFieldChange(selectedUploadPinKey, 'capability_tags', nextTags)}
                                suggestedTags={SUGGESTED_PIN_CAPABILITY_TAGS}
                                placeholder="e.g. i2c-sda, gpio, interrupt"
                                inputAriaLabel={`Capability tags for ${selectedUploadPin.pin_name || selectedUploadPin.pin_number || 'selected pin'}`}
                                helperText="Type free-form tags, or click a suggested tag to insert it."
                              />
                            </FormGroup>

                            {Boolean(selectedUploadPin.use_with_caution) && (
                              <FormGroup style={{ gridColumn: '1 / -1' }}>
                                <Label>Caution Reason</Label>
                                <TextArea
                                  value={selectedUploadPin.caution_reason || ''}
                                  onChange={(e) => handleUploadPinFieldChange(selectedUploadPinKey, 'caution_reason', e.target.value)}
                                  placeholder="Explain why this pin should be used carefully..."
                                />
                              </FormGroup>
                            )}

                            <FormGroup style={{ gridColumn: '1 / -1' }}>
                              <Label>Alternate Names</Label>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px' }}>
                                {selectedUploadPinAlternateNameEntries.map((alternateNameEntry, index) => {
                                  const positionSuggestion = uploadAlternateNamePositionSuggestionsByPinKey[selectedUploadPinKey]?.[index] || null;

                                  return (
                                    <div
                                      key={`upload-alt-name-${index}`}
                                      style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
                                    >
                                      <Input
                                        type="text"
                                        value={alternateNameEntry.name}
                                        onChange={(e) => handleUploadPinAlternateNameChange(selectedUploadPinKey, index, 'name', e.target.value)}
                                        placeholder={`Alternate name ${index + 1}`}
                                      />
                                      <details
                                        defaultOpen={Boolean(alternateNameEntry.x || alternateNameEntry.y)}
                                        style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px 12px', background: '#f8fafc' }}
                                      >
                                        <summary style={{ cursor: 'pointer', color: '#475569', fontSize: '13px', fontWeight: 600 }}>
                                          Optional position override
                                        </summary>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px', marginTop: '10px' }}>
                                          <Input
                                            type="number"
                                            step="0.1"
                                            value={alternateNameEntry.x}
                                            onChange={(e) => handleUploadPinAlternateNameChange(selectedUploadPinKey, index, 'x', e.target.value)}
                                            placeholder={formatSuggestedAlternateNameCoordinate(positionSuggestion?.x) || 'X'}
                                          />
                                          <Input
                                            type="number"
                                            step="0.1"
                                            value={alternateNameEntry.y}
                                            onChange={(e) => handleUploadPinAlternateNameChange(selectedUploadPinKey, index, 'y', e.target.value)}
                                            placeholder={formatSuggestedAlternateNameCoordinate(positionSuggestion?.y) || 'Y'}
                                          />
                                        </div>
                                        {positionSuggestion && (
                                          <div style={{ marginTop: '8px', fontSize: '12px', color: '#64748b' }}>
                                            Suggested default: x {formatSuggestedAlternateNameCoordinate(positionSuggestion.x)}, y {formatSuggestedAlternateNameCoordinate(positionSuggestion.y)}
                                          </div>
                                        )}
                                      </details>
                                      <AltNameCalloutColorSection>
                                        <AltNameCalloutColorLabel>Callout Color</AltNameCalloutColorLabel>
                                        <AltNameCalloutColorRow>
                                          {ALT_PIN_CALLOUT_COLOR_OPTIONS.map((colorOption) => (
                                            <AltNameCalloutColorButton
                                              key={`${selectedUploadPinKey}-${index}-${colorOption.value}`}
                                              type="button"
                                              $selected={alternateNameEntry.color === colorOption.value}
                                              $swatch={colorOption.fill}
                                              onClick={() => handleUploadPinAlternateNameChange(selectedUploadPinKey, index, 'color', colorOption.value)}
                                              aria-label={`Use ${colorOption.label.toLowerCase()} for alternate name ${index + 1}`}
                                              title={colorOption.label}
                                            />
                                          ))}
                                        </AltNameCalloutColorRow>
                                      </AltNameCalloutColorSection>
                                    </div>
                                  );
                                })}
                              </div>
                              <FieldHelperText>
                                Add up to four alternate labels for this pin. X and Y use SVG coordinates for the alternate callout box.
                              </FieldHelperText>
                            </FormGroup>

                            <FormGroup style={{ gridColumn: '1 / -1' }}>
                              <Label>Alternate Functions</Label>
                              <TextArea
                                value={selectedUploadPin.alternate_functions || ''}
                                onChange={(e) => handleUploadPinFieldChange(selectedUploadPinKey, 'alternate_functions', e.target.value)}
                                placeholder="e.g. SPI MOSI, I2C SDA, PWM, ADC"
                              />
                            </FormGroup>

                            <FormGroup style={{ gridColumn: '1 / -1' }}>
                              <Label>Description</Label>
                              <TextArea
                                value={selectedUploadPin.description || ''}
                                onChange={(e) => handleUploadPinFieldChange(selectedUploadPinKey, 'description', e.target.value)}
                                placeholder="Add any pin-specific notes or usage details..."
                              />
                            </FormGroup>
                          </>
                        )}
                      </FormGrid>
                    </div>
                  )}

                  <div style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                    <Button 
                      type="button" 
                      onClick={() => confirmCreateBoardExit()}
                      disabled={loading}
                      style={{ background: '#6b7280', width: 'auto', minWidth: '140px' }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={handleCreateAndPublishBoard}
                      disabled={loading || !hasUnsavedBoardDraft}
                      title={hasUnsavedBoardDraft ? 'Create and publish this board' : 'Publish is available after changes are made'}
                      style={{
                        background: loading || !hasUnsavedBoardDraft ? '#9ca3af' : '#16a34a',
                        width: 'auto',
                        minWidth: '150px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                    >
                      <Globe size={16} />
                      {creatingPublishedBoard ? 'Publishing...' : 'Publish'}
                    </Button>
                    <Button type="submit" disabled={loading} style={{ width: 'auto', minWidth: '180px' }}>
                      {loading ? 'Creating...' : 'Create Board'}
                    </Button>
                  </div>
                </form>
              </ModalContent>
            </Modal>
          )}
            </AnimatePresence>
          </AdminMain>
        </AdminShell>
      </AdminContent>
    </AdminContainer>
  );
};

export default Admin;
